import moment from 'moment';
import {ActivityEndType} from '../backend/enums/activity-end-type';
import {AffiliationType} from '../backend/enums/affiliation-type';
import {BackendLanguageTag} from '../backend/enums/backend-language-tag';
import {CameraFacing} from '../backend/enums/camera-facing';
import {CaregiverRelationshipRole} from '../backend/enums/caregiver-relationship-role';
import {DeviceType} from '../backend/enums/device-type';
import {EntitySpecialPurpose} from '../backend/enums/entity-special-purpose';
import {EntityType} from '../backend/enums/entity-type';
import {InviteState} from '../backend/enums/invite-state';
import {PlaylistType} from '../backend/enums/playlist-type';
import {MobileNetworkType} from '../backend/enums/mobile-network-type';
import {OperativeSystemName} from '../backend/enums/operative-system-name';
import {ProcessStatus} from '../backend/enums/process-status';
import {RegionCode} from '../backend/enums/region-code';
import {RelationshipType} from '../backend/enums/relationship-type';
import {ReminderResponseValue} from '../backend/enums/reminder-response-value';
import {UserGender} from '../backend/enums/user-gender';
import {UserSignInType} from '../backend/enums/user-sign-in-type';
import {UserType} from '../backend/enums/user-type';
import {InviteContactMethod} from '../enums/invite-contact-method';
import {MomentLanguageCode} from '../enums/moment-language-code';
import {SignInError} from '../enums/sign-in-error';
import {TimeLapseType} from '../enums/time-lapse-type';
import {LanguageTranslation} from './types';
import {Religion} from '../backend/enums';
import {CaregiverTaskCompletion} from '../enums/caregiver-task-completion';
import {CareTaskNotDoneReason} from '../backend/enums/care-task-not-done-reason';
import {CareTaskFeature} from '../backend/enums/care-task-feature';
import {RolePermissionType} from '../backend/enums/role-permission-type';
import {PosologySchedule} from '../backend/enums/posology-schedule';
import {Frequency} from '../types';
import {MaritalStatus} from '../backend/enums/marital-status';
import {EducationLevel} from '../backend/enums/education-level';

// Overriding
moment.updateLocale(MomentLanguageCode.ptPT, {
  // Start week on Sunday
  // https://github.com/react-dates/react-dates/issues/60#issuecomment-244576967
  week: {
    dow: 0,
  },
});

export const pt_BR: LanguageTranslation = {
  legal_documents: {
    terms_and_conditions: 'Termos e Condições',
    privacy_policy: 'Política de Privacidade',
  },
  products: {
    bluebird: 'Sioslife',
    bluebird_with_gender: 'à Sioslife',
  },
  family_applications: {
    title: 'Aplicativos',
    entity: 'Entidade',
    choose_entity: 'Selecione uma entidade para ver os aplicativos deste idoso.',
    no_affiliations:
      'Este idoso não tem vínculos com entidades. Os aplicativos estão indisponíveis.',
    missing_affiliations:
      'Os vínculos deste idoso estão indisponíveis. Atualize a sessão ou entre em contato com o suporte.',
    load_error:
      'Não foi possível carregar os aplicativos. O acesso pode ter sido negado ou o aplicativo estar indisponível.',
    retry: 'Tentar novamente',
    more: 'Carregar mais',
    back: 'Voltar aos aplicativos',
    access: 'Acesso do idoso',
    enabled: 'Ativado',
    disabled: 'Desativado',
    enable: 'Ativar acesso do idoso',
    disable: 'Desativar acesso do idoso',
    confirm:
      'Alterar o acesso deste idoso ao aplicativo? Esta alteração se aplica a todas as entidades; não instala o aplicativo nem altera a disponibilidade na entidade.',
    write_error: 'Não foi possível alterar o acesso do idoso. Tente novamente.',
    write_success: 'Acesso do idoso atualizado.',
  },
  reminders: {
    group_time: 'Hora',
    group_reminder: 'Lembrete',
    group_repeats: 'Repetição',
    group_recipients: 'Destinatários',
    group_actions: 'Ações',
    no_entity_assigned: 'Sem entidade atribuída',
    group_switch_entity: 'Mude para a entidade do lembrete para gerenciá-lo.',
    group_edit: 'Editar grupo de lembretes',
    group_delete: 'Excluir grupo de lembretes',
    group_delete_confirm: 'Excluir “{{title}}” para todos os destinatários ({{count}})?',
    group_partial: 'Algumas alterações falharam. Tente novamente ou feche para atualizar.',
    group_future_required: 'Escolha uma data/hora futura.',
    group_advanced:
      'A hora e a repetição desta programação avançada são fixas. Você pode alterar a data de início.',
    my_title: 'Meus lembretes',
    my_scope: 'Lembretes criados por você em todas as entidades.',
    creation_scope: 'Novos lembretes são criados para idosos da entidade selecionada: {{entity}}.',
    recipient: 'Destinatário',
    title: 'Lembretes',
    subtitle: 'Gerenciar os lembretes desta pessoa idosa.',
    add: 'Criar lembrete',
    bulk_title: 'Criar lembretes',
    bulk_success: 'Foram criados {{success}} de {{total}} lembretes.',
    bulk_partial:
      'Foram criados {{success}} de {{total}} lembretes. Tente novamente para as demais pessoas.',
    edit: 'Editar lembrete',
    loading: 'Carregando lembretes…',
    empty_title: 'Ainda não há lembretes',
    empty_description: 'Crie um lembrete para a pessoa selecionada.',
    load_more: 'Carregar mais',
    retry: 'Tentar novamente',
    load_error: 'Não foi possível carregar os lembretes.',
    senior_unavailable:
      'A pessoa selecionada não está disponível. Selecione-a novamente para continuar.',
    delete_title: 'Excluir lembrete',
    delete_description:
      'Tem certeza de que deseja excluir este lembrete? Esta ação não pode ser desfeita.',
    delete: 'Excluir',
    cancel: 'Cancelar',
    enable: 'Ativar lembrete',
    disable: 'Desativar lembrete',
    status_active: 'Ativo',
    status_inactive: 'Desativado',
    next_run: 'Próxima execução',
    save_error: 'Não foi possível salvar o lembrete.',
    save_success: 'Lembrete salvo com sucesso.',
    delete_error: 'Não foi possível excluir o lembrete.',
    delete_success: 'Lembrete excluído com sucesso.',
    active_error: 'Não foi possível alterar o status do lembrete.',
    active_success: 'Lembrete ativado com sucesso.',
    inactive_success: 'Lembrete desativado com sucesso.',
    response_types: {yes_no: 'Sim ou não', ok: 'OK'},
    recurrence: {
      once: 'Uma vez',
      per_minute: 'A cada minuto',
      hourly: 'A cada {{count}} hora(s)',
      daily: 'A cada {{count}} dia(s)',
      weekly: 'A cada {{count}} semana(s)',
      monthly: 'A cada {{count}} mês(es)',
      yearly: 'A cada {{count}} ano(s)',
      current_unsupported: 'Valor atual ({{value}})',
    },
    form: {
      title: 'Título',
      title_placeholder: 'Digite o título do lembrete',
      message: 'Mensagem',
      message_placeholder: 'Digite a mensagem do lembrete',
      response_type: 'Tipo de resposta',
      date: 'Data inicial',
      time: 'Horário inicial',
      recurrence: 'Repetição',
      recurrence_count: 'Repetir a cada',
      save: 'Salvar',
      title_required: 'O título é obrigatório.',
      title_too_long: 'O título não pode exceder {{max}} caracteres.',
      message_required: 'A mensagem é obrigatória.',
      message_too_long: 'A mensagem não pode exceder {{max}} caracteres.',
      date_required: 'A data é obrigatória.',
      time_required: 'O horário é obrigatório.',
      invalid_date_time: 'Informe uma data e horário válidos.',
      date_in_past: 'A data não pode estar no passado.',
      time_in_past: 'O horário não pode estar no passado.',
    },
  },
  family_services: {
    title: 'Serviços',
    choose: 'Escolha uma instituição',
    none: 'Sem instituições associadas.',
    unavailable: 'Os serviços não estão disponíveis nas suas instituições.',
    empty: 'Não há serviços disponíveis.',
    error: 'Não foi possível carregar os serviços.',
    denied: 'Acesso negado.',
    not_found: 'Instituição ou serviço indisponível.',
    retry: 'Tentar novamente',
    call: 'Ligar',
    no_phone: 'Contato telefônico indisponível.',
    unsupported: 'Ação de contato indisponível para este serviço.',
    active: 'Ativo',
    inactive: 'Inativo',
    enable: 'Ativar',
    disable: 'Desativar',
    confirm: 'Confirmar',
    cancel: 'Cancelar',
    save_error: 'Não foi possível atualizar o serviço.',
    saved: 'Serviço atualizado.',
    refresh_error: 'Atualizado, mas não foi possível atualizar a lista.',
  },
  family_invite: {
    contact_method: 'Forma de contato',
    phone: 'Celular',
    action: 'Convidar cuidador',
    context: 'Convidar um cuidador para {{name}}',
    name: 'Nome',
    email: 'E-mail',
    next: 'Continuar',
    back: 'Voltar',
    send: 'Enviar convite',
    confirm: 'Enviar um convite para {{name}} em {{destination}}?',
    success: 'Convite enviado. A pessoa precisa aceitar antes de se tornar contato.',
    uncertain:
      'O convite pode ter sido enviado. Confirme com a pessoa antes de enviar outro convite.',
    invalid: 'Verifique o nome e os dados de contato.',
    denied: 'Você não tem permissão para enviar este convite.',
    unavailable: 'O idoso selecionado não está disponível.',
  },
  family_profile: {
    user_details: 'Dados do usuário',
    remove_link: 'Remover vínculo',
    remove_link_success: 'Seu relacionamento foi removido.',
    remove_link_confirm:
      'Remover seu relacionamento com {{name}}? Você perderá o acesso às informações dessa pessoa. Isso não excluirá o idoso nem o relacionamento de outra pessoa.',
    remove_link_error: 'Não foi possível remover seu relacionamento. Tente novamente.',
    remove_link_denied: 'Você não tem permissão para remover este relacionamento.',
    cancel: 'Cancelar',
    institutions: 'Instituições',
    senior_contacts: 'Contatos idosos',
    senior_empty: 'Nenhum contato idoso encontrado.',
    title: 'Perfil',
    contacts: 'Cuidadores',
    empty: 'Nenhum cuidador encontrado.',
    load_more: 'Carregar mais',
    loading: 'Carregando…',
    error: 'Não foi possível carregar os dados.',
    denied: 'Acesso negado.',
    unavailable: 'Esta informação não está disponível.',
    retry: 'Tentar novamente',
    close: 'Fechar',
  },
  family_media: {
    recording: 'Gravando',
    camera: 'Usar câmera',
    take_photo: 'Tirar foto',
    record_video: 'Gravar vídeo',
    stop_recording: 'Parar gravação',
    camera_error: 'Câmera ou microfone indisponível. Verifique as permissões e tente novamente.',
    download: 'Baixar',
    share: 'Compartilhar',
    export_error: 'Não foi possível preparar o arquivo. Tente novamente ou use outro navegador.',
    title: 'Álbum da família',
    upload: 'Enviar',
    refresh: 'Atualizar',
    loading: 'Carregando o álbum…',
    empty_title: 'Ainda não há fotos ou vídeos',
    empty_description: 'As fotos e os vídeos compartilhados com esta pessoa idosa aparecerão aqui.',
    load_error: 'Não foi possível carregar o álbum.',
    refresh_error: 'Não foi possível atualizar o álbum.',
    load_more: 'Carregar mais',
    loading_more: 'Carregando mais…',
    retry: 'Tentar novamente',
    senior_unavailable:
      'A pessoa selecionada não está disponível. Selecione-a novamente para continuar.',
    photo: 'Foto',
    video: 'Vídeo',
    processing: 'Processando o vídeo…',
    processing_failed: 'Não foi possível processar este conteúdo.',
    uploaded_by: 'Enviado por',
    you: 'Você',
    created_at: 'Adicionado em',
    description: 'Descrição',
    edit: 'Editar',
    delete: 'Excluir',
    cancel: 'Cancelar',
    save: 'Salvar',
    saving: 'Salvando…',
    upload_photo: 'Enviar foto ou vídeo',
    upload_progress: 'Enviando…',
    invalid_file_type: 'Selecione um arquivo JPG, PNG, GIF, MP4 ou MOV.',
    upload_request_error: 'Não foi possível iniciar o envio.',
    upload_transfer_error: 'Não foi possível enviar o arquivo.',
    upload_record_error:
      'A solicitação para salvar o arquivo falhou, mas ele pode ter sido salvo. Atualize e confira o álbum antes de tentar novamente para evitar duplicatas.',
    upload_success: 'Enviado com sucesso. Vídeos podem levar algum tempo para processar.',
    edit_success: 'Descrição atualizada com sucesso.',
    edit_error: 'Não foi possível atualizar a descrição.',
    router_prompt_edit_title: 'Sair sem salvar as alterações neste item do álbum?',
    router_prompt_edit_message: 'Ao sair, você perderá as alterações feitas na descrição.',
    delete_success: 'Item do álbum excluído com sucesso.',
    delete_error: 'Não foi possível excluir este item do álbum.',
    delete_confirm_title: 'Excluir item do álbum',
    delete_confirm_description:
      'Tem certeza de que deseja excluir este item? Esta ação não pode ser desfeita.',
    detail_not_found: 'Não foi possível encontrar este item do álbum.',
    detail_load_error: 'Não foi possível carregar este item.',
    detail_unavailable: 'Este item não está disponível.',
    signed_url_error: 'Não foi possível abrir este item. Tente novamente.',
  },
  relationship_types: {
    [RelationshipType.Family]: 'Familiar',
    [RelationshipType.Friend]: 'Amigo',
    [RelationshipType.Healthcare]: 'Cuidador de saúde',
    [RelationshipType.Service]: 'Serviço',
    [RelationshipType.Other]: 'Outro',
  },
  user_types: {
    [UserType.Caregiver]: 'Cuidador',
    [UserType.Follower]: 'Seguidor',
    [UserType.Senior]: 'Utente',
  },
  genders: {
    [UserGender.Female]: 'Feminino',
    [UserGender.Male]: 'Masculino',
    [UserGender.Undisclosed]: 'Não divulgado',
    [UserGender.Unspecified]: 'Outro',
  },
  marital_statuses: {
    [MaritalStatus.CivilUnion]: 'União de facto',
    [MaritalStatus.Divorced]: 'Divorciado(a)',
    [MaritalStatus.Married]: 'Casado(a)',
    [MaritalStatus.Other]: 'Outro',
    [MaritalStatus.Single]: 'Solteiro(a)',
    [MaritalStatus.Widowed]: 'Viúvo(a)',
  },
  education_levels: {
    [EducationLevel.BachelorsDegree]: 'Graduação (Licenciatura/Bacharelado)',
    [EducationLevel.BasicEducation]: 'Ensino fundamental',
    [EducationLevel.DoctoratePhd]: 'Doutorado',
    [EducationLevel.MastersDegree]: 'Mestrado',
    [EducationLevel.NoFormalEducation]: 'Sem escolaridade',
    [EducationLevel.Other]: 'Outro',
    [EducationLevel.PostgraduateDiploma]: 'Pós-graduação',
    [EducationLevel.PrimaryEducation]: 'Ensino primário',
    [EducationLevel.SecondaryEducation]: 'Ensino médio',
    [EducationLevel.VocationalTechnicalTraining]: 'Ensino técnico profissional',
  },
  languages: {
    [BackendLanguageTag.ptPT]: 'Português (Portugal)',
    [BackendLanguageTag.enUS]: 'Inglês (EUA)',
    [BackendLanguageTag.esES]: 'Espanhol (Espanha)',
    [BackendLanguageTag.glES]: 'Galego (Espanha)',
    [BackendLanguageTag.ptBR]: 'Português (Brasil)',
  },
  regions: {
    [RegionCode.PT]: 'Portugal',
    [RegionCode.ES]: 'Espanha',
    [RegionCode.GL]: 'Galiza',
    [RegionCode.BR]: 'Brasil',
  },
  roles: {
    [CaregiverRelationshipRole.Administrator]: 'Administrador',
    [CaregiverRelationshipRole.Contact]: 'Contacto',
    [CaregiverRelationshipRole.Member]: 'Membro',
  },
  religions: {
    [Religion.AfricanTraditionalAndDiasporic]: 'Tradicional e diáspora africana',
    [Religion.Agnostic]: 'Agnosticismo',
    [Religion.Animism]: 'Animismo',
    [Religion.Atheist]: 'Ateísmo',
    [Religion.BahaI]: 'Baháʼí',
    [Religion.Buddhism]: 'Budismo',
    [Religion.CaoDai]: 'Caodaísmo',
    [Religion.ChineseTraditionalReligion]: 'Tradicional chinesa',
    [Religion.Christianity]: 'Cristianismo',
    [Religion.Hinduism]: 'Hinduísmo',
    [Religion.Islam]: 'Islamismo',
    [Religion.Jainism]: 'Jainismo',
    [Religion.Juche]: 'Juche',
    [Religion.Judaism]: 'Judaísmo',
    [Religion.NeoPaganism]: 'Neopaganismo',
    [Religion.Nonreligious]: 'Sem religião',
    [Religion.Rastafari]: 'Rastafári',
    [Religion.Secular]: 'Secularismo',
    [Religion.Shinto]: 'Xintoísmo',
    [Religion.Sikhism]: 'Sikhismo',
    [Religion.Spiritism]: 'Espiritismo',
    [Religion.Tenrikyo]: 'Tenrikyo',
    [Religion.UnitarianUniversalism]: 'Unitário-Universalismo',
    [Religion.Zoroastrianism]: 'Zoroastrismo',
    [Religion.PrimalIndigenous]: 'Indígena',
    [Religion.Undisclosed]: 'Não divulgado',
    [Religion.Unspecified]: 'Outro',
  },
  affiliation_types: {
    [AffiliationType.Senior]: 'Utente',
    [AffiliationType.Caregiver]: 'Cuidador',
    [AffiliationType.Administrator]: 'Administrador',
    [AffiliationType.Manager]: 'Manager',
    [AffiliationType.Operator]: 'Operador',
    [AffiliationType.MedicalProfessional]: 'Profissional de saúde',
  },
  role_permissions_section: {
    overview: 'Visão geral',
    seniors: 'Utentes',
    team: 'Equipa',
    'digital-inclusion': 'Inclusão digital',
    settings: 'Configurações',
    'daily-care-log': 'Registo de cuidados',
    calendar: 'Calendário',
    occurrences: 'Ocorrências',
    'quick-access-devices': 'Acesso Rápido',
  },
  role_permissions: {
    [RolePermissionType.Overview_Activity]: 'Atividade geral',
    [RolePermissionType.Overview_Statistics]: 'Estatísticas gerais',
    [RolePermissionType.Seniors_List]: 'Lista de Utentes',
    [RolePermissionType.Seniors_Dashboard]: 'Painel de Bordo',
    [RolePermissionType.Seniors_Dashboard_Activity]: 'Painel de Bordo > Atividade',
    [RolePermissionType.Seniors_Dashboard_GeneralStatistics]:
      'Painel de Bordo > Estatísticas Gerais',
    [RolePermissionType.Seniors_Dashboard_HealthStatistics]:
      'Painel de Bordo > Estatísticas de Saúde',
    [RolePermissionType.Seniors_IndividualPlan]: 'Plano Individual',
    [RolePermissionType.Seniors_PersonalData_Reminders]: 'Lembretes',
    [RolePermissionType.Seniors_BADL]: 'Plano de cuidados > ABVD',
    [RolePermissionType.Seniors_IADL]: 'Plano de cuidados > AIVD',
    [RolePermissionType.Seniors_ClinicalSupport]: 'Plano de cuidados > Apoio Clínico',
    [RolePermissionType.Seniors_PersonalDevelopment]: 'Plano de cuidados > Desenvol. Pessoal',
    [RolePermissionType.Seniors_CareAndActivities]: 'Plano de cuidados > Resumo de cuidados',
    [RolePermissionType.Seniors_CareAndActivities_BADL]:
      'Plano de cuidados > Resumo de cuidados > ABVD',
    [RolePermissionType.Seniors_CareAndActivities_IADL]:
      'Plano de cuidados > Resumo de cuidados > AIVD',
    [RolePermissionType.Seniors_CareAndActivities_ClinicalSupport]:
      'Plano de cuidados > Resumo de cuidados > Apoio Clínico',
    [RolePermissionType.Seniors_CareAndActivities_PersonalDevelopment]:
      'Plano de cuidados > Resumo de cuidados > Desenvol. Pessoal',
    [RolePermissionType.Seniors_CareServices]: 'Escala de Serviço',
    [RolePermissionType.Seniors_PersonalData_Profile]: 'Dados Pessoais > Perfil',
    [RolePermissionType.Seniors_PersonalData_Caregivers]: 'Dados Pessoais > Cuidadores',
    [RolePermissionType.Seniors_PersonalData_Health]: 'Dados Pessoais > Saúde',
    [RolePermissionType.Seniors_DigitalInclusion_Applications]: 'Inclusão Digital > Aplicativos',
    [RolePermissionType.Seniors_DigitalInclusion_Devices]: 'Inclusão Digital > Dispositivos',
    [RolePermissionType.Seniors_DigitalInclusion_Playlists]:
      'Inclusão Digital > Listas de Reprodução',
    [RolePermissionType.Seniors_DigitalInclusion_Cards]: 'Inclusão Digital > Cartões',
    [RolePermissionType.Seniors_PersonalData_Attachments]: 'Dados Pessoais > Anexos',
    [RolePermissionType.Team_Professionals]: 'Lista de Profissionais',
    [RolePermissionType.Team_CareServices]: 'Escala Geral de Serviço',
    [RolePermissionType.Team_Professionals_Profile]: 'Profissional > Perfil',
    [RolePermissionType.Team_Professionals_Applications]: 'Profissional > Aplicativos',
    [RolePermissionType.Team_Professionals_Cards]: 'Profissional > Cartões',
    [RolePermissionType.Team_Professionals_Playlists]: 'Profissional > Listas de Reprodução',
    [RolePermissionType.Team_Professionals_CareServices]: 'Profissional > Escala de Serviço',
    [RolePermissionType.Team_Shifts_Management]: 'Gestão dos turnos',
    [RolePermissionType.Team_Shifts_Attributions]: 'Atribuição dos turnos',
    [RolePermissionType.Team_Permissions]: 'Permissões',
    [RolePermissionType.Team_AttendanceTracker_Registry]: 'Registro de Ponto > Registros',
    [RolePermissionType.Team_AttendanceTracker_Assignments]: 'Registro de Ponto > Atribuições',
    [RolePermissionType.DigitalInclusion_Applications]: 'Aplicativos',
    [RolePermissionType.DigitalInclusion_Media]: 'Fotografias',
    [RolePermissionType.DigitalInclusion_Devices]: 'Dispositivos',
    [RolePermissionType.DigitalInclusion_Cards]: 'Cartões',
    [RolePermissionType.DigitalInclusion_Playlists]: 'Listas de reprodução',
    [RolePermissionType.DigitalInclusion_Services]: 'Serviços',
    [RolePermissionType.Settings_Entity_Profile]: 'Instituição > Perfil',
    [RolePermissionType.Settings_Entity_Settings]: 'Instituição > Configurações',
    [RolePermissionType.Settings_Licenses]: 'Licenças',
    [RolePermissionType.Settings_BADL]: 'ABVD',
    [RolePermissionType.Settings_IADL]: 'AIVD',
    [RolePermissionType.Settings_ClinicalSupport]: 'Apoio Clínico',
    [RolePermissionType.Settings_PersonalDevelopment]: 'Desenvol. pessoal',
    [RolePermissionType.Settings_Templates]: 'Modelos',
    [RolePermissionType.Settings_Occurrences]: 'Ocorrências',
    [RolePermissionType.DailyCareLog]: 'Registo de cuidados',
    [RolePermissionType.DailyCareLog_BADL]: 'Registo de cuidados > ABVD',
    [RolePermissionType.DailyCareLog_IADL]: 'Registo de cuidados > AIVD',
    [RolePermissionType.DailyCareLog_PersonalDevelopment]:
      'Registo de cuidados > Desenvol. Pessoal',
    [RolePermissionType.DailyCareLog_ClinicalSupport]: 'Registo de cuidados > Apoio Clínico',
    [RolePermissionType.DailyCareLog_Occurrences]: 'Registo de cuidados > Ocorrências',
    [RolePermissionType.Calendar]: 'Calendário',
    [RolePermissionType.Calendar_BADL]: 'Calendário > ABVD',
    [RolePermissionType.Calendar_IADL]: 'Calendário > AIVD',
    [RolePermissionType.Calendar_PersonalDevelopment]: 'Calendário > Desenvol. Pessoal',
    [RolePermissionType.Calendar_ClinicalSupport]: 'Calendário > Apoio Clínico',
    [RolePermissionType.Occurrences]: 'Lista de Ocorrências',
    [RolePermissionType.SettingsQuickAccessDevices]: 'Acesso Rápido',
  },
  screen_paths: {
    professional_caregiver_seniors: 'Utentes',
    professional_caregiver_team: 'Equipa',
    professional_caregiver_roles_and_permissions: 'Cargo e Permissões',
    senior_dashboard: 'Painel de bordo',
    senior_individual_plan: 'Plano individual',
    senior_care_plan: 'Plano de cuidados',
    senior_care_and_activity: 'Resumo de cuidados',
    senior_personal_data: 'Dados Pessoais',
    senior_profile: 'Perfil',
    senior_caregivers: 'Cuidadores',
    senior_applications: 'Aplicativos',
    senior_devices: 'Dispositivos',
    senior_activity: 'Atividade',
    senior_user_cards: 'Cartões',
    senior_statistics: 'Estatísticas',
    senior_health: 'Saúde',
    senior_playlists: 'Listas de Reprodução',
    senior_attachments: 'Anexos',
    institution_settings_profile: 'Perfil',
    institution_settings_settings: 'Configurações',
    institution_settings_activity: 'Atividade',
    institution_settings_user_cards: 'Cartões',
    institution_settings_devices: 'Dispositivos',
    institution_settings_services: 'Serviços',
    institution_settings_playlist: 'Playlist',
    institution_settings_statistics: 'Estatisticas',
    institution_settings_licenses: 'Licenças',
    institution_settings_templates: 'Modelos',
    institution_settings_occurrences: 'Ocorrências',
    institution_settings_quick_access_devices: 'Acesso Rápido',
  },
  institution_settings_screen: {
    subtitle: {
      write: 'Você pode configurar as definições da instituição',
      read: 'Você pode verificar as definições da instituição',
    },
    columns: {
      setting: 'Configuração',
      active: 'Ativa',
    },
    attendance_tracker: 'Registro de Ponto',
    load_error_toast: 'Não foi possível carregar as configurações',
    update_error_toast: 'Não foi possível atualizar a configuração',
  },
  attendance_tracker_registry_screen: {
    subtitle: 'Registros de ponto dos profissionais',
    filters: {
      all_statuses: 'Todos os status',
      all_day: 'Dia todo',
      all_professionals: 'Todos os profissionais',
    },
    empty: 'Não há registros de ponto neste dia.',
    load_error: 'Não foi possível carregar os registros de ponto.',
    retry: 'Tentar novamente',
    waiting_exit: 'Aguardando registro de saída',
    add: {
      title: 'Adicionar registro de ponto',
      professional: 'Profissional',
      professionals_hint:
        'Só aparecem os profissionais com o registro de ponto ativo (obrigatório ou opcional).',
      professionals_empty:
        'Não há profissionais com o registro de ponto ativo (obrigatório ou opcional).',
      professional_placeholder: 'Escolha o profissional',
      kind_label: 'O que deseja registrar?',
      kind_session: 'Sessão completa (entrada e saída)',
      kind_entry: 'Apenas a entrada (sessão em andamento)',
      kind_exit: 'Saída faltante',
      open_session_info:
        'Este profissional tem uma entrada em aberto desde {{date}}. Registre a saída faltante.',
      entry_date: 'Data da entrada',
      entry_time: 'Hora da entrada',
      exit_date: 'Data da saída',
      exit_time: 'Hora da saída',
      reason: 'Motivo',
      reason_placeholder: 'Ex.: o profissional esqueceu de registrar a saída',
      notice:
        'Este registro ficará identificado como registrado por {{admin}} em nome de {{professional}} e não tem localização.',
      self_warning:
        'Você não pode registrar o seu próprio ponto por aqui, para evitar que registre o ponto em nome de si mesmo. Para registrar a sua entrada ou saída, use o {{app}}.',
      self_action: 'Abrir o {{app}}',
      cancel: 'Cancelar',
      continue: 'Continuar',
      back: 'Voltar',
      confirm: 'Registrar',
      confirm_title: 'Confirme o registro',
      confirm_description:
        'Depois de registrado, o registro de ponto não pode ser alterado nem apagado.',
      summary_record: 'Registro',
      success: 'Registro de ponto adicionado com sucesso.',
      checking: 'Verificando o registro de ponto do profissional...',
      error_future: 'A data e a hora não podem estar no futuro.',
      error_order:
        'A saída precisa ser depois da entrada e não pode existir outro registro na mesma data e hora.',
      error_overlap: 'As datas escolhidas se sobrepõem a outro registro do profissional.',
      error_type:
        'O registro não se encaixa nos registros que o profissional já tem. Confira as datas e as horas.',
      error_conflict:
        'Os registros do profissional mudaram nesse meio tempo. Confira os dados e tente novamente.',
      error_not_tracking: 'Este profissional não tem o registro de ponto ativo.',
      error_generic: 'Não foi possível adicionar o registro de ponto. Tente novamente.',
      validation_future: 'Não pode estar no futuro.',
      validation_exit_before_entry: 'A saída precisa ser depois da entrada.',
      validation_exit_before_open_entry: 'A saída precisa ser depois da entrada em aberto.',
      validation_reason_required: 'Informe o motivo.',
    },
    void: {
      title: 'Anular sessão',
      description:
        'Ao anular, a sessão deixa de contar como tempo trabalhado e esse período fica livre para novos registros. O registro continua guardado como anulado, com o seu nome e o motivo.',
      reason: 'Motivo da anulação',
      reason_placeholder: 'Ex.: registro feito por engano',
      cancel: 'Cancelar',
      confirm: 'Anular sessão',
      success: 'Sessão anulada com sucesso.',
      error_conflict:
        'Os registros do profissional mudaram nesse meio tempo. Atualize a lista e tente novamente.',
      error_self: 'Você não pode anular os seus próprios registros.',
      error_generic: 'Não foi possível anular a sessão. Tente novamente.',
      validation_reason_required: 'Informe o motivo.',
    },
    correct: {
      title: 'Corrigir sessão',
      description:
        'A sessão atual será anulada e substituída pelos registros que você indicar abaixo. O registro anulado continua guardado, e o tempo que tinha fica livre para os novos registros.',
      current_session: 'Sessão atual',
      reason: 'Motivo da correção',
      notice:
        'Os novos registros ficarão identificados como registrados por {{admin}} em nome de {{professional}} e não têm localização. A sessão atual ficará anulada, com este motivo.',
      confirm_title: 'Confirme a correção',
      confirm_description:
        'A sessão atual será anulada e os novos registros ficarão no seu lugar. Depois de registrados, não podem ser alterados nem apagados.',
      summary_old: 'Sessão a anular',
      summary_new: 'Nova sessão',
      confirm: 'Corrigir sessão',
      success: 'Sessão corrigida com sucesso.',
    },
    details: {
      correct_action: 'Corrigir',
      void_action: 'Anular sessão',
      duration: 'Duração',
      title: 'Detalhes do registro',
      close: 'Fechar',
    },
  },
  attendance_tracker_assignments_screen: {
    subtitle: {
      write: 'Você pode definir quem deve registrar o ponto',
      read: 'Você pode verificar quem deve registrar o ponto',
    },
    columns: {
      professional: 'Profissional',
      assignment: 'Registro de ponto',
      last_change: 'Última alteração',
    },
    assignment_types: {
      mandatory: 'Obrigatório',
      optional: 'Opcional',
      not_applicable: 'Não aplicável',
    },
    empty: 'Não existem profissionais',
    last_change_summary: 'Por {{name}} a {{date}}',
    last_change_summary_no_author: '{{date}}',
    load_error_toast: 'Não foi possível carregar as atribuições',
    update_error_toast: 'Não foi possível atualizar a atribuição',
  },
  date_time_picker: {
    cancel_button: 'Cancelar',
    confirm_button: 'Confirmar',
    clear_button: 'Apagar',
    today: 'Hoje',
  },
  date_range_input: {
    title: 'Escolha um intervalo de tempo',
    ok_button: 'Confirmar',
    cancel_button: 'Cancelar',
    presets: {
      day: 'Dia',
      week: 'Semana',
      month: 'Mês',
      '7_days': '7 dias',
      '15_days': '15 dias',
      '30_days': '30 dias',
      '180_days': '180 dias',
      '365_days': '365 dias',
      custom: 'Personalizado',
    },
    errors: {
      invalid_date_range: 'Intervalo de datas inválido',
    },
  },
  generic_header: {
    actions: {
      edit: 'Editar',
      delete: 'Apagar plano',
      save: 'Guardar modelo',
      import: 'Importar modelo',
      archivePlan: 'Arquivar plano',
      remove: 'Remover',
    },
  },
  user_card: {
    actions: {
      edit: 'Editar',
      delete: 'Apagar',
    },
  },
  role_autocomplete: {
    unselected: 'Não seleccionado',
    no_matching: 'Não foi encontrada nenhuma correspondência de {{ type }}.',
  },
  view_type_toggler: {
    tooltip_message: 'Mudar para vista em {{ type }}',
  },
  index_grid_screen: {
    total_counter: 'Total',
    add_button: 'Adicionar',
    search_placeholder: 'Pesquisar',
    filter_placeholder: 'Filtrar',
    empty_message_add_encouraging: 'Prima adicionar para registar um {{ type }}.',
    empty_search_message: {
      title: 'Desculpe, não conseguimos encontrar resultados para "{{ name }}".',
      subtitle: 'Tente uma pesquisa diferente.',
    },
    empty_filters_message: {
      title: 'As opções de filtragem selecionadas não retornaram resultados.',
      subtitle: 'Tente com filtros diferentes.',
    },
    toggler: {
      grid: 'grelha',
      list: 'lista',
    },
  },
  login: {
    welcome: 'Bem-vindo(a) à plataforma Sioslife!',
    welcome_daily_care_log: 'Bem-vindo(a) à App de registo Sioslife',
    welcome_accept_invite: 'Entre na sua conta para aceitar o convite!',
    instruction: 'Utilize as credenciais para aceder',
    email_input: 'Email',
    password_input: 'Palavra-passe',
    enter_button: 'Login',
    enter_button_microsoft: 'Entrar com Microsoft',
    enter_button_google: 'Entrar com Google',
    forgot_password: 'Recuperar palavra-passe',
    quick_access: 'Entrar com acesso rápido',
    validate_invite: 'Foi convidado para ser cuidador?',
    use_invite_code: 'Utilizar código de convite',
    errors: {
      form_validation_invalid_field: 'Preencha este campo para entrar na sua conta',
      [SignInError.Unknown]: 'Ocorreu um erro. Por favor, tente mais tarde.',
      [SignInError.InvalidEmail]: 'E-mail inválido',
      [SignInError.UserDisabled]: 'Usuário desativado',
      [SignInError.UserNotFound]: 'Usuário não encontrado',
      [SignInError.WrongPassword]: 'Palavra-passe errada',
      [SignInError.FailedToGetMe]:
        'Não foi possível carregar a informação do usuário. Por favor, contacte com o Apoio ao Cliente.',
    },
  },
  recover_password: {
    title: 'Esqueceu a sua palavra-passe?',
    subtitle: 'Nós vamos ajudar!',
    instructions:
      'De forma a poder definir uma nova palavra-passe, por favor, indique o seu endereço de e-mail. Vamos enviar-lhe uma mensagem com uma hiperligação que lhe permitirá efetuar a mudança.',
    email_input: 'Endereço de e-mail',
    send_button: 'Enviar',
    password_remembered: 'Afinal já me lembrei da minha palavra-passe!',
    go_back_button: 'Voltar para o ecrã de Entrar',
    errors: {
      form_validation_invalid_field:
        'Preencha este campo para completar a recuperação da palavra-passe',
      send_request_error_message: 'Um erro ocorreu. Não foi possível efetuar o pedido.',
    },
    success: {
      send_request_success_message:
        'As instruções para alterar a sua palavra-passe foram enviadas para o endereço de e-mail que forneceu.',
    },
  },
  replace_password: {
    title: 'Definir nova palavra-passe',
    instructions:
      'Ok! Bom trabalho! Vamos então definir uma nova palavra-passe. Escreva a sua nova palavra-passe nos dois espaços abaixo para terminar o processo.',
    new_password_input: 'Nova palavra-passe',
    repeat_password_input: 'Repetir a nova palavra-passe',
    submit_button: 'Confirmar',
    password_remembered: 'Afinal já me lembrei da minha palavra-passe!',
    go_back_button: 'Ir para o ecrã de Entrar',
    errors: {
      form_validation_invalid_field:
        'Preencha este campo para completar a definição da nova palavra-passe',
      form_validation_invalid_password_minimum_characters:
        'A palavra-passe deve ter pelo menos 6 carateres',
      form_validation_invalid_password_blank_spaces:
        'A palavra-passe não pode começar nem terminar com espaços em branco',
      form_validation_invalid_repeat_password_mismatch:
        'Por favor, certifique-se que as palavras-passe são iguais',
      change_password_error_message:
        'Um erro ocorreu. Não foi possível definir a sua nova palavra-passe.',
    },
    success: {
      change_password_success_message:
        'A sua nova palavra-passe foi definida. Pode agora entrar com a sua nova palavra-passe.',
    },
  },
  validate_invite: {
    title: 'Utilizar código de convite',
    instructions_1: 'Para ser cuidador {{brand}} deverá possuir um convite de acesso à plataforma.',
    instructions_2:
      'Caso já tenha solicitado um convite, por favor, verifique a sua caixa de mensagens de e-mail e/ou a sua caixa de mensagens de SMS e procure por uma mensagem {{brand}}. Nessa mensagem encontrará o código que deverá indicar abaixo.',
    code_input: 'Código do convite',
    send_button: 'Validar convite e avançar',
    errors: {
      form_validation_invalid_field: 'Preencha este campo para completar a validação do convite',
      invalid_invite_error_message:
        'O convite que tentou utilizar está inválido. Por favor, verifique se insiriu o código correto. Em caso afirmativo, solicite um novo convite.',
    },
  },
  accept_invite: {
    relationship_invite_title: 'Aceitar convite para ser cuidador de',
    relationship_invite_instructions:
      'Para ser cuidador deverá ter uma conta de acesso à plataforma {{brand}}. Se é a primeira vez que está a aceitar um convite, pressione em "{{button_create}}". Se já aceitou outros convites no passado, utilize a sua conta existente e pressione em "{{button_login}}".',
    affiliation_invite_title: 'Aceitar convite para afiliar-se e ser cuidador da entidade',
    affiliation_invite_instructions:
      'Para afiliar-se e ser cuidador deverá ter uma conta de acesso à plataforma {{brand}}. Se é a primeira vez que está a aceitar um convite, pressione em "{{button_create}}". Se já aceitou outros convites no passado, utilize a sua conta existente e pressione em "{{button_login}}".',
    affiliation_type_description: 'Este convite permite que seja {{type}} da entidade {{name}}.',
    create_account_button: 'Aceitar e criar nova conta',
    login_button: 'Entrar numa conta já existente',
    errors: {
      invalid_invite_error_message:
        'O convite que tentou utilizar está inválido. Por favor, solicite um novo convite.',
    },
  },
  quick_access_login: {
    instruction: 'Escolha o usuário para iniciar sessão',
    all_users: 'Todos os usuários',
    error:
      'Não é possivel continuar com o Acesso Rápido, tente de novo. Se o problema persistir, por favor contacte o Administrador do Sistema.',
    back_to_email_and_password_login: 'Entrar com E-mail e Palavra-passe',
    go_back_to_the_recent_users_list: 'Voltar à lista de usuários recentes',
    hello_user: 'Olá, {{name}}!',
    insert_your_quick_access_code: 'Insira o seu código de acesso rápido',
    login_failed: 'Ocorreu um erro ao tentar iniciar sessão. Por favor, tente novamente.',
    login_failed_code: 'Código de acesso rápido errado',
    try_again: 'Tentar novamente',
    regain_access: 'Recuperar código de acesso',
    remove_recent: 'Pretende remover o usuário da lista de usuários recentes?',
    regain_access_message:
      'O seu código de acesso rápido poderá ser redefinido na Área de gestão da plataforma Sioslife, no seu perfil de profissional.',
    regain_access_description:
      'Caso não tenha permissões de acesso à Área de gestão, por favor fale com um administrador da sua entidade.',
    back_to_previous: 'Voltar para o ecrã anterior',
  },
  notifications: {
    date: 'Data',
    time: 'Hora',
    title: 'Notificações',
    check_read: 'Marcar como lida',
    check_unread: 'Marcar como não lida',
    read_all_notifications: 'Marcar todas como lidas',
    read_all_notifications_dialog_title: 'Marcar todas as notificações como lidas?',
    read_all_notifications_dialog_content:
      'Poderá voltar a marcar individualmente cada notificação como não lida posteriormente se assim o desejar.',
    read_all_notifications_disabled_tooltip_message: 'Já marcou todas as notificações como lidas.',
    read_all_notifications_success_message: 'As notificações foram todas marcadas como lidas.',
    read_all_notifications_error_message:
      'Não foi possível marcar todas as notificações como lidas.',
  },
  push_notifications: {
    warnings: {
      unsubscribed_generic_warning_title: 'Não pode receber notificações em tempo real',
      unsubscribed_not_supported_warning_message:
        'O navegador que está a utilizar não suporta a receção de notificações. Por favor, utilize o Chrome, Firefox ou Edge para poder receber notificações em tempo real.',
      unsubscribed_error_warning_message:
        'Um erro ocorreu pelo que não pode receber notificações em tempo real.',
      unsubscribed_permissions_denied_warning_message:
        'As permissões para receber notificações foram negadas. Por favor, ative as notificações para o navegador que está a utilizar.',
    },
  },
  invite_modal: {
    caregiver_invite_title: 'Aceitar o convite para ser cuidador',
    affiliation_type_description: 'Este convite permite que seja {{type}} da entidade {{name}}.',
    relationship_type_input: 'Qual o tipo de relacionamento que possui com {{name}}?',
    invite_recipient_not_current_user_description:
      'Está a utilizar uma conta com informação diferente da informação presente neste convite. Por favor, reveja com atenção se pretende continuar.',
    accept_button: 'Sim, aceito',
    decline_button: 'Não, recuso',
    relationship_already_exists_description:
      'Já é cuidador de {{name}} por ter aceite um convite anteriormente. Não necessita de fazer mais nada.',
    affiliation_already_exists_description:
      'Já possui uma afiliação com a entidade {{name}} por ter aceite um convite anteriormente. Não necessita de fazer mais nada.',
    loading_invite_description_1: 'Carregando detalhes do convite...',
    loading_invite_description_2: 'Por favor, aguarde...',
    invite_done_unknown_description: 'O convite já não pode ser utilizado.',
    invite_done_refused_description: 'O convite já foi recusado. Já não pode ser utilizado.',
    invite_done_accepted_description: 'O convite já foi aceite. Já não pode ser utilizado.',
    invite_done_cancelled_description: 'O convite foi cancelado. Já não pode ser utilizado.',
    invite_done_expired_description: 'O convite expirou. Já não pode ser utilizado.',
    errors: {
      refuse_invite_error_message: 'Um erro ocorreu. Não foi possível recusar o convite.',
      accept_invite_error_message: 'Um erro ocorreu. Não foi possível aceitar o convite.',
      fetch_invite_error_message: 'Um erro ocorreu. Não foi possível abrir o convite.',
      fetch_invite_invalid_error_message: 'O convite que tentou abrir está inválido.',
    },
    success: {
      refuse_relationship_invite_success_message:
        'O convite foi recusado. {{name}} não faz parte da sua lista de contactos.',
      accept_relationship_invite_success_message:
        'O convite foi aceite. {{name}} faz agora parte da sua lista de contactos.',
      refuse_affiliation_invite_success_message:
        'O convite foi recusado. {{name}} não faz parte da sua lista de afiliações.',
      accept_affiliation_invite_success_message:
        'O convite foi aceite. {{name}} faz agora parte da sua lista de afiliações.',
    },
  },
  profile: {
    title: 'Perfil',
    exit_button: 'Sair',
    personal_information_section_title: 'Editar perfil',
    change_theme_section_title: 'Meu tema',
    legal_information_section_title: 'Informação legal',
    relationships_section_title: 'Minhas relações',
    affiliations_section_title: 'Minhas afiliações',
    logout_normal_button: 'Terminar sessão',
    logout_loading_button: 'Terminando sessão...',
    logout_error_button: 'Erro a terminar sessão',
    theme_input: 'Tema',
    dark_option: 'Escuro',
    light_option: 'Claro',
    system_option: 'Tema do sistema (automático)',
  },
  create_caregiver_account: {
    register_title: 'Registo',
    essential_information_step_description:
      'Bem-vindo à plataforma {{brand}}! Vamos criar uma conta de usuário para que se possa tornar cuidador e assim terminar o processo de aceitação do convite que recebeu.',
    essential_information_step: 'Informação essencial',
    personal_information_step: 'Informação pessoal',
    optional_information_step: 'Informação opcional',
    note_title: 'Nota',
    note_statement:
      'Os dados indicados com "*" são obrigatórios para a criação da conta de acesso para cuidador.',
    continue_button: 'Continuar',
    create_account_button: 'Aceitar convite e criar conta',
    start_using_button: 'Começar a utilizar',
    go_back_to_login_button: 'Ir para o ecrã de Entrar',
    go_back_button: 'Voltar para trás',
    success_title: 'Sucesso!',
    success_description:
      'Parabéns! Acabou de se tornar usuário da plataforma {{brand}}! Obrigado por se juntar a esta comunidade. Pode agora avançar para a sua conta de usuário.',
    error_title: 'Não foi possível criar a conta de usuário!',
    error_description:
      'Infelizmente algo falhou a criar a sua conta de usuário. Por favor, contacte o Apoio ao Cliente para obter ajuda.',
    relationship_type_input: 'Tipo de relacionamento com {{name}}',
    profession_input: 'Área profissional',
    profession_description_input: 'Profissão/Ocupação',
    email_address_input: 'Endereço de e-mail',
    password_input: 'Palavra-passe',
    repeat_password_input: 'Repetir a palavra-passe',
    legal_documents_title: 'Documentos legais',
    legal_documents_statement:
      'Confirmo que li e aceito as <0>Condições Gerais de Utilização</0> e a <1>Política de Privacidade e Proteção de Dados</1>.',
    full_name_input: 'Nome completo',
    display_name_input: 'Nome',
    mobile_number_input: 'Número de telemóvel',
    profile_picture_input: 'Foto de perfil',
    gender_input: 'Género',
    gender_placeholder_input: 'Selecione um género',
    language_input: 'Idioma',
    language_placeholder_input: 'Selecione um idioma',
    birthday_input: 'Data de nascimento',
    birthday_input_placeholder: 'Selecione uma data',
    tax_identification_number_input: 'Número de identificação fiscal',
    profile_picture_info_message: 'Para melhor resolução, a imagem deverá ser do tipo JPG/JPEG.',
    display_name_info_message:
      'Neste campo, indique o nome pelo qual é chamado no dia-a-dia. Este é o nome que os restantes usuários irão ver no sistema quando interagirem consigo.',
    errors: {
      form_validation_invalid_field:
        'Preencha este campo para completar a criação da conta de cuidador',
      form_validation_invalid_email: 'O e-mail indicado não é válido',
      form_validation_invalid_password_minimum_characters:
        'A palavra-passe deve ter pelo menos 6 carateres',
      form_validation_invalid_password_blank_spaces:
        'A palavra-passe não pode começar nem terminar com espaços em branco',
      form_validation_invalid_repeat_password_mismatch:
        'Por favor, certifique-se que as palavras-passe são iguais',
      form_validation_invalid_full_name_maximum_characters:
        'O nome completo deve ter menos do que {{max}} carateres',
      form_validation_invalid_display_name_maximum_characters:
        'O nome deve ter menos do que {{max}} carateres',
      form_validation_invalid_mobile_phone_number: 'O número de telemóvel indicado não é válido',
      form_validation_not_mobile_phone_number:
        'O número indicado não corresponde a um número de telemóvel',
      form_validation_invalid_tax_identification_number:
        'O número de identificação fiscal indicado não é válido',
      form_validation_invalid_legal_documents:
        'Aceite os documentos legais para completar a criação da conta de cuidador',
      enter_account_error_message: 'Um erro ocorreu. Não foi possível entrar na sua conta.',
      invalid_invite_error_message:
        'O convite que tentou utilizar para criar uma conta está inválido.',
    },
  },
  office: {
    generics: {
      cancel: 'Cancelar',
      save: 'Guardar',
      exit: 'Sair',
      back: 'Voltar',
      apply: 'Aplicar',
      save_pdf: 'Guardar em PDF (.pdf)',
      save_xlsx: 'Guardar em Excel (.xlsx)',
    },
    localized_string_input: {
      fields: {
        placeholder: 'Escreva uma tradução',
        rules: {
          required: 'Campo obrigatório',
          max_length: 'Não pode ter mais de {{ char }} caracteres.',
        },
      },
    },
    users_filters: {
      title: 'Filtros',
      clear_all: 'Limpar todos',
      sections: {
        socialResponses: {title: 'Serviços socioassistenciais'},
        shifts: {title: 'Turnos'},
        activeStatus: {
          title: 'Estados',
          options: {
            active: 'Ativos',
            inactive: 'Inativos',
          },
        },
        taskStatus: {
          title: 'Estados',
          options: {
            planned: 'Planeada',
            handled: 'Tratada',
            done: 'Realizada',
            not_done: 'Não realizada',
            not_handled: 'Não tratada',
          },
        },
        taskGroups: {
          title: 'Tarefas',
        },
        patientOccurrences: {
          title: 'Ocorrências utentes',
        },
        generalOccurrences: {
          title: 'Ocorrências gerais',
        },
      },
    },
    profile_type_tabs: {
      informal_caregiver: 'Informal',
      professional_caregiver: 'Profissional',
      system_administrator: 'Admin. do Sistema',
      partner: 'Parceiro',
    },
    informal_caregiver: {
      tabs: {
        activity: 'Atividade',
        profile: 'Perfil',
        media: 'Multimédia',
        reminders: 'Lembretes',
      },
      add_menu: {
        button: 'Adicionar',
        title: 'O que pretende adicionar?',
        create_reminder: 'Lembrete',
        send_photo: 'Fotografia',
        send_video: 'Vídeo',
        send_invite: 'Convite para cuidador',
      },
      relationship_picker: {
        caption: 'Está a ver o perfil de:',
        dialog_title: 'Selecione outro contacto',
      },
      caregiver_app_info_message:
        'Estamos ainda a trabalhar para melhorar esta área. Para tirar todo o proveito das funcionalidades {{brand}} enquanto familiar, amigo ou cuidador informal, por favor utilize a nossa aplicativo para dispositivos móveis <0>Android</0> ou <1>iOS</1>.',
    },
    professional_caregiver: {
      tabs: {
        statistics: 'Estatísticas',
        digital_inclusion: 'Inclusão digital',
        seniors: 'Utentes',
        applications: 'Aplicativos',
        services: 'Serviços',
        professional_caregivers: 'Equipa',
        activity: 'Atividade',
        devices: 'Dispositivos',
        user_cards: 'Cartões',
        licenses: 'Licenças',
        entity_photos: 'Fotografias',
        playlists: 'Listas de reprodução',
        start: 'Iniciar',
        planner: 'Calendário',
        occurrences: 'Ocorrências',
        settings: 'Configurações',
        sioslifeAcademy: 'Academia',
        myToDos: 'As minhas tarefas',
      },
      profile: {
        generic_header: {
          title: 'Perfil',
          subtitle: {
            write: 'Podes configurar o perfil e outras informações',
            read: 'Podes verificar todas as informações do perfil',
          },
          breadcrumbs: {
            team: 'Equipa',
            professionals: 'Profissionais',
            profile: 'Perfil',
          },
        },
      },
      affiliation_picker: {
        caption: 'Está a trabalhar na entidade:',
        dialog_title: 'Selecione outra entidade',
      },
      settings: {
        generic_header: {
          breadcrumbs: {
            settings: 'Configurações',
            institution: 'Instituição',
            licenses: 'Licenças',
            templates: 'Modelos',
            occurrences: 'Ocorrências',
            quick_access_devices: 'Acesso Rápido',
          },
          subtitle: {
            institution: {
              write: 'Podes configurar as informações da instituição',
              read: 'Podes verificar todas as informações da instituição',
            },
            activities_sector: 'Podes configurar as atividades do setor',
            licenses: 'Podes verificar as licenças',
            templates: {
              write: 'Podes configurar todos os modelos',
              read: 'Podes ver todos os modelos',
            },
            occurrences: 'Configurar e gerir os tipos de ocorrências',
            quick_access_devices: 'Configurar e gerir os dispositivos de acesso rápido',
          },
        },
        templates: {
          empty: 'Não existem templates',
          name: 'Nome',
          area: 'Área',
          description: 'Descrição',
          update: 'Última alteração',
          edit: 'Editar',
          delete: 'Apagar',
          revert: 'Reverter',
          duplicate: 'Duplicar',
          cancel: 'Cancelar',
          delete_title: 'Tem a certeza que deseja apagar o modelo "{{name}}"?',
          delete_subtitle: 'O plano de cuidados será mantido nos utentes',
          snackbar: {
            delete_success_message: 'O modelo "{{name}}" foi apagado com sucesso.',
            delete_error_message: 'Não foi possível apagar o modelo "{{name}}".',
            duplicate_success_message: 'O modelo "{{name}}" foi duplicado.',
            duplicate_error_message: 'Não foi possível duplicar o modelo "{{name}}".',
            edit_name_success_message: 'O modelo "{{name}}" foi editado com sucesso.',
            edit_name_error_message: 'Não foi possível editar o modelo "{{name}}".',
          },
          edit_name: 'Editar nome',
          alert: 'As alterações feitas neste modelo não irão afetar os utentes.',
          no_permissions: 'Não possui permissões para editar os modelos',
        },
        occurrences: {
          patients: 'Utentes',
          general: 'Geral',
          general_plural: 'Gerais',
          occurrence: 'Ocorrência',
          active: 'Ativa',
          action_required: 'Requer ação',
          notify: 'Perfis a notificar',
          all_profiles: 'Todos os perfis',
          add: 'Adicionar',
          add_modal_title: 'Adicionar tipo de ocorrência - ',
          add_modal_name: 'Nome do tipo de ocorrência',
          add_name_error: 'Nome já existente',
          delete_modal_title: 'Tem a certeza que deseja apagar o tipo de ocorrência "{{name}}"?',
          revert_modal_title: 'Tem a certeza que deseja reverter o tipo de ocorrência "{{name}}"?',
          delete_modal_subtitle: 'O tipo de ocorrência deixará de estar disponível para registo',
          revert_modal_subtitle: 'O tipo de ocorrência será revertido para seus valores padrão.',
          name_error: 'O nome da ocorrência deve ter menos do que {{max}} carateres',
          add_error_toast: 'Não foi possível adicionar o tipo de ocorrência "{{name}}".',
          add_success_toast: 'O tipo de ocorrência foi adicionado com sucesso!',
          edit_error_toast: 'Não foi possível editar o tipo de ocorrência "{{name}}".',
          edit_success_toast: 'O tipo de ocorrência foi editado com sucesso!',
          delete_error_toast: 'Não foi possível apagar o tipo de ocorrência "{{name}}".',
          delete_success_toast: 'O tipo de ocorrência "{{name}}" foi apagado com sucesso!',
          revert_success_toast: 'O tipo de ocorrência "{{name}}" foi revertido com sucesso!',
          delete_default: 'Configuração padrão - Não é possível apagar este tipo de ocorrência',
          edit_name_default: 'Configuração padrão - Não é possível editar o nome',
          default: 'configuração padrão',
          empty_general: 'Ainda não existem tipos de ocorrência gerais',
          empty_patients: 'Ainda não existem tipos de ocorrência com utentes',
          no_permissions: 'Não possui permissões para gerir os tipos de ocorrências',
        },
        quick_access: {
          empty: 'Não existem dispositivos de acesso rápido',
          name: 'Nome',
          type: 'Tipo',
          access: 'Último acesso',
          professional: 'Profissional',
          deactivate: 'Desativar',
          activate_device: 'Ativar neste dispositivo',
          activate_disabled: 'O acesso rápido já está ativado neste dispositivo',
          activate_disabled_ist:
            'O acesso rápido já está ativado neste dispositivo na instituição "{{name}}"',
          no_permissions: 'Não possui permissões para gerir os dispositivos de acesso rápido',
          activate_modal: {
            title: 'Ativar acesso rápido neste dispositivo',
            device_name: 'Nome do dispositivo',
            device_type: 'Tipo de dispositivo',
            success_message: 'O accesso rápido neste dispositivo foi ativado com sucesso!',
            error_message: 'Não foi possível ativar o acesso rápido neste dispositivo!',
            name_repeat_error: 'O nome do dispositivo já está a ser utilizado',
          },
          edit_modal: {
            title: 'Editar dispositivo:',
            success_message: 'O dispositivo de acesso rátpido foi editado com sucesso!',
            error_message: 'Não foi possível editar o dispositivo de acesso rápido!',
          },
          deactivate_modal: {
            title: 'Tem a certeza que deseja desativar o acesso rápido do dispositivo "{{name}}"?',
            subtitle:
              'O dispositivo poderá continua a ser utilizado normalmente com email e palavra-passe.',
            success_message: 'O acesso rápido neste dispositivo foi desativado com sucesso!',
            error_message: 'Não foi possível desativar o acesso rápido neste dispositivo!',
            button: 'Desativar',
            no_name: 'Sem nome',
          },
        },
      },
      seniors: {
        edit: {
          profile: {
            generic_header: {
              subtitle: {
                write: 'Podes configurar o perfil e outras informações importantes',
              },
            },
          },
        },
        table_view_screen: {
          name: 'Nome',
          social_response: 'Serviços socioassistenciais',
          age: 'Idade',
          room: 'Quarto',
          status: 'Estado ',
          role: 'Permissões',
          phone: 'Telefone',
          email: 'E-mail',
        },
      },
      shifts_allocations: {
        generic_header: {
          breadcrumbs: {
            team: 'Equipa',
            shifts_allocations: 'Atribuição de turnos',
          },
        },
        exit_edit_mode: 'Sair da edição',
        table: {
          collaborators: 'Colaboradores',
          today: 'Hoje',
          select_shift: 'Selecionar turno',
        },
      },
      roles_and_permissions: {
        generic_header: {
          breadcrumbs: {
            team: 'Equipa',
            roles_and_permissions: 'Cargo e Permissões',
          },
          subtitle: 'Criar cargo e gerir permissões',
        },
        create: 'Criar',
        edit: 'Editar',
        delete: 'Apagar',
        default_role: 'Configuração Padrão',
        confirm_delete: {
          title: 'Apagar cargo - {{ name }}',
          copy: {
            title: 'Tem a certeza que deseja apagar o cargo?',
            subtitle: 'Esta ação não pode ser revertida.',
          },
          snackbar: {
            error_message: 'Ocorreu um erro ao apagar o cargo.',
          },
        },
      },
      occurrences: {
        empty_title: 'Ainda não há ocorrências registadas',
        empty_subtitle: 'Prima adicionar para registar uma ocorrência',
        table_header_date: 'Data',
        table_header_time: 'Hora',
        table_header_type: 'Tipo',
        table_header_patient: 'Utente',
        table_header_responsible: 'Responsável',
        table_header_registered: 'Registado por',
        table_header_status: 'Estado',
        table_header_knowledge: 'Conhecimento',
        add_occurrence: 'Adicionar ocorrência',
        type_placeholder: 'Seleccione um tipo de ocorrência',
        local_placeholder: 'Escreva qual o local da ocorrência',
        patient_placeholder: 'Seleccione um utente',
        responsible_placeholder: 'Seleccione um responsável',
        details_placeholder: 'Escreva os detalhes da ocorrência',
        status: {
          registered: 'Registada',
          not_handled: 'Por tratar',
          handled: 'Tratada',
        },
        details: 'Detalhes',
        information: 'Informações da ocorrência',
        date_hour: 'Data e hora',
        acknowledge: 'Tomar conhecimento',
        add_notes: 'Adicionar notas',
        write_notes: 'Escreva uma nota',
        acknowledge_success: 'Tomou conhecimento da ocorrência.',
        acknowledge_error: 'Não foi possível tomar conhecimento da ocorrência.',
        handle_success: 'A ocorrência foi tratada com sucesso!',
        handle_error: 'Não foi possível tratar a ocorrência.',
        responsible: 'Responsável',
        registered_by: 'Registado por',
        took_acknowledge: 'Tomou conhecimento',
        history: {
          acknowledge: 'Tomou conhecimento da ocorrência',
          state_update: 'Alterou o estado da ocorrência',
        },
        notes_success: 'As notas foram adicionadas com sucesso.',
        notes_error: 'Não foi possível adicionar as notas.',
      },
    },
    system_administrator: {
      tabs: {
        statistics: 'Estatísticas',
        activity: 'Atividade',
        devices: 'Dispositivos',
        apiDocs: 'Doc. da API',
        applications: 'Aplicativos',
        entities: 'Entidades',
        cards: 'Cartões',
        licenses: 'Licenças',
        seniors: 'Utentes',
        caregivers: 'Cuidadores',
        playlists: 'Listas de reprodução',
        gdprErasureRequests: 'Direito ao esquecimento (LGPD)',
      },
    },
    partner: {
      tabs: {
        home: 'Início',
        apiDocs: 'Doc. da API',
      },
    },
    legacy_integration: {
      entity_services_disabled_alert_title: 'Não foi possível executar a ação.',
      entity_services_disabled_alert_message:
        'Lamentamos, mas não tem acesso a esta área. Por favor, contacte com o Apoio ao Cliente para mais informações.',
    },
    none: {
      tabs: {
        home: 'Início',
      },
    },
    permissions: {
      access_denied_message: 'Desculpe, não tem permissões para aceder a esta página.',
    },
    access: {
      denied_message_first: 'Para ter acesso a esta área é necessário ter o módulo de ',
      denied_message_second: ' Sioslife.',
      care_management: 'Gestão de Cuidados',
      digital_inclusion: 'Inclusão Digital',
      speak: 'Fale connosco!',
      tooltip:
        'Para ter accesso a esta opção é necessário ter o módulo de <strong>{{ module }}</strong> Sioslife.',
    },
    pending_tasks: {
      alert:
        'Atenção!<br>Identificámos que existem {{number}} tarefas já realizadas, pendentes de validação.',
      see_tasks: 'Ver tarefas',
      header_title: 'Tarefas pendentes',
      info: 'As tarefas apresentadas são dadas como realizadas automaticamente, sempre que os utentes utilizam o sistema interativo Sioslife.<br>O registo apenas será considerado efetivo após a respetiva validação das tarefas.',
      empty: 'Não existem tarefas pendentes.',
      all: 'Seleccionar todos',
      validate: 'Validar tarefas',
      modal_title: 'Tem a certeza que deseja validar {{number}} tarefas?',
      modal_observation: 'Escreva uma observação',
      modal_validate: 'Validar',
      snackbar_success: 'Todas as tarefas foram validadas com sucesso!',
      snackbar_error: 'Não foi possível validar as tarefas.',
      modal_task_validation_title:
        'Atenção!<br>Identificamos que esta tarefa já foi realizada e está pendente de validação.',
    },
  },
  seniors: {
    root: {
      title: 'Utentes',
      add_button: 'Adicionar',
      search_input_placeholder: 'Pesquisar utentes',
      name_header: 'Nome',
      gender_header: 'Género',
      state_header: 'Estado',
      state_value_true: 'Ativo',
      state_value_false: 'Inativo',
      no_seniors: 'Ainda não há utentes registados',
      deleted_senior_dialog_title: 'Perfil do utente "{{name}}" apagado',
      deleted_senior_dialog_message: 'O perfil do utente já não existe na lista de utentes.',
      deleted_senior_dialog_ok_button: 'Ok',
      senior: 'Utente',
      seniors: 'Utentes',
      modals: {
        add_senior_flow: {
          form: {
            title: 'Criar Utente',
            cancel: 'Cancelar',
            add: 'Adicionar',
            inputs: {
              active: 'Ativo',
              name: {
                label: 'Nome completo',
                placeholder: 'Nome e apelido',
                required_message: 'Escreva um nome e apelido',
                pattern_message: 'O nome inserido não é válido',
                max_message:
                  'O nome inserido não poderá ultrapassar os {{ characters }} caracteres',
              },
              name_alias: {
                label: 'Como gosta de ser chamado(a)',
                placeholder: 'Nome',
                required_message: 'Escreva um nome',
                pattern_message: 'O nome inserido não é válido',
                max_message:
                  'O nome inserido não poderá ultrapassar os {{ characters }} caracteres',
              },
              birthdate: {
                label: 'Data de nascimento',
                placeholder: 'Selecione uma data de nascimento',
                required_message: 'Selecione uma data de nascimento',
              },
            },
          },
          confirm: {
            title: 'Criar Utente',
            confirm_title: 'Tem a certeza que pretende adicionar "{{ name }}”?',
            confirm_subtitle: 'O novo utente será adicionado ao sistema.',
            cancel: 'Cancelar',
            add: 'Adicionar',
          },
          snackbar: {
            success_message: 'O utente "{{name}}" foi criado com sucesso!',
            error_message: 'Erro a guardar',
          },
        },
      },
    },
    edit: {
      senior_dcm: {
        careplan: {
          load_error: 'Não foi possível carregar os dados do plano de cuidados.',
          caregiver: {
            title: 'Responsável',
            unnasigned: 'Não definido',
            unnasigned_tooltip: 'Não possui permissões para editar o Responsável',
          },
          activities: {
            add_button: 'Adicionar',
            load_error: 'Não foi possível carregar grupos de actividades para adicionar.',
            empty_state: {
              title: 'Ainda não há atividades registadas.',
              subtitle: 'Prima adicionar para registar uma atividade.',
            },
          },
          on_save: {
            snackbar: {
              success_message: 'Plano de cuidados guardado',
              error_message: 'Não foi possível guardar o plano de cuidados.',
            },
          },
          on_update: {
            snackbar: {
              success_message: 'Plano de cuidados actualizado',
              error_message: 'Não foi possível actualizar o plano de cuidados.',
            },
          },
          on_delete: {
            title: 'Tem a certeza que deseja apagar o plano de cuidados do utente "{{name}}"?',
            description: 'Esta ação irá apagar o plano de cuidados permanentemente.',
            snackbar: {
              success_message: 'Plano de cuidados eliminado',
              error_message: 'Não foi possível eliminar o plano de cuidados.',
            },
          },
        },
        individual_plan: {
          add_cares: 'Adicionar cuidados',
          caregiver: {
            title: 'Responsável',
            coordinator: 'Coordenador',
            unassigned: 'Não definido',
            unassigned_tooltip: 'Não possui permissões para editar o Responsável',
          },
          observations: 'Observações',
          created_at: 'Criado em',
          valid_until: 'Válido até',
          not_defined: 'Não definido',
          empty_state: {
            title: 'Ainda não há diagnósticos registados no plano individual.',
            subtitle: 'Prima para adicionar um diagnóstico.',
          },
          new_diagnostic: 'Novo diagnóstico',
          diagnostic: 'Diagnóstico',
          goal: 'Objetivo geral',
          new_goal: 'Novo objetivo geral',
          new_objective: 'Novo objetivo específico',
          history: {
            action: 'Histórico',
            title: 'Histórico do plano individual',
            close: 'Fechar',
            version: 'Versão {{revision}}',
            unknown_author: 'Usuário desconhecido',
            load_error: 'Não foi possível carregar o histórico.',
            source: {
              manual: 'Manual',
              ai: 'IA',
              restore: 'Restauração',
            },
            summary: {
              entity: {
                plan: 'Plano',
                diagnostic: 'Diagnóstico',
                goal: 'Objetivo geral',
                objective: 'Objetivo específico',
              },
              entity_plural: {
                plan: 'planos',
                diagnostic: 'diagnósticos',
                goal: 'objetivos gerais',
                objective: 'objetivos específicos',
              },
              field: {
                definition: 'definição',
                responsibleUserId: 'responsável',
                cares: 'cuidados',
                activityTypesIds: 'atividades associadas',
                indicators: 'indicadores',
                isActive: 'status',
                coordinatorUserId: 'coordenador',
                validUntil: 'validade',
                observations: 'observações',
                diagnosticsOrder: 'ordem dos diagnósticos',
              },
              created: '{{entity}} criado: "{{name}}"',
              created_unnamed: '{{entity}} criado',
              created_many: '{{count}} {{entities}} criados',
              deleted: '{{entity}} apagado: "{{name}}"',
              deleted_unnamed: '{{entity}} apagado',
              deleted_many: '{{count}} {{entities}} apagados',
              updated: 'Alteração em {{entity}}: {{field}}',
              updated_many: 'Alteração em {{count}} {{entities}}: {{field}}',
              more: 'e mais {{count}} alterações',
              restored_from: 'Restaurado a partir da versão {{revision}}',
            },
            restore: {
              button: 'Restaurar esta versão',
              prompt_title: 'Restaurar a versão {{revision}}?',
              prompt_description:
                'O plano individual passa a ter o conteúdo desta versão. É criada uma nova versão e nenhuma versão anterior é apagada, por isso você pode voltar atrás a qualquer momento.',
              confirm: 'Restaurar',
              success: 'Versão {{revision}} restaurada.',
              conflict:
                'O plano foi alterado nesse meio tempo. Ele foi atualizado, tente novamente.',
              error: 'Não foi possível restaurar a versão.',
            },
            view_version: 'Ver versão',
            back: 'Voltar',
            detail_load_error: 'Não foi possível carregar esta versão.',
            no_diagnostics: 'Esta versão não tem diagnósticos.',
            detail_note: 'Estado do plano nesta versão (somente leitura).',
            change_type_unknown: 'Alteração',
            change_type: {
              baseline: 'Estado inicial (antes do histórico)',
              'plan-created': 'Plano criado',
              'plan-updated': 'Plano atualizado',
              'plan-archived': 'Plano arquivado',
              'plan-reactivated': 'Plano reativado',
              'diagnostic-created': 'Diagnóstico criado',
              'diagnostic-updated': 'Diagnóstico atualizado',
              'diagnostic-deleted': 'Diagnóstico apagado',
              'goal-created': 'Objetivo geral criado',
              'goal-updated': 'Objetivo geral atualizado',
              'goal-deleted': 'Objetivo geral apagado',
              'objective-created': 'Objetivo específico criado',
              'objective-updated': 'Objetivo específico atualizado',
              'objective-deleted': 'Objetivo específico apagado',
              'bulk-append': 'Diagnósticos adicionados em lote',
              'bulk-replace': 'Diagnósticos substituídos em lote',
              restored: 'Versão restaurada',
            },
          },
          ai_generate: {
            title: 'Gerar plano com IA',
            description:
              'Informe o tema clínico que deseja priorizar ou deixe a IA decidir com o plano automático. O texto serve apenas para indicar o tema, não são instruções para a IA.',
            focus_label: 'Foco do plano',
            focus_placeholder:
              'Ex.: plano com foco nas atividades cognitivas para reduzir o declínio mental',
            automatic: 'Plano automático',
            with_focus: 'Gerar com foco',
          },
          ai_proposal: {
            title: 'Proposta da IA',
            subtitle: 'Esta é uma pré-visualização. Nada é salvo no plano individual até aprovar.',
            discard: 'Descartar',
            approve: 'Aprovar',
            approved: 'Proposta da IA adicionada ao plano individual.',
            replaced:
              'Plano individual substituído pela proposta da IA. A versão anterior ficou guardada no histórico.',
            apply_prompt: {
              title: 'Deseja apagar os dados do plano individual existente?',
              description:
                'O plano individual já tem diagnósticos. Você pode apagá-los e ficar apenas com a proposta da IA, ou adicionar a proposta aos diagnósticos existentes. Se apagar, a versão anterior fica guardada no histórico do plano.',
              replace: 'Apagar plano',
              append: 'Adicionar ao plano',
            },
            rate_limited:
              'Você atingiu o limite de gerações com IA por hora. Tente novamente em {{minutes}} min.',
            save_error: 'Não foi possível salvar a proposta no plano individual.',
            invalid_references:
              'Não foi possível salvar: a proposta excede os limites permitidos ou tem referências inválidas.',
            error: 'Não foi possível gerar o plano com IA. Tente novamente.',
          },
          on_delete: {
            title: 'Tem a certeza que deseja arquivar o plano individual do utente "{{name}}"?',
            description:
              'Esta ação irá arquivar o plano individual. Poderá consultar o mesmo mais tarde.',
            snackbar: {
              success_message: 'Plano individual arquivado',
              error_message: 'Não foi possível arquivar o plano individual.',
            },
          },
          add_diagnostic: {
            title: 'Adicionar diagnóstico',
            edit: 'Editar diagnóstico',
            form: {
              definition: {
                label: 'Definição',
                placeholder: 'Pequena descrição do diagnóstico',
                error_message: 'A definição deve ter entre 1 e {{max}} carateres.',
              },
            },
          },
          on_diagnostic_remove: {
            header: 'Apagar diagnóstico',
            title: 'Tem a certeza que deseja apagar o diagnóstico "{{name}}"?',
            description: 'Esta ação irá apagar o diagnóstico permanentemente.',
          },
          add_diagnostic_goal: {
            title: 'Adicionar objetivo geral',
            edit: 'Editar objetivo geral',
            form: {
              definition: {
                label: 'Definição',
                placeholder: 'Pequena descrição do objetivo geral',
                error_message: 'A definição deve ter entre 1 e {{max}} carateres.',
              },
              responsibleUser: {
                label: 'Responsável',
                placeholder: 'Selecione um responsável',
              },
            },
          },
          on_diagnostic_goal_remove: {
            header: 'Apagar objetivo geral',
            title: 'Tem a certeza que deseja apagar o objetivo geral "{{name}}"?',
            description: 'Esta ação irá apagar o objetivo geral permanentemente.',
          },
          add_diagnostic_goal_objective: {
            title: 'Adicionar objetivo específico',
            edit: 'Editar objetivo específico',
            form: {
              definition: {
                label: 'Definição',
                placeholder: 'Pequena descrição do objetivo específico',
                error_message: 'A definição deve ter entre 1 e {{max}} carateres.',
              },
            },
            cares: {
              add_non_automatic: 'Adicionar cuidado não automático',
              non_automatic_title: 'Cuidados não automáticos',
              max_reached: 'Você pode adicionar no máximo {{max}} cuidados não automáticos.',
              too_long: 'O cuidado não automático não pode ter mais de {{max}} caracteres.',
            },
            snackbar: {
              error: 'Não foi possível salvar o objetivo específico.',
              invalid_references:
                'Não foi possível salvar: um dos cuidados ou indicadores selecionados não está disponível para esta entidade.',
            },
            table: {
              definition: 'Objetivo específico',
              cares: 'Cuidados',
              indicators: 'Indicadores',
              targets: 'Metas',
              execution: 'Execução',
            },
          },
          on_diagnostic_goal_objective_remove: {
            header: 'Apagar objetivo específico',
            title: 'Tem a certeza que deseja apagar o objetivo específico "{{name}}"?',
            description: 'Esta ação irá apagar o objetivo específico permanentemente.',
          },
        },
      },
    },
  },
  senior_tabs: {
    tabs: {
      profile: 'Perfil',
      caregivers: 'Cuidadores',
      applications: 'Aplicativos',
      devices: 'Dispositivos',
      activity: 'Atividade',
      statistics: 'Estatísticas',
      user_cards: 'Cartões',
      health: 'Saúde',
      affiliations: 'Afiliações',
      playlists: 'Listas de reprodução',
      external_accounts: 'Contas Externas',
    },
    errors: {
      senior_does_not_exist_error: 'Não foi possível obter o utente que tentou aceder.',
    },
  },
  health_profile_form: {
    clinical_conditions_title: 'Patologias/Limitações',
    visual_difficulty_label: 'Dificuldades visuais',
    hearing_difficulty_label: 'Dificuldades auditivas',
    cognitive_deficit_label: 'Défice cognitivo',
    parkinson_label: 'Parkinson',
    alzheimer_label: 'Alzheimer',
    other_dementias_label: 'Outras demências',
    reduced_mobility_upper_limbs_label: 'Mobilidade reduzida dos membros superiores',
    reduced_mobility_lower_limbs_label: 'Mobilidade reduzida dos membros inferiores',
    paralysis_left_limb_label: 'Paralisia do membro superior esquerdo',
    paralysis_right_limb_label: 'Paralisia do membro superior direito',
    wheelchair_label: 'Cadeira de rodas',
    illiterate_label: 'Analfabetismo',
    disable_clinical_conditions_tooltip: 'Não possui permissões para editar as condições clínicas',
    errors: {
      update_health_profile_error_message:
        'Um erro ocorreu. Não foi possível atualizar o perfil de saúde.',
    },
    noHealthMeasurements: 'Não existem medições de saúde.',
  },
  health_charts: {
    noData: 'Não existem dados para apresentar.',
    heartRate: {
      subtitle1: 'normal até 120bpm',
    },
    bloodOxygen: {
      subtitle1: 'mínimo 50%',
      subtitle2: 'máximo 100%',
    },
    bloodPressure: {
      subtitle1: 'Sistólica: até 120mmHg',
      subtitle2: 'Diastólica: até 80mmHg',
    },
    bloodGlucose: {
      subtitle1: 'mínimo 80mg/dL',
      subtitle2: 'máximo 180mg/dL',
    },
    bodyTemperature: {
      subtitle1: 'normal até 36ºC',
    },
    bodyWeight: {
      subtitle1: undefined,
    },
    errors: {
      fetch: 'Ocorreu um erro ao carregar os dados de saúde.',
    },
  },
  health_measurements: {
    title: 'Últimas Medições',
    units: {
      beatsPerMinute: 'bpm',
      breathsPerMinute: 'rpm',
    },
  },
  senior_caregivers_listing: {
    invite_caregiver_button: 'Convidar cuidador',
    invites_history_button: 'Ver convites',
    no_caregivers: 'Não existem cuidadores.',
    name_header: 'Nome',
    relationship_type_header: 'Tipo',
    remove_relationship_header: 'Remover relação',
    remove_relationship_dialog_title:
      'Tem a certeza que deseja remover a relação entre o utente {{senior}} e o cuidador {{caregiver}}?',
    remove_relationship_dialog_message:
      'Ao remover esta relação, o utente {{senior}} não poderá mais comunicar com o cuidador {{caregiver}}.',
    remove_relationship_button: 'Remover',
    remove_relationship_button_tooltip: 'Não possui permissões para remover a relação',
    remove_relationship_error_message:
      'Não foi possível remover a relação entre o utente {{senior}} e o cuidador {{caregiver}}.',
    remove_relationship_success_message:
      'A relação entre o utente {{senior}} e o cuidador {{caregiver}} foi removida com sucesso.',
  },
  senior_relationships_invites_history: {
    title: 'Convites',
    no_invites: 'Não existem convites.',
    invite_caregiver_button: 'Convidar cuidador',
    process_closed: 'Fechado',
    cancel_button: 'Cancelar',
    name_header: 'Nome',
    contact_header: 'Contacto',
    state_header: 'Estado do convite',
    status_header: 'Processo',
    cancel_invite_dialog_title:
      'Tem a certeza que deseja cancelar o convite para {{caregiver}} ser cuidador de {{senior}}?',
    cancel_invite_dialog_message:
      'Ao cancelar este convite, o cuidador {{caregiver}} não poderá aceitar o convite para comunicar com o utente {{senior}}.',
    cancel_invite_dialog_action_yes_button: 'Cancelar convite',
    cancel_invite_dialog_action_no_button: 'Manter convite',
    cancel_invite_error_message:
      'Não foi possível cancelar o convite para {{caregiver}} ser cuidador de {{senior}}',
    cancel_invite_success_message:
      'O convite para {{caregiver}} ser cuidador de {{senior}} foi cancelado.',
  },
  informal_caregiver_invite_form: {
    title: 'Convidar cuidador',
    name_input: 'Nome',
    contact_method_input: 'Método de contacto',
    email_input: 'Endereço de e-mail',
    mobile_phone_number_input: 'Número de telemóvel',
    save_button: 'Enviar convite',
    saving_button: 'Enviando...',
    save_dialog_title: 'Enviar convite para {{name}}?',
    save_dialog_message:
      'Ao enviar, {{name}} irá receber um convite para comunicar e ter acesso a algumas informações de {{senior}}.',
    save_dialog_action_save_button: 'Enviar',
    router_prompt_title: 'Tem a certeza que deseja sair sem enviar o convite?',
    router_prompt_message: 'Ao sair, perderá todas as informações inseridas no convite.',
    cancel_toast: 'O convite foi cancelado.',
    success_message: 'O convite foi enviado para {{name}}.',
    errors: {
      form_validation_invalid_field: 'Preencha este campo para completar o convite',
      form_validation_invalid_mobile_phone_number: 'O número de telemóvel indicado não é válido',
      form_validation_not_mobile_phone_number:
        'O número indicado não corresponde a um número de telemóvel',
      form_validation_invalid_email: 'O endereço de e-mail indicado não é válido',
      error_message: 'Não foi possível enviar o convite para {{name}}.',
    },
  },
  devices_associations: {
    identifier_header: 'Identificador',
    type_header: 'Tipo',
    state_header: 'Estado',
    state_value_true: 'Ativo',
    state_value_false: 'Inativo',
    remove_device_header: 'Remover dispositivo',
    no_devices: 'Não existem dispositivos.',
    associate_device_button: 'Associar dispositivo',
    remove_device_dialog_title:
      'Tem a certeza que deseja remover a associação entre o dispositivo com identificação "{{identifier}}" e {{name}}?',
    remove_device_dialog_message:
      'Ao remover a associação, {{name}} não poderá mais utilizar o dispositivo para usufruir do sistema {{brand}}.',
    remove_device_button: 'Remover',
    remove_device_button_disabled_tooltip: 'Não possui permissões para remover o dispositivo',
    remove_device_error_message:
      'Não foi possível remover a associação com o dispositivo "{{identifier}}".',
    remove_device_success_message:
      'A associação com o dispositivo "{{identifier}}" foi removida com sucesso.',
  },
  user_cards_associations: {
    name_header: 'Nome',
    state_header: 'Estado',
    state_value_true: 'Ativo',
    state_value_false: 'Inativo',
    remove_card_header: 'Remover cartão',
    associate_card_button: 'Associar cartão',
    no_cards: 'Não existem cartões.',
    remove_card_dialog_title:
      'Tem a certeza que deseja remover a associação entre o cartão "{{card}}" e {{name}}?',
    remove_card_dialog_message:
      'Ao remover a associação, {{name}} não poderá mais utilizar o cartão para usufruir do sistema {{brand}}.',
    remove_card_button: 'Remover',
    remove_card_button_disabled_tooltip: 'Não possui permissões para remover o cartão',
    remove_card_error_message:
      'Não foi possível remover a associação com o cartão  "{{identifier}}".',
    remove_card_success_message:
      'A associação com o cartão "{{identifier}}" foi removida com sucesso.',
  },
  associate_user_card_form: {
    title: 'Associar cartão',
    card_name_input: 'Nome do cartão',
    entity_input: 'Entidade',
    card_name_info_message:
      'Este campo só pode ser preenchido se uma entidade for previamente selecionada.',
    save_button: 'Guardar',
    saving_button: 'Guardando...',
    save_dialog_title: 'Associar o cartão "{{card}}" a {{name}}?',
    save_dialog_message:
      'Ao associar o cartão, {{name}} poderá utilizá-lo para usufruir do sistema {{brand}}.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Associar',
    router_prompt_title: 'Tem a certeza que deseja sair sem associar o cartão?',
    router_prompt_message: 'Ao sair, perderá todas as informações inseridas.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Sim, sair',
    cancel_toast: 'A associação foi cancelada.',
    no_affiliations_info_message:
      'O usuário não possui afiliação com nenhuma entidade. Assim, não é possível associar cartões ao usuário.',
    success: {
      save_success_button: 'Guardado!',
      success_message: 'O cartão foi associado a {{name}}.',
    },
    errors: {
      error_message: 'Não foi possível associar o cartão a {{name}}.',
      error_on_save_button: 'Erro a guardar',
      form_validation_invalid_field: 'Preencha este campo para completar a associação do cartão',
    },
  },
  associate_device_form: {
    title: 'Associar dispositivo',
    identifier_input: 'Identificador do dispositivo',
    entity_input: 'Entidade',
    save_button: 'Guardar',
    saving_button: 'Guardando...',
    save_dialog_title: 'Associar o dispositivo com identificação "{{device}}" a {{name}}?',
    save_dialog_message:
      'Ao associar o dispositivo, {{name}} poderá utilizá-lo para usufruir do sistema {{brand}}.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Associar',
    router_prompt_title: 'Tem a certeza que deseja sair sem associar o dispositivo?',
    router_prompt_message: 'Ao sair, perderá todas as informações inseridas.',
    cancel_toast: 'A associação foi cancelada.',
    no_affiliations_info_message:
      'O usuário não possui afiliação com nenhuma entidade. Assim, não é possível associar dispositivos ao usuário.',
    success: {
      save_success_button: 'Guardado!',
      success_message: 'O dispositivo foi associado a {{name}}.',
    },
    errors: {
      error_message: 'Não foi possível associar o dispositivo a {{name}}.',
      error_on_save_button: 'Erro a guardar',
      form_validation_invalid_field:
        'Preencha este campo para completar a associação do dispositivo',
    },
  },
  external_accounts: {
    types: {
      call: 'Voximplant',
      partner_p3_n1: 'Hope Care',
      partner_p3_n2: 'Altice SmartAL',
      partner_p3_n3: 'DeepClick',
      partner_p3_n4: 'Itelcare (Kerox)',
    },
    list: {
      type_header: 'Tipo',
      delete_account_header: 'Excluir',
      delete_account_button: 'Excluir',
      associate_account_button: 'Adicionar conta',
      no_accounts: 'Não existem contas externas.',
      error_message: 'Não foi possível obter as contas externas.',
      delete_account_dialog_title: 'Excluir a conta {{type}}?',
      delete_account_dialog_description: 'Tem certeza que deseja excluir esta conta externa?',
      delete_account_dialog_cannot_be_undone: 'Esta ação não pode ser desfeita.',
      delete_account_dialog_confirm_button: 'Excluir',
      delete_account_dialog_cancel_button: 'Cancelar',
      delete_account_success_message: 'A conta {{type}} foi excluída com sucesso.',
      delete_account_error_message: 'Não foi possível excluir a conta {{type}}.',
    },
    associate: {
      title: 'Adicionar conta externa',
      type_input: 'Tipo de conta',
      save_button: 'Criar',
      saving_button: 'Criando...',
      save_dialog_title: 'Criar a conta {{type}}?',
      save_dialog_message: 'Tem certeza que deseja criar esta conta externa?',
      save_dialog_action_cancel_button: 'Cancelar',
      save_dialog_action_save_button: 'Criar',
      no_available_types_message:
        'Já existe uma conta externa de todos os tipos disponíveis para este usuário.',
      success_message: 'A conta {{type}} foi criada com sucesso.',
      errors: {
        generic_message: 'Não foi possível criar a conta externa.',
        conflict_message: 'Já existe uma conta externa deste tipo para este usuário.',
        provider_error_message:
          'O provedor externo falhou ao criar a conta. Tente novamente mais tarde.',
        error_on_save_button: 'Erro',
      },
    },
    details: {
      delete_button: 'Excluir conta',
      fields: {
        password: 'Senha',
        application_name: 'Nome do aplicativo',
        username: 'Usuário',
        language: 'Idioma',
        project_identification: 'Identificação do projeto',
        serial: 'Número de série',
        profile: 'Perfil',
        timezone: 'Fuso horário',
        institution_id: 'ID da instituição',
        instance_name: 'Nome da instância',
        manufacturer: 'Fabricante',
        model: 'Modelo',
        user_id: 'ID do usuário',
        patient_id: 'ID do paciente',
        email: 'E-mail',
      },
      errors: {
        not_found_message: 'Não foi possível obter a conta externa.',
      },
    },
  },
  caregivers: {
    root: {
      title: 'Cuidadores',
      search_input_placeholder: 'Pesquisar cuidadores',
      name_header: 'Nome',
      email_header: 'E-mail',
      state_header: 'Estado',
      state_value_true: 'Ativo',
      state_value_false: 'Inativo',
      no_caregivers: 'Não existem cuidadores.',
    },
  },
  professional_caregivers: {
    root: {
      title: 'Profissionais',
      invite_button: 'Convidar profissional',
      search_input_placeholder: 'Pesquisar profissionais',
      name_header: 'Nome',
      email_header: 'E-mail',
      affiliation_type_header: 'Tipo',
      state_header: 'Estado',
      state_value_true: 'Ativo',
      state_value_false: 'Inativo',
      no_professional_caregivers: 'Não existem profissionais.',
      deleted_professional_caregiver_dialog_title: 'Perfil do profissional {{name}} apagado',
      deleted_professional_caregiver_dialog_message:
        'O perfil do profissional já não existe na lista de profissionais.',
      deleted_professional_caregiver_dialog_ok_button: 'Ok',
      colaborator: 'colaborador',
      colaborators: 'colaboradores',
      modals: {
        add_staff_flow: {
          form: {
            title: 'Adicionar colaborador',
            cancel: 'Cancelar',
            send_invite: 'Enviar convite',
            create_profile: 'Criar perfil',
            inputs: {
              name: {
                label: 'Nome',
                placeholder: 'Nome e apelido',
                required_message: 'Escreva o nome completo',
                pattern_message: 'O nome inserido não é válido',
                max_message:
                  'O nome inserido não poderá ultrapassar os {{ characters }} caracteres',
              },
              email: {
                label: 'Email',
                placeholder: 'Escreva um email',
                required_message: 'O campo email é obrigatório',
                pattern_message: 'O campo email não é válido',
              },
              roles: {
                label: 'Cargo',
                placeholder: 'Selecionar cargo',
              },
              invitation_type: {
                label: 'Como criar o perfil?',
                placeholder: 'Selecione uma opção',
                required_message: 'Tem de selecionar uma opção de envio',
                via_email: 'Convidar por Endereço de E-mail',
                via_sms: 'Convidar por Mensagem SMS',
                via_quickAccessCode: 'Criar com Código de Acesso Rápido',
              },
              phone_number: {
                label: 'Número de telemóvel/telefone',
                placeholder: 'Insira um número de telemóvel/telefone',
                required_message: 'O campo número de telemóvel/telefone é obrigatório',
                is_valid_message: 'Número de telemóvel/telefone inválido ',
              },
              quick_access_code: {
                label: 'Código de Acesso Rápido',
                placeholder: 'Insira um código de acesso rápido com 4 dígitos',
                required_message: 'O campo código de acesso rápido é obrigatório',
                is_valid_message: 'Código de acesso rápido inválido',
              },
            },
          },
          confirm: {
            title: 'Enviar convite',
            title_quickAccessCode: 'Criar perfil',
            confirm_title: 'Tem a certeza que pretende enviar um convite para {{name}}?',
            confirm_title_quickAccessCode:
              'Tem a certeza que pretende criar um perfil para {{name}}?',
            confirm_subtitle:
              '{{name}} passará a ter acesso à algumas informações sobre a {{institution}}.',
            cancel: 'Cancelar',
            send_invite: 'Enviar convite',
            create_profile: 'Criar perfil',
          },
          success: {
            title: 'Enviar convite',
            title_quickAccessCode: 'Criar perfil',
            confirm_title: 'O convite foi enviado!',
            confirm_title_quickAccessCode: 'O perfil foi criado!',
            confirm_subtitle:
              'O colaborador será adicionado integralmente à equipa após confirmar o convite.',
            confirm_subtitle_quickAccessCode:
              'O colaborador foi adicionado integralmente à equipa e deverá mudar o seu código de acesso rápido logo que possível.',
            confirm: 'Fechar',
          },
          snackbar: {
            success_message: 'Foi enviado um convite a {{name}}.',
            error_message: 'Ocorreu um erro ao enviar o convite',
            success_message_create_profile: 'O perfil de {{name}} foi criado.',
            error_message_create_profile: 'Ocorreu um erro ao criar o perfil',
          },
        },
      },
    },
  },
  caregiver_tabs: {
    tabs: {
      profile: 'Perfil',
      affiliations: 'Afiliações',
      applications: 'Aplicativos',
      user_cards: 'Cartões',
      playlists: 'Listas de reprodução',
    },
    errors: {
      caregiver_does_not_exist_error: 'Não foi possível obter o cuidador que tentou aceder.',
    },
  },
  user_affiliations: {
    entity_name_header: 'Nome da entidade',
    entity_type_header: 'Tipo de entidade',
    affiliation_type_header: 'Tipo de afiliação',
    remove_affiliation_header: 'Remover afiliação',
    remove_affiliation_button: 'Remover',
    remove_affiliation_dialog_title:
      'Remover a afiliação entre o usuário {{user}} e a entidade {{entity}}?',
    remove_affiliation_dialog_message:
      'Ao remover esta afiliação, o usuário {{user}} deixará de ter acesso à entidade {{entity}}.',
    remove_affiliation_dialog_action_yes_button: 'Sim, remover',
    remove_affiliation_dialog_action_no_button: 'Cancelar',
    update_affiliation_dialog_title:
      'Alterar o tipo de afiliação do usuário {{user}} com a entidade {{entity}}?',
    update_affiliation_dialog_message:
      'Ao alterar, o tipo de afiliação do usuário será atualizado para "{{type}}" na entidade {{entity}}.',
    update_affiliation_dialog_action_yes_button: 'Sim, alterar',
    update_affiliation_dialog_action_no_button: 'Cancelar',
    no_affiliations: 'Não existem afiliações.',
    errors: {
      remove_affiliation_error_message: 'Um erro ocorreu. Não foi possível remover a afiliação.',
      update_affiliation_error_message:
        'Um erro ocorreu. Não foi possível alterar o tipo de afiliação.',
    },
  },
  user_applications: {
    entity_filter: 'Entidade',
    search_input_placeholder: 'Pesquisar aplicativos',
    name_header: 'Nome',
    category_header: 'Categoria',
    state_header: 'Estado',
    state_value_true: 'Ativo',
    state_value_false: 'Inativo',
    disabled_state_tooltip: 'Não possui permissões para alterar o estado da aplicativo',
    no_applications: 'Não existem aplicativos.',
    enable_application_dialog_title: 'Tem a certeza que deseja ativar a aplicativo {{name}}?',
    enable_application_dialog_message:
      'Ao ativar a aplicativo, ela estará disponível para {{name}}.',
    enable_application_for_entity_dialog_message:
      'Ao ativar a aplicativo, ela estará disponível para {{name}} na entidade {{entity}}.',
    enable_application_dialog_action_yes_button: 'Sim, ativar',
    enable_application_dialog_action_no_button: 'Cancelar',
    disable_application_dialog_title: 'Tem a certeza que deseja desativar a aplicativo {{name}}?',
    disable_application_dialog_message:
      'Ao desativar a aplicativo, ela deixará de estar disponível para {{name}}.',
    disable_application_for_entity_dialog_message:
      'Ao desativar a aplicativo, ela deixará de estar disponível para {{name}} na entidade {{entity}}.',
    disable_application_dialog_action_yes_button: 'Sim, desativar',
    disable_application_dialog_action_no_button: 'Cancelar',
    no_affiliations_info_message:
      'O usuário não possui afiliação com nenhuma entidade. Assim, não é possível consultar os aplicativos do usuário.',
    errors: {
      enable_application_error_message: 'Um erro ocorreu. Não foi possível ativar a aplicativo.',
      disable_application_error_message:
        'Um erro ocorreu. Não foi possível desativar a aplicativo.',
      update_state_application_error_message:
        'Um erro ocorreu. Não foi possível atualizar o estado da aplicativo.',
    },
  },
  user_form: {
    title_create_senior: 'Adicionar utente',
    title_current_user_profile: 'Perfil',
    delete_button: 'Apagar este perfil',
    delete_caregiver_dialog_title: 'Apagar perfil do cuidador {{name}}?',
    delete_caregiver_dialog_content:
      'Ao apagar este perfil, ele será eliminado permanentemente da lista de cuidadores e a afiliação de {{name}} com a entidade {{entity}} será eliminada.',
    delete_senior_dialog_title: 'Tem a certeza que deseja apagar "{{name}}" da lista de utentes?',
    delete_senior_dialog_content: 'O utente será marcado como inativo.',
    delete_caregiver_invite_title: 'Cancelar o convite para {{name}}?',
    delete_caregiver_invite_content: 'Ao cancelar, {{name}} não poderá aceitar o convite.',
    entity_input: 'Entidade',
    full_name_input: 'Nome completo',
    display_name_input: 'Como gosta de ser chamado (a)',
    state_input: 'Estado {{ role }} ',
    state_value_true: 'Ativo',
    state_value_false: 'Inativo',
    gender_input: 'Género',
    gender_placeholder_input: 'Selecione um género',
    birthday_input: 'Data de nascimento',
    date_placeholder_input: 'Selecione uma data',
    admission_date_input: 'Data de admissão',
    departure_date_input: 'Data de saída',
    address_input: 'Morada',
    address_placeholder_input: 'Escreva a morada',
    religion_input: 'Religião',
    religion_placeholder_input: 'Selecione uma religião',
    profession_input: 'Área profissional',
    profession_description_input: 'Profissão/Ocupação',
    social_network_city_input: 'Localização Social',
    language_input: 'Idioma',
    language_placeholder_input: 'Selecione um idioma',
    region_input: 'Região',
    region_placeholder_input: 'Selecione uma região',
    input_disabled_by_unknown_region: 'Precisa de adicionar a "Região" para editar este campo',
    email_input: 'Endereço de e-mail',
    email_placeholder_input: 'Adicione o email',
    mobile_phone_number_input: 'Número de telemóvel',
    mobile_phone_number_placeholder_input: 'Adicione um número de telemóvel',
    mobile_and_landline_phone_number_input: 'Número de telemóvel/telefone',
    tax_payer_number_input: 'Cadastro Nacional da Pessoa Jurídica - CNPJ',
    tax_payer_number_input_placeholder: 'Adicione o número de CNPJ',
    patient_number_input: 'Número de usuario - CNS',
    patient_number_input_placeholder: 'Adicione o número de usuario',
    social_security_number_input: 'Número da Providencia Social - INSS',
    social_security_number_input_placeholder: 'Adicione o número da providencia social',
    room_input: 'Quarto',
    room_placeholder_input: 'O número do quarto onde o utente se encontra alojado',
    social_responses_input: 'Serviços socioassistenciais',
    social_responses_placeholder_input:
      'Selecione as serviços socioassistenciais da sua intituição',
    profile_picture_input: 'Foto de perfil',
    process_number_input: 'Número de processo',
    process_number_input_placeholder: 'Adicione o número de processo',
    recurring_fee_input: 'Mensalidade',
    recurring_fee_input_placeholder: '500',
    marital_status_input: 'Estado civil',
    marital_status_input_placeholder: 'Selecione o estado civil',
    aditional_info_input: 'Outras informações',
    additional_info_input_placeholder: 'Escreva...',
    education_level_input: 'Grau de escolaridade',
    education_level_input_placeholder: 'Selecione o grau de escolaridade',
    citizen_card_input: 'Número do registro geral - RG',
    citizen_card_input_placeholder: 'Adicione o número do registro geral',
    cancel_button: 'Cancelar',
    save_button: 'Guardar',
    saving_button: 'Guardando...',
    deleting_button: 'Apagando...',
    create_senior_dialog_title: 'Guardar criação do utente "{{name}}"?',
    create_senior_dialog_message: 'Ao guardar, o novo utente será adicionado à lista de utentes.',
    edit_senior_dialog_title: 'Guardar alterações no perfil do utente "{{name}}"?',
    edit_senior_dialog_message:
      'Ao guardar, o utente será atualizado com as informações recentemente inseridas.',
    edit_caregiver_dialog_title: 'Guardar alterações no perfil do cuidador "{{name}}"?',
    edit_caregiver_dialog_message:
      'Ao guardar, o cuidador será atualizado com as informações recentemente inseridas.',
    edit_caregiver_affiliation_type_dialog_title:
      'Guardar alterações no tipo de afiliação do cuidador {{name}}?',
    edit_caregiver_affiliation_type_dialog_message:
      'Ao guardar, o tipo de afiliação do cuidador será atualizado com as informações recentemente inseridas.',
    edit_current_user_profile_dialog_title: 'Guardar alterações no seu perfil?',
    edit_current_user_profile_dialog_message:
      'Ao guardar, o seu perfil será atualizado com as informações recentemente inseridas.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Guardar',
    senior_router_prompt_edit_title:
      'Tem a certeza que deseja sair sem guardar as alterações no perfil do utente "{{name}}"?',
    senior_router_prompt_create_title:
      'Tem a certeza que deseja sair sem guardar criação do utente "{{name}}"?',
    senior_router_prompt_create_message:
      'Ao sair, o utente não será guardado e todas as informações nele inseridas serão apagadas.',
    senior_router_prompt_edit_message:
      'Ao sair, perderá todas as alterações recentemente inseridas no perfil do utente.',
    caregiver_edit_router_prompt_edit_title:
      'Tem a certeza que deseja sair sem guardar as alterações no perfil do cuidador "{{name}}"?',
    caregiver_edit_router_prompt_edit_message:
      'Ao sair, perderá todas as alterações recentemente inseridas no perfil do cuidador.',
    current_user_router_prompt_edit_title:
      'Tem a certeza que deseja sair sem guardar as alterações no seu perfil?',
    current_user_router_prompt_edit_message:
      'Ao sair, perderá todas as alterações recentemente inseridas no seu perfil.',
    caregiver_edit_affiliation_type_router_prompt_edit_title:
      'Tem a certeza que deseja sair sem guardar as alterações no tipo de afiliação do cuidador {{name}}?',
    caregiver_edit_affiliation_type_router_prompt_edit_message:
      'Ao sair, perderá todas as alterações recentemente inseridas no tipo de afiliação do cuidador.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Sim, sair',
    senior_create_cancel_toast: 'A criação do novo utente foi cancelada.',
    senior_edit_cancel_toast: 'A edição do utente "{{name}}" foi cancelada.',
    caregiver_edit_cancel_toast: 'A edição do cuidador "{{name}}" foi cancelada.',
    current_user_edit_cancel_toast: 'A edição do seu perfil foi cancelada.',
    caregiver_edit_affiliation_type_cancel_toast:
      'A edição do tipo de afiliação do cuidador {{name}} foi cancelada.',
    current_user_edit_email_not_allowed_tooltip_title:
      'Não é possível alterar o seu endereço de e-mail atualmente.',
    caregiver_edit_email_not_allowed_tooltip_title:
      'Não é possível alterar o endereço de e-mail do cuidador atualmente.',
    current_user_edit_state_not_allowed_tooltip_title:
      'Não é possível alterar o seu estado atualmente.',
    profile_picture_info_message: 'Para melhor resolução, a imagem deverá ser do tipo JPG/JPEG.',
    display_name_other_user_info_message:
      'Neste campo indique o nome pelo qual o usuário é chamado no dia-a-dia. Este é o nome que os familiares, amigos e restantes cuidadores irão ver no sistema quando interagirem com o usuário.',
    display_name_current_user_info_message:
      'Neste campo indique o nome pelo qual é chamado no dia-a-dia. Este é o nome que os restantes usuários irão ver no sistema quando interagirem consigo.',
    roles_ids_input: 'Permissões',
    roles_ids_placeholder_input: 'Selecionar permissões',
    input_placeholder: 'Escreva um {{ type }}',
    not_specified: 'Não especificado',
    input_read_only_tooltip: 'Não possui permissões para editar as informações do perfil',
    quick_access_code_input: 'Código de Acesso Rápido',
    quick_access_code_redefine_button: 'Redefinir',
    quick_access_code_disabled:
      'Já existe um código de acesso rápido. Para alterar, cliquei em "Redefenir".',
    success: {
      save_success_button: 'Guardado!',
      delete_success_button: 'Apagado!',
      create_senior_success_message: 'O utente "{{name}}" foi criado com sucesso!',
      edit_senior_success_message: 'As alterações no perfil do utente foram guardadas.',
      edit_caregiver_success_message: 'As alterações no perfil do cuidador foram guardadas.',
      edit_caregiver_roles_ids_success_message:
        'As alterações nos cargo do cuidador foram guardadas.',
      edit_current_user_success_message: 'As alterações no seu perfil foram guardadas.',
      edit_current_user_app_language_to_chosen_language_success_message:
        'O idioma da aplicativo foi atualizado para {{language}}.',
      edit_current_user_app_language_to_browser_language_success_message:
        'O idioma da aplicativo foi atualizado para o idioma do seu navegador, {{language}}.',
      edit_current_user_app_language_to_default_language_success_message:
        'O idioma da aplicativo foi atualizado para o idioma utilizado por defeito, {{language}}.',
      success_delete_senior: 'Utente {{name}} apagado.',
      success_delete_caregiver: 'Cuidador {{name}} apagado.',
      success_delete_invite: 'Convite para {{name}} cancelado.',
    },
    errors: {
      error_on_save_button: 'Erro a guardar',
      error_on_delete_senior_button: 'Erro ao apagar o utente',
      error_on_delete_caregiver_button: 'Erro ao apagar o cuidador',
      error_on_delete_invite: 'Erro ao cancelar o convite',
      edit_form_validation_invalid_field: 'Preencha este campo para completar a edição do perfil',
      caregiver_edit_affiliation_type_form_validation_invalid_field:
        'Preencha este campo para completar a edição do tipo de afiliação do cuidador',
      form_validation_invalid_phone_number: 'O número de telemóvel indicado não é válido',
      form_validation_not_mobile_phone_number:
        'O número indicado não corresponde a um número de telemóvel',
      form_validation_invalid_email: 'O endereço de e-mail indicado não é válido',
      form_validation_invalid_number:
        'De acordo com a região {{region}} o número deve ter {{digits}} caracteres',
      form_validation_invalid_room_name: 'O nome do quarto indicado não é válido',
      form_validation_room_maximum_characters:
        'O nome do quarto indicado não pode ter mais do que {{max}} carateres',
      form_validation_invalid_display_name_maximum_characters:
        'O nome deve ter menos do que {{max}} carateres',
      create_active_senior_licenses_number_exceeded_error:
        'Não foi possível criar um utente ativo. A entidade já atingiu o número máximo de utentes ativos para o número de licenças ativas.',
      activate_senior_licenses_number_exceeded_error:
        'Não foi possível ativar o utente. A entidade já atingiu o número máximo de utentes ativos para o número de licenças ativas.',
      form_validation_invalid_quick_access_code:
        'O código de acesso rápido deve ter {{digits}} digitos',
      form_validation_process_number_max_chars:
        'O número de processo deve ter menos do que {{max}} carateres',
      form_validation_recurring_fee_max_chars:
        'A mensalidade deve ter menos do que {{max}} carateres',
      form_validation_aditional_info_max_chars:
        'As outras informações devem ter menos do que {{max}} carateres',
    },
  },
  user_attachments: {
    list: {
      headers: {
        name: 'Nome',
        description: 'Descrição',
        author: 'Adicionado por',
        date: 'Data',
        size: 'Tamanho',
      },
      actions: {
        edit: 'Editar',
        delete: 'Apagar',
        download: 'Transferir',
      },
    },
    empty: {
      title: 'Ainda não há anexos registados.',
      subtitle: 'Prima adicionar para adicionar um anexo.',
    },
    modals: {
      create: {
        title: 'Adicionar anexo',
      },
      edit: {
        title: 'Editar anexo',
        name: 'Nome',
        description: 'Descrição',
        errors: {
          name_required: 'O nome do anexo é obrigatório',
          name_max_length: 'O nome do anexo deve ter menos do que {{max}} carateres',
          description_max_length: 'A descrição do anexo deve ter menos do que {{max}} carateres',
        },
      },
      delete: {
        title: 'Tem a certeza que deseja apagar o anexo<br/>"{{name}}"?',
        description: 'O anexo será apagado permanentemente.',
      },
    },
    success: {
      create: 'Os anexos foram adicionados com sucesso',
      edit: 'Anexo editado com sucesso',
      delete: 'Anexo apagado com sucesso',
    },
    error: {
      create: 'Erro ao adicionar anexos',
      edit: 'Erro ao editar o anexo',
      delete: 'Erro ao apagar o anexo',
      download: 'Erro ao transferir o anexo',
    },
    access_denied: {
      create: 'Não tem permissões para adicionar anexos',
      edit: 'Não tem permissões para editar anexos',
      delete: 'Não tem permissões para apagar anexos',
    },
  },
  entity_services: {
    root: {
      title: 'Serviços',
      add_button: 'Criar serviço',
      search_input_placeholder: 'Pesquisar serviços',
      name_header: 'Nome',
      display_order_header: 'Ordem',
      state_header: 'Estado',
      state_value_true: 'Ativo',
      state_value_false: 'Inativo',
      no_services: 'Não existem serviços configurados.',
      deleted_service_dialog_title: 'Serviço "{{name}}" apagado',
      deleted_service_dialog_message1: '"{{name}}" foi apagado.',
      deleted_service_dialog_message2:
        'O serviço já não está disponível no aplicativo {{caregiversAppName}} e no {{seniorsDeviceName}}.',
      deleted_service_dialog_ok_button: 'Ok',
    },
    edit: {
      title_create: 'Criar serviço',
      title_edit: 'Editar serviço',
      title_view: 'Ver serviço',
      exit_button: 'Sair',
      save_button: 'Guardar',
      saving_button: 'Guardando...',
      title_input: 'Nome',
      state_input: 'Estado',
      phone_number_input: 'Número de contacto',
      phone_number_use_general_number: 'Utilizar número geral',
      phone_number_use_specific_number: 'Utilizar número específico',
      display_order_input: 'Ordem',
      description_input: 'Descrição',
      details_input: 'Detalhes',
      attach_document_input: 'Adicionar anexo PDF',
      document_informative_note_input: 'Mensagem sobre informação contratual',
      icon_input: 'Ícone',
      image_input: 'Imagem',
      button_preview: 'Visualização do botão',
      state_value_true: 'Ativo',
      state_value_false: 'Inativo',
      number_input: 'Número',
      upload: 'Anexar',
      remove_document: 'Remover',
      view_document: 'Ver',
      image_info:
        'A imagem deverá ser do tipo JPG/JPEG. Para melhores resultados deverá utilizar a resolução de 292x288px.',
      delete_button: 'Apagar serviço',
      deleting_button: 'Apagando...',
      delete_dialog_title: 'Apagar serviço "{{name}}"?',
      delete_dialog_content:
        'Ao apagar este serviço, todos os usuários da aplicativo {{caregiversAppName}} e do {{seniorsDeviceName}} perderão o acesso ao mesmo. Além disso, o serviço também será apagado permanentemente da lista de serviços da entidade.',
      delete_dialog_action_yes_button: 'Sim, apagar',
      delete_dialog_action_no_button: 'Cancelar',
      router_prompt_edit_title:
        'Tem a certeza que deseja sair sem guardar as alterações nos detalhes do serviço "{{name}}"?',
      router_prompt_create_title:
        'Tem a certeza que deseja sair sem guardar criação do serviço "{{name}}"?',
      router_prompt_create_message:
        'Ao sair, o serviço não será guardado e todas as informações nele inseridas serão apagadas.',
      router_prompt_edit_message:
        'Ao sair, perderá todas as alterações recentemente inseridas nos detalhes do serviço.',
      router_prompt_action_cancel_button: 'Cancelar',
      router_prompt_action_ok_button: 'Sim, sair',
      create_service_dialog_title: 'Guardar criação do serviço "{{name}}"?',
      create_service_dialog_message:
        'Ao guardar, o novo serviço será adicionado à lista de serviços.',
      edit_service_dialog_title: 'Guardar alterações nos detalhes do serviço "{{name}}"?',
      edit_service_dialog_message:
        'Ao guardar, o serviço será atualizado com as informações recentemente inseridas.',
      save_service_dialog_action_cancel_button: 'Cancelar',
      save_service_dialog_action_save_button: 'Guardar',
      service_create_cancel_toast: 'A criação do novo serviço foi cancelada.',
      service_edit_cancel_toast: 'A edição do serviço "{{name}}" foi cancelada.',
      errors: {
        error_on_save_button: 'Erro a guardar',
        error_on_delete_button: 'Erro ao apagar',
        edit_form_validation_invalid_field:
          'Preencha este campo para completar a edição do serviço',
        create_form_validation_invalid_field:
          'Preencha este campo para completar a criação do serviço',
        create_form_validation_invalid_icon: 'Escolha um ícone para completar a criação do serviço',
        create_form_validation_invalid_image:
          'Carregue uma imagem para completar a criação do serviço',
        form_validation_invalid_phone_number: 'O número indicado não é válido',
        service_does_not_exist_error: 'Não foi possível obter o serviço que tentou aceder.',
      },
      success: {
        save_success_button: 'Guardado!',
        delete_success_button: 'Apagado!',
        create_success_message: 'O serviço "{{name}}" foi criado com sucesso!',
        edit_success_message: 'As alterações nos detalhes do serviço foram guardadas.',
      },
    },
  },
  applications: {
    system_administrator: {
      root: {
        title: 'Aplicativos',
        search_input_placeholder: 'Pesquisar aplicativos',
        name_header: 'Nome',
        category_header: 'Categoria',
        state_header: 'Estado',
        state_value_true: 'Ativo',
        state_value_false: 'Inativo',
        no_applications: 'Não existem aplicativos.',
        add_button: 'Adicionar aplicativo',
      },
      edit: {
        entities: {
          search_input_placeholder: 'Pesquisar entidades',
          name_header: 'Nome',
          type_header: 'Tipo',
          state_header: 'Estado',
          state_value_true: 'Ativo',
          state_value_false: 'Inativo',
          no_entities: 'Não existem entidades.',
          enable_application_dialog_message:
            'Ao ativar a aplicativo, ela estará disponível para a entidade {{name}}.',
          enable_application_dialog_action_yes_button: 'Sim, ativar',
          enable_application_dialog_action_no_button: 'Cancelar',
          disable_application_dialog_message:
            'Ao desativar a aplicativo, ela deixará de estar disponível para a entidade {{name}}.',
          disable_application_dialog_action_yes_button: 'Sim, desativar',
          disable_application_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_application_error_message:
              'Um erro ocorreu. Não foi possível ativar o aplicativo.',
            disable_application_error_message:
              'Um erro ocorreu. Não foi possível desativar o aplicativo.',
            update_state_application_error_message:
              'Um erro ocorreu. Não foi possível atualizar o estado do aplicativo.',
          },
        },
      },
    },
    professional_caregiver: {
      root: {
        search_input_placeholder: 'Pesquisar aplicativos',
        name_header: 'Nome',
        category_header: 'Categoria',
        state_header: 'Estado',
        state_value_true: 'Ativo',
        state_value_false: 'Inativo',
        no_applications: 'Não existem aplicativos.',
        enable_application_dialog_message:
          'Ao ativar o aplicativo, ela estará disponível para todos os usuários da entidade {{name}}.',
        enable_application_dialog_action_yes_button: 'Sim, ativar',
        enable_application_dialog_action_no_button: 'Cancelar',
        disable_application_dialog_message:
          'Ao desativar o aplicativo, ela deixará de estar disponível para todos os usuários da entidade {{name}}.',
        disable_application_dialog_action_yes_button: 'Sim, desativar',
        disable_application_dialog_action_no_button: 'Cancelar',
        errors: {
          enable_application_error_message:
            'Um erro ocorreu. Não foi possível ativar o aplicativo.',
          disable_application_error_message:
            'Um erro ocorreu. Não foi possível desativar o aplicativo.',
          update_state_application_error_message:
            'Um erro ocorreu. Não foi possível atualizar o estado do aplicativo.',
        },
      },
      edit: {
        users: {
          search_input_placeholder: 'Pesquisar usuários',
          name_header: 'Nome',
          type_header: 'Tipo',
          state_header: 'Estado',
          state_value_true: 'Ativo',
          state_value_false: 'Inativo',
          no_users: 'Não existem usuários.',
          enable_application_dialog_message:
            'Ao ativar o aplicativo, ela estará disponível para {{name}}.',
          enable_application_dialog_action_yes_button: 'Sim, ativar',
          enable_application_dialog_action_no_button: 'Cancelar',
          disable_application_dialog_message:
            'Ao desativar o aplicativo, ela deixará de estar disponível para {{name}}.',
          disable_application_dialog_action_yes_button: 'Sim, desativar',
          disable_application_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_application_error_message:
              'Um erro ocorreu. Não foi possível ativar o aplicativo.',
            disable_application_error_message:
              'Um erro ocorreu. Não foi possível desativar o aplicativo.',
            update_state_application_error_message:
              'Um erro ocorreu. Não foi possível atualizar o estado do aplicativo.',
          },
        },
      },
    },
  },
  application_tabs: {
    tabs: {
      details: 'Detalhes',
      entities: 'Entidades',
      users: 'Usuários',
    },
    errors: {
      application_does_not_exist_error: 'Não foi possível obter o aplicativo que tentou aceder.',
    },
  },
  application_form: {
    name_input: 'Nome',
    state_input: 'Estado',
    state_value_true: 'Ativo',
    state_value_false: 'Inativo',
    description_input: 'Descrição',
    is_system_application_input: 'Aplicativo do sistema',
    is_system_application_value_yes: 'Sim',
    is_system_application_value_no: 'Não',
    available_for_user_types_input: 'Tipos de usuários para os quais o aplicativo está disponível',
    icon_input: 'Ícone',
    picture_input: 'Imagem',
    category_input: 'Categoria',
    activity_type_input: 'Tipo de atividade',
    windows_key_input: 'Chave Windows',
    android_key_input: 'Chave Android',
    input_disabled_tooltip: 'Não possui permissões para editar o aplicativo',
  },
  devices: {
    root: {
      search_input_placeholder: 'Pesquisar dispositivos',
      identifier_header: 'Identificador',
      type_header: 'Tipo',
      state_header: 'Estado',
      state_value_true: 'Ativo',
      state_value_false: 'Inativo',
      user_header: 'Usuário',
      no_devices: 'Não existem dispositivos.',
      add_button: 'Adicionar dispositivo',
    },
  },
  device_tabs: {
    device_title: 'Dispositivo "{{name}}"',
    tabs: {
      details: 'Detalhes',
      health: 'Saúde',
      information: 'Informação',
    },
    errors: {
      device_does_not_exist_error: 'Não foi possível obter o dispositivo que tentou aceder.',
    },
  },
  user_cards: {
    root: {
      title: 'Cartões',
      search_input_placeholder: 'Pesquisar cartões',
      name_header: 'Nome',
      state_header: 'Estado',
      state_value_true: 'Ativo',
      state_value_false: 'Inativo',
      user_header: 'Usuário',
      no_cards: 'Não existem cartões.',
      add_button: 'Adicionar cartão',
      deleted_card_dialog_title: 'Cartão "{{name}}" apagado',
      deleted_card_dialog_message: 'O cartão já não existe no sistema.',
      deleted_card_dialog_ok_button: 'Ok',
    },
    edit: {
      errors: {card_does_not_exist_error: 'Não foi possível obter o cartão que tentou aceder.'},
    },
  },
  gdpr_erasure_requests: {
    root: {
      title: 'Direito ao esquecimento (LGPD)',
      add_button: 'Solicitar exclusão',
      no_requests: 'Não existem solicitações de exclusão.',
      status_header: 'Status',
      requested_at_header: 'Solicitado em',
      completed_at_header: 'Concluído em',
      error_header: 'Erro',
      user_id_hash_header: 'Hash do usuário',
      user_email_hash_header: 'Hash do e-mail',
      status_processing: 'Em processamento',
      status_completed: 'Concluído',
      status_failed: 'Falhou',
    },
    create_modal: {
      title: 'Solicitar exclusão de dados pessoais',
      email_label: 'E-mail do usuário',
      email_placeholder: 'usuario@exemplo.com',
      email_required: 'O e-mail é obrigatório.',
      email_invalid: 'Informe um e-mail válido.',
      cancel_button: 'Cancelar',
      submit_button: 'Continuar',
      submitting_button: 'Enviando...',
      confirm_title: 'Confirmar exclusão de dados',
      confirm_description:
        'Esta ação irá excluir/anonimizar permanentemente os dados pessoais do usuário associado a este e-mail.',
      confirm_cannot_be_undone: 'Esta ação é irreversível e não pode ser desfeita.',
      confirm_button: 'Excluir definitivamente',
      cancel_confirm_button: 'Cancelar',
    },
    errors: {
      forbidden: 'Você não tem permissão para realizar esta ação.',
      user_not_found: 'Não existe nenhum usuário com esse e-mail.',
      unknown: 'Ocorreu um erro ao processar a solicitação. Tente novamente.',
    },
    success: {
      request_created: 'Solicitação de exclusão criada com sucesso.',
    },
  },
  entity_photos: {
    root: {
      add_button: 'Adicionar',
      deleted_photo_dialog_title: 'Fotografia apagada',
      deleted_photo_dialog_message:
        'A fotografia já não está disponível no ecrã de bloqueio do computador {{brand}}.',
      deleted_photo_dialog_ok_button: 'Ok',
      entity_photos_only_for_computer_info_message:
        'As fotografias adicionadas poderão ser visualizadas nos computadores e Tablets Sioslife.',
    },
    edit: {
      title_create: 'Adicionar fotografias',
      title_edit: 'Editar fotografia',
      photo_input: 'Fotografia',
      photos_input: 'Fotografias',
      description_input: 'Descrição',
      exit_button: 'Sair',
      save_button: 'Guardar',
      saving_button: 'Guardando...',
      photos_create_cancel_toast: 'A criação das novas fotografias foi cancelada.',
      photo_edit_cancel_toast: 'A edição da fotografia foi cancelada.',
      delete_button: 'Apagar fotografia',
      deleting_button: 'Apagando...',
      delete_dialog_title: 'Tem a certeza que deseja apagar fotografia?',
      delete_dialog_content:
        'Ao apagar a fotografia, esta deixará de aparecer no ecrã de bloqueio do computador {{brand}} e será apagada permanentemente.',
      delete_dialog_action_yes_button: 'Sim, apagar',
      delete_dialog_action_no_button: 'Cancelar',
      router_prompt_edit_title:
        'Tem a certeza que deseja sair sem guardar as alterações na fotografia?',
      router_prompt_edit_message:
        'Ao sair, perderá todas as alterações recentemente inseridas na fotografia.',
      router_prompt_create_title:
        'Tem a certeza que deseja sair sem guardar criação das fotografias?',
      router_prompt_create_message:
        'Ao sair, as fotografias não serão guardadas e todas as informações nelas inseridas serão apagadas.',
      router_prompt_action_cancel_button: 'Cancelar',
      router_prompt_action_ok_button: 'Sim, sair',
      photos_info: 'As fotografias deverão ser do tipo JPG/JPEG.',
      choose_photos_button: 'Escolher fotografias',
      input_disabled_tooltip: 'Não possui permissões para editar os detalhes da fotografia',
      errors: {
        error_on_save_button: 'Erro a guardar',
        error_on_delete_button: 'Erro ao apagar',
        photo_does_not_exist_error: 'Não foi possível obter a fotografia que tentou aceder.',
        create_form_validation_invalid_field:
          'Preencha este campo para completar a criação das fotografias',
        form_validation_invalid_description_maximum_characters:
          'A descrição deve ter menos do que {{max}} carateres',
      },
      success: {
        save_success_button: 'Guardado!',
        delete_success_button: 'Apagado!',
        create_success_message: 'As fotografias foram criadas com sucesso!',
        edit_success_message: 'As alterações na fotografia foram guardadas.',
      },
      warnings: {
        partial_create_success_message:
          'Algumas fotografias foram criadas com sucesso! No entanto, não foi possível criar {{total}} fotografia(s): {{files}}.',
      },
    },
  },
  entities: {
    root: {
      title: 'Entidades',
      search_input_placeholder: 'Pesquisar entidades',
      name_header: 'Nome',
      type_header: 'Tipo',
      state_header: 'Estado',
      state_value_true: 'Ativo',
      state_value_false: 'Inativo',
      no_entities: 'Não existem entidades.',
      add_button: 'Adicionar entidade',
      deleted_entity_dialog_title: 'Entidade "{{name}}" apagada',
      deleted_entity_dialog_message: 'A entidade já não existe no sistema.',
      deleted_entity_dialog_ok_button: 'Ok',
    },
    edit: {
      tabs: {
        details: 'Detalhes',
        seniors: 'Utentes',
        professional_caregivers: 'Profissionais',
        applications: 'Aplicativos',
        devices: 'Dispositivos',
        user_cards: 'Cartões',
        activity: 'Atividade',
        statistics: 'Estatísticas',
        licenses: 'Licenças',
        playlists: 'Listas de reprodução',
      },
      seniors: {
        search_input_placeholder: 'Pesquisar utentes',
        name_header: 'Nome',
        gender_header: 'Género',
        state_header: 'Estado',
        state_value_true: 'Ativo',
        state_value_false: 'Inativo',
        no_seniors: 'Não existem utentes.',
        enable_senior_dialog_title: 'Ativar o utente {{name}}?',
        enable_senior_dialog_message:
          'Ao ativar o utente, ele poderá usufruir do sistema {{brand}}.',
        enable_senior_dialog_action_yes_button: 'Sim, ativar',
        enable_senior_dialog_action_no_button: 'Cancelar',
        disable_senior_dialog_title: 'Desativar o utente {{name}}?',
        disable_senior_dialog_message:
          'Ao desativar o utente, ele deixará de poder usufruir do sistema {{brand}}.',
        disable_senior_dialog_action_yes_button: 'Sim, desativar',
        disable_senior_dialog_action_no_button: 'Cancelar',
        errors: {
          enable_senior_error_message: 'Um erro ocorreu. Não foi possível ativar o utente.',
          disable_senior_error_message: 'Um erro ocorreu. Não foi possível desativar o utente.',
          update_state_senior_error_message:
            'Um erro ocorreu. Não foi possível atualizar o estado do utente.',
        },
      },
      professional_caregivers: {
        search_input_placeholder: 'Pesquisar profissionais',
        invite_button: 'Convidar profissional',
        name_header: 'Nome',
        email_header: 'E-mail',
        affiliation_type_header: 'Tipo',
        state_header: 'Estado',
        state_value_true: 'Ativo',
        state_value_false: 'Inativo',
        no_professional_caregivers: 'Não existem professionals.',
      },
      applications: {
        search_input_placeholder: 'Pesquisar aplicativos',
        name_header: 'Nome',
        category_header: 'Categoria',
        state_header: 'Estado',
        state_value_true: 'Ativo',
        state_value_false: 'Inativo',
        no_applications: 'Não existem aplicativos.',
        enable_application_dialog_message:
          'Ao ativar o aplicativo, ela estará disponível para a entidade {{name}}.',
        enable_application_dialog_action_yes_button: 'Sim, ativar',
        enable_application_dialog_action_no_button: 'Cancelar',
        disable_application_dialog_message:
          'Ao desativar o aplicativo, ela deixará de estar disponível para a entidade {{name}}.',
        disable_application_dialog_action_yes_button: 'Sim, desativar',
        disable_application_dialog_action_no_button: 'Cancelar',
        errors: {
          enable_application_error_message:
            'Um erro ocorreu. Não foi possível ativar o aplicativo.',
          disable_application_error_message:
            'Um erro ocorreu. Não foi possível desativar o aplicativo.',
          update_state_application_error_message:
            'Um erro ocorreu. Não foi possível atualizar o estado do aplicativo.',
        },
      },
      devices: {
        search_input_placeholder: 'Pesquisar dispositivos',
        identifier_header: 'Identificador',
        type_header: 'Tipo',
        state_header: 'Estado',
        state_value_true: 'Ativo',
        state_value_false: 'Inativo',
        user_header: 'Usuário',
        no_devices: 'Não existem dispositivos.',
      },
      user_cards: {
        search_input_placeholder: 'Pesquisar cartões',
        name_header: 'Nome',
        state_header: 'Estado',
        state_value_true: 'Ativo',
        state_value_false: 'Inativo',
        user_header: 'Usuário',
        no_cards: 'Não existem cartões.',
        front_card_design_button: 'Design Frontal do Cartão',
        back_card_design_button: 'Design Traseiro do Cartão',
      },
      playlists: {
        search_input_placeholder: 'Pesquisar listas',
        type_filter: 'Tipo',
        name_header: 'Nome',
        state_header: 'Estado',
        state_value_true: 'Ativo',
        state_value_false: 'Inativo',
        no_playlist: 'Não existem listas de reprodução.',
        enable_playlist_dialog_title: 'Ativar a lista de reprodução "{{name}}"?',
        enable_playlist_dialog_message:
          'Ao ativar a lista de reprodução, ela estará disponível para a entidade {{name}}.',
        enable_playlist_dialog_action_yes_button: 'Sim, ativar',
        enable_playlist_dialog_action_no_button: 'Cancelar',
        disable_playlist_dialog_title: 'Desativar a lista de reprodução "{{name}}"?',
        disable_playlist_dialog_message:
          'Ao desativar a lista de reprodução, ela deixará de estar disponível para a entidade {{name}}.',
        disable_playlist_dialog_action_yes_button: 'Sim, desativar',
        disable_playlist_dialog_action_no_button: 'Cancelar',
        errors: {
          enable_playlist_error_message:
            'Um erro ocorreu. Não foi possível ativar a lista de reprodução.',
          disable_playlist_error_message:
            'Um erro ocorreu. Não foi possível desativar a lista de reprodução.',
          update_state_playlist_error_message:
            'Um erro ocorreu. Não foi possível atualizar o estado da lista de reprodução.',
        },
      },
      playlist_items: {
        title: 'Lista de reprodução "{{name}}"',
        search_input_placeholder: 'Pesquisar itens',
        title_header: 'Título',
        state_header: 'Estado',
        state_value_true: 'Ativo',
        state_value_false: 'Inativo',
        no_items: 'Não existem itens na lista de reprodução.',
        enable_item_dialog_title: 'Ativar o item "{{name}}" na lista de reprodução?',
        enable_item_dialog_message:
          'Ao ativar o item na lista de reprodução, ele estará disponível para a entidade {{name}}.',
        enable_item_dialog_action_yes_button: 'Sim, ativar',
        enable_item_dialog_action_no_button: 'Cancelar',
        disable_item_dialog_title: 'Desativar o item "{{name}}" na lista de reprodução?',
        disable_item_dialog_message:
          'Ao desativar o item na lista de reprodução, ele deixará de estar disponível para a entidade {{name}}.',
        disable_item_dialog_action_yes_button: 'Sim, desativar',
        disable_item_dialog_action_no_button: 'Cancelar',
        errors: {
          enable_item_error_message:
            'Um erro ocorreu. Não foi possível ativar o item na lista de reprodução.',
          disable_item_error_message:
            'Um erro ocorreu. Não foi possível desativar o item na lista de reprodução.',
          update_state_item_error_message:
            'Um erro ocorreu. Não foi possível atualizar o estado do item na lista de reprodução.',
          playlist_does_not_exist_error:
            'Não foi possível obter a lista de reprodução que tentou aceder.',
        },
      },
      licenses: {
        number_of_monthly_active_licenses_not_defined: 'Não definido',
        number_of_monthly_active_licenses_title: 'Licenças ativas mensais',
        save_button: 'Guardar',
        saving_button: 'Guardando...',
        edit_dialog_title: 'Guardar alterações no número de licenças ativas mensais?',
        edit_dialog_content:
          'Ao guardar, o número de licenças ativas mensais será atualizado para {{total}} para a entidade {{entity}}.',
        edit_dialog_action_cancel_button: 'Cancelar',
        edit_dialog_action_save_button: 'Guardar',
        router_prompt_title:
          'Tem a certeza que deseja sair sem guardar as alterações no número de licenças ativas mensais?',
        router_prompt_message:
          'Ao sair, perderá todas as alterações recentemente inseridas no número de licenças ativas mensais para a entidade {{entity}}.',
        router_prompt_action_cancel_button: 'Cancelar',
        router_prompt_action_ok_button: 'Sim, sair',
        number_of_monthly_active_licenses_edit_cancel_toast:
          'A edição do número de licenças mensais foi cancelada.',
        success: {save_success_button: 'Guardado!'},
        errors: {
          error_on_save_button: 'Erro a guardar',
          edit_form_validation_invalid_field:
            'Preencha este campo para completar a edição do número de licenças ativas mensais',
          form_validation_invalid_number_of_monthly_active_licenses:
            'O número de licenças ativas mensais está inválido',
        },
      },
      errors: {entity_does_not_exist_error: 'Não foi possível obter a entidade que tentou aceder.'},
    },
  },
  affiliated_entity_profile: {
    errors: {entity_does_not_exist_error: 'Não foi possível obter a entidade que tentou aceder.'},
  },
  section_tabs: {exit_button: 'Sair'},
  profession_input: {no_options: 'Sem opções', loading: 'Carregando...'},
  social_network_city_input: {no_options: 'Sem opções', loading: 'Carregando...'},
  user_menu: {
    logout: 'Terminar sessão',
    profile: 'Perfil',
    settings: 'Configurações',
    office: 'Área de gestão',
    dailyCareLog: 'Registo de cuidados',
    errors: {logout_error_message: 'Um erro ocorreu. Não foi possível terminar a sessão.'},
  },
  error_box: {
    title: 'Não foi possível executar a ação.',
    description:
      'Lamentamos, mas ocorreu um erro ao tentar executar a ação. Por favor, contacte com o Apoio ao Cliente indicando os detalhes fornecidos.',
    details_button: 'Ver detalhes',
  },
  country_select: {
    title: 'Indicativo do país',
    no_options: 'Sem opções',
  },
  tablet_button: {
    empty_title: '(Título do botão)',
  },
  icons_picker: {
    title: 'Selecionador de ícones',
    cancel_button: 'Cancelar',
    confirm_button: 'Confirmar',
  },
  image_cropper_dialog: {
    title: 'Preparar imagem',
    zoom: 'Ampliação',
    rotation: 'Rotação',
    save_button: 'Guardar',
    cancel_button: 'Cancelar',
  },
  loading_content: {
    loading: 'Carregando...',
  },
  pdf_viewer: {
    error: 'Não foi possível exibir o PDF.',
  },
  activity_card: {
    today: 'Hoje',
    yesterday: 'Ontem',
    this_week: 'Esta semana',
    this_month: 'Este mês',
    previous_month: 'No mês passado',
    past: 'Passado',
    entity: 'Entidade',
    device: 'Dispositivo',
    duration: 'Duração',
    end_type: 'Tipo de finalização',
    participants: 'Participantes',
    application: 'Aplicativo',
    application_battle_with: 'com',
    level_on_app: 'Nível {{level}} no aplicativo',
    missed_call: 'tentou ligar mas a chamada não foi atendida.',
    rejected_call: 'tentou ligar mas a chamada foi rejeitada.',
    call_started: 'telefonou para',
    drawing_sent: 'Desenho enviado para',
    drawing_saved: 'Desenho partilhado com',
    drawing_shared: 'Desenho partilhado no Álbum de Família de',
    painting_sent: 'Pintura enviada para',
    painting_saved: 'Pintura partilhada com',
    painting_shared: 'Pintura partilhada no Álbum de Família de',
    and: 'e',
    photo_sent: 'Fotografia enviada para',
    photo_saved: 'Fotografia partilhada com',
    photo_shared: 'Fotografia partilhada no Álbum de Família de',
    camera_used: 'Câmera utilizada',
    response: 'Resposta',
    video_message_sent_to: 'Mensagem de vídeo enviada para',
    video_message_sent_by: 'Mensagem de vídeo enviada por',
    video_sent: 'Vídeo enviado para',
    video_saved: 'Vídeo partilhado com',
    video_shared: 'Vídeo partilhado no Álbum de Família de',
    currently_happening: 'A acontecer neste momento',
  },
  notification_card: {
    open_invite: 'Abrir convite',
    open_occurrences: 'Ir para ocorrência',
    relationship_invite: {
      message_sender:
        '{{sender}} enviou-lhe um convite para adicionar {{target}} à sua lista de contactos.',
      message_sender_same_as_target:
        '{{sender}} enviou-lhe um convite para adicioná-lo à sua lista de contactos.',
      message_sender_unknown:
        'Recebeu um convite para adicionar {{target}} à sua lista de contactos.',
    },
    affiliation_invite: {
      message_sender: '{{sender}} enviou-lhe um convite para se afiliar à entidade {{target}}.',
      message_sender_same_as_target: '{{sender}} enviou-lhe um convite para se afiliar.',
      message_sender_unknown: 'Recebeu um convite para se afiliar à entidade {{target}}.',
    },
    user_signed_in: '{{name}} iniciou sessão no {{brand}}.',
    photo_sent: '{{name}} enviou-lhe uma fotografia.',
    photo_shared: '{{name}} partilhou uma fotografia.',
    drawing_sent: '{{name}} enviou-lhe um desenho.',
    drawing_shared: '{{name}} partilhou um desenho.',
    painting_sent: '{{name}} enviou-lhe uma pintura.',
    painting_shared: '{{name}} partilhou uma pintura.',
    video_sent: '{{name}} enviou-lhe um vídeo.',
    video_shared: '{{name}} partilhou um vídeo.',
    without_license: '{{name}} não possui licença para usar os sistemas {{brand}}.',
    missed_call: '{{name}} tentou ligar-lhe mas não obteve resposta.',
    video_message: '{{name}} enviou-lhe uma mensagem de vídeo.',
    sos_request: 'Pedido de ajuda SOS de {{name}}. Por favor, confirme se está tudo bem.',
    reminder_response: {
      message: '{{name}} {{response}} ao lembrete "{{reminder}}".',
      responses: {
        positive: 'respondeu "Sim"',
        negative: 'respondeu "Não"',
        not_responding: 'não respondeu',
      },
    },
    relationship_invite_request_result: {
      message_accepted: '{{name}} aceitou {{target}} como contacto.',
      message_declined: '{{name}} não aceitou {{target}} como contacto.',
    },
    caregiver_administrator_role_request_result: {
      message_accepted: '{{name}} aceitou {{target}} como Administrador.',
      message_declined: '{{name}} não aceitou {{target}} como Administrador.',
    },
    relationship_role_change: {
      message_senior_myself: '{{name}} definiu o seu perfil como {{role}} do seu {{brand}}.',
      message_senior_other: '{{name}} definiu {{target}} como {{role}} do seu {{brand}}.',
      message_caregiver_myself:
        '{{name}} definiu o seu perfil como {{role}} do {{brand}} de {{senior}}.',
      message_caregiver_other:
        '{{name}} definiu {{target}} como {{role}} do {{brand}} de {{senior}}.',
      message_unknown_myself: 'O seu perfil foi definido como {{role}} do {{brand}} de {{senior}}.',
      message_unknown_other: '{{target}} foi definido como {{role}} do {{brand}} de {{senior}}.',
    },
    personal_data_sharing_preferences_change: {
      message: '{{name}} alterou as definições de partilha de dados do seu {{brand}}.',
      current_data_settings_title: 'As definições atuais são:',
      can_access_profile_data: 'Informação de perfil',
      can_access_contacts: 'Contactos',
      can_access_applications: 'Aplicativos',
      can_access_multimedia: 'Multimédia',
      can_access_reminders: 'Lembretes',
      can_access_activity_feed: 'Registo de atividade',
    },
    relationship_end: {
      message_myself:
        '{{name}} removeu o seu perfil da sua lista de contactos. A partir daqui não vão conseguir comunicar através dos sistemas {{brand}}.',
      message_other:
        '{{name}} removeu {{target}} da sua lista de contactos. A partir daqui {{target}} não vai conseguir comunicar com {{name}} através dos sistemas {{brand}}.',
    },
    media_processing_status: {
      video_message: {
        [ProcessStatus.Done]: 'A mensagem de vídeo foi enviada para {{name}}.',
        [ProcessStatus.Failed]:
          'Ocorreu um erro ao enviar a mensagem de vídeo. {{name}} não receberá a mensagem.',
      },
      video_shared: {
        [ProcessStatus.Done]: 'O vídeo foi partilhado com {{name}}.',
        [ProcessStatus.Failed]:
          'Ocorreu um erro ao partilhar o vídeo. {{name}} não receberá o vídeo.',
      },
      video_saved: {
        [ProcessStatus.Done]: 'O vídeo foi guardado com sucesso.',
        [ProcessStatus.Failed]: 'Ocorreu um erro ao guardar o vídeo.',
      },
      photo_shared: {
        [ProcessStatus.Done]: 'A fotografia foi partilhada com {{name}}.',
        [ProcessStatus.Failed]:
          'Ocorreu um erro ao partilhar a fotografia. {{name}} não receberá a fotografia.',
      },
      photo_saved: {
        [ProcessStatus.Done]: 'A fotografia foi guardada com sucesso.',
        [ProcessStatus.Failed]: 'Ocorreu um erro ao guardar a fotografia.',
      },
      unknown: {
        [ProcessStatus.Done]: 'O multimédia foi guardado com sucesso.',
        [ProcessStatus.Failed]: 'Ocorreu um erro ao guardar o multimédia.',
      },
    },
    media_empty: '{{name}} pediu para partilhar fotografias e vídeos.',
    new_occurrence: 'Nova ocorrência "{{name}}" para tomar conhecimento.',
    new_occurrence_patient: 'Nova ocorrência "{{name}}" para o utente "{{patient}}".',
    new_occurrence_assigned: '{{registered}} o indicou como responsável da ocorrência "{{name}}".',
    new_occurrence_assigned_patient:
      '{{registered}} o indicou como responsável da ocorrência "{{name}}" do usuário {{patient}}.',
  },
  section_list: {
    refresh: 'Atualizar',
    no_more_data: 'Não existem mais dados',
    without_data: 'Não existem dados',
    today: 'Hoje',
    old_f: 'Mais antigas',
    old_m: 'Mais antigos',
  },
  activity_timeline: {title: 'Atividade'},
  clipboard_text_field: {
    copy_to_clipboard_button: 'Copiar',
    copy_to_clipboard_tooltip_message: 'Copiar para a área de transferência',
    success: {copy_to_clipboard_success_message: 'Texto copiado para a área de transferência!'},
  },
  activity_end_types: {
    [ActivityEndType.UserAction]: 'Ação do usuário',
    [ActivityEndType.SystemTimeout]: 'Tempo de espera do Sistema',
  },
  device_types: {
    [DeviceType.Handset]: 'Telemóvel',
    [DeviceType.Tablet]: 'Tablet',
    [DeviceType.Desktop]: 'Computador',
    [DeviceType.Laptop]: 'Portátil',
    [DeviceType.Unknown]: 'Desconhecido',
  },
  camera_facing: {
    [CameraFacing.Front]: 'Câmara frontal',
    [CameraFacing.Rear]: 'Câmara traseira',
    [CameraFacing.External]: 'Câmara externa',
    [CameraFacing.Unknown]: 'Câmara desconhecida',
  },
  reminder_response_values: {
    [ReminderResponseValue.Positive]: 'Sim / Está bem',
    [ReminderResponseValue.Negative]: 'Não',
    [ReminderResponseValue.NotResponding]: 'Não respondeu',
  },
  invite_states: {
    [InviteState.Accepted]: 'Aceite',
    [InviteState.Cancelled]: 'Cancelado',
    [InviteState.Open]: 'Aberto',
    [InviteState.Pending]: 'Pendente',
    [InviteState.Refused]: 'Recusado',
    [InviteState.RefusedBySenior]: 'Recusado pelo utente',
    [InviteState.WaitingForSeniorApproval]: 'Aguarda aprovação pelo utente',
    [InviteState.AcceptedBySenior]: 'Aceite pelo utente',
    [InviteState.Expired]: 'Expirado',
  },
  process_statuses: {
    [ProcessStatus.Done]: 'Concluído',
    [ProcessStatus.InProgress]: 'Em progresso',
    [ProcessStatus.ToDo]: 'Por fazer',
    [ProcessStatus.Undefined]: 'Não definido',
    [ProcessStatus.Failed]: 'Falhou',
  },
  entity_types: {
    [EntityType.DayCareCenter]: 'Centro de dia',
    [EntityType.Family]: 'Família',
    [EntityType.Generic]: 'Genérico',
    [EntityType.HomeSupport]: 'Suporte ao domicílio',
    [EntityType.Institution]: 'Instituição',
    [EntityType.InsuranceCompany]: 'Seguradora',
    [EntityType.TechCompany]: 'Empresa tecnológica',
  },
  care_taks_not_done_reasons: {
    [CareTaskNotDoneReason.SeniorRefusal]: 'Recusa do utente',
    [CareTaskNotDoneReason.seniorAbsence]: 'Ausência do utente',
    [CareTaskNotDoneReason.LackOfMaterial]: 'Falta de material',
    [CareTaskNotDoneReason.SeniorWasNotFeelingWell]: 'Utente não se sentia bem',
    [CareTaskNotDoneReason.Other]: 'Outro motivo (obs)',
  },
  time_units: {
    seconds: 's',
    minutes: 'min',
    hours: 'h',
  },
  entity_form: {
    exit_button: 'Cancelar',
    create_entity_title: 'Criar entidade',
    picture_input: 'Foto',
    name_input: 'Nome da instituição',
    name_placeholder_input: 'Insira o nome da instituição',
    state_input: 'Estado',
    state_value_true: 'Ativo',
    state_value_false: 'Inativo',
    description_input: 'Descrição',
    type_input: 'Tipo',
    language_input: 'Idioma',
    language_placeholder_input: 'Selecione um idioma',
    region_input: 'Região',
    region_placeholder_input: 'Selecione uma região',
    time_zone_input: 'Fuso horário',
    time_zone_placeholder_input: 'Selecione um fuso horário',
    email_input: 'Endereço de e-mail',
    email_placeholder_input: 'Adicione o email',
    short_name_input: 'Nome curto',
    short_name_placeholder_input: 'Insira um nome curto',
    anniversary_input: 'Data de fundação/Aniversário',
    anniversary_placeholder_input: 'Selecione uma data',
    capacity_input: 'Capacidade',
    capacity_placeholder_input: 'Capacidade de acolhimento da instituição',
    address_input: 'Morada',
    address_placeholder_input: 'Escreva a morada',
    website_input: 'Website',
    website_placeholder_input: 'Adicione o website',
    number_input: 'Código',
    landline_phone_number_input: 'Número de telefone',
    mobile_phone_number_input: 'Número de telemóvel',
    phone_number_placeholder: 'Insira um número de telefone',
    services_name_input: 'Nome dos serviços',
    services_name_placeholder_input: 'Nome dos serviços',
    services_phone_number_input: 'Número de telefone dos serviços',
    services_image_url_input: 'Imagem dos serviços',
    services_image_info_message: 'Para melhor resolução, a imagem deverá ser do tipo JPG/JPEG.',
    picture_info_message: 'Para melhor resolução, a imagem deverá ser do tipo JPG/JPEG.',
    special_purpose_input: 'Finalidade da entidade',
    special_purpose_placeholder_input: 'Selecione uma finalidade',
    social_responses_input: 'Serviços socioassistenciais',
    social_responses_placeholder_input:
      'Selecione as serviços socioassistenciais da sua intituição',
    save_button: 'Guardar',
    saving_button: 'Guardando...',
    deleting_button: 'Apagando...',
    delete_button: 'Apagar entidade',
    delete_dialog_title: 'Apagar entidade "{{name}}"?',
    delete_dialog_content: 'Ao apagar a entidade, esta será apagada permanentemente do sistema.',
    delete_dialog_action_yes_button: 'Sim, apagar',
    delete_dialog_action_no_button: 'Cancelar',
    router_prompt_create_title:
      'Tem a certeza que deseja sair sem guardar criação da entidade "{{name}}"?',
    router_prompt_create_message:
      'Ao sair, a entidade não será guardada e todas as informações nela inseridas serão apagadas.',
    router_prompt_edit_title:
      'Tem a certeza que deseja sair sem guardar as alterações no perfil da entidade "{{name}}"?',
    router_prompt_edit_message:
      'Ao sair, perderá todas as alterações recentemente inseridas no perfil da entidade.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Sim, sair',
    edit_cancel_toast: 'A edição da entidade "{{name}}" foi cancelada.',
    create_cancel_toast: 'A criação da nova entidade foi cancelada.',
    edit_special_purpose_not_allowed_tooltip_title:
      'Não possui permissões para indicar a finalidade da entidade.',
    edit_is_active_not_allowed_tooltip_title:
      'Não possui permissões para indicar se a entidade está ativa.',
    input_disabled_tooltip: 'Não possui permissões para editar as informações da instituição',
    not_specified: 'Não especificado',
    alert: {
      short_name_input: 'Este é o nome que será visualizado no sistema.',
    },
    success: {
      save_success_button: 'Guardado!',
      delete_success_button: 'Apagado!',
      create_success_message: 'A entidade "{{name}}" foi criada com sucesso!',
      edit_success_message: 'As alterações no perfil da entidade foram guardadas.',
    },
    errors: {
      error_on_save_button: 'Erro a guardar',
      error_on_delete_button: 'Erro ao apagar',
      edit_form_validation_invalid_field: 'Preencha este campo para completar a edição da entidade',
      create_form_validation_invalid_field:
        'Preencha este campo para completar a criação da entidade',
      form_validation_invalid_full_name_maximum_characters:
        'O nome completo deve ter menos do que {{max}} carateres',
      form_validation_invalid_landline_phone_number: 'O número de telefone indicado não é válido',
      form_validation_invalid_mobile_phone_number: 'O número de telemóvel indicado não é válido',
      form_validation_not_mobile_phone_number:
        'O número indicado não corresponde a um número de telemóvel',
      form_validation_invalid_services_phone_number:
        'O número de telefone dos serviços indicado não é válido',
      form_validation_invalid_email: 'O endereço de e-mail indicado não é válido',
      form_validation_invalid_website: 'O site indicado não é válido',
      form_validation_invalid_capacity: 'A capacidade indicada não é válida',
      form_validation_required_social_responses:
        'Por favor indique as serviços socioassistenciais da instituição',
    },
  },
  professional_caregiver_invite_form: {
    exit_button: 'Sair',
    title: 'Convidar profissional',
    entity_input: 'Entity',
    name_input: 'Nome',
    contact_method_input: 'Método de contacto',
    email_input: 'Endereço de e-mail',
    mobile_phone_number_input: 'Número de telemóvel',
    affiliation_type_input: 'Tipo de afiliação',
    save_dialog_title: 'Enviar convite para {{name}}?',
    save_dialog_message:
      'Ao enviar, {{name}} irá receber um convite para ser profissional e ter acesso a algumas informações da entidade {{entity}}.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Enviar',
    router_prompt_title: 'Tem a certeza que deseja sair sem enviar o convite?',
    router_prompt_message: 'Ao sair, perderá todas as informações inseridas no convite.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Sim, sair',
    cancel_toast: 'O convite foi cancelado.',
    save_button: 'Enviar convite',
    saving_button: 'Enviando...',
    success: {
      save_success_button: 'Enviado!',
      success_message: 'O convite foi enviado para {{name}}.',
    },
    errors: {
      error_on_save_button: 'Erro a enviar',
      form_validation_invalid_field: 'Preencha este campo para completar o convite',
      form_validation_invalid_mobile_phone_number: 'O número de telemóvel indicado não é válido',
      form_validation_not_mobile_phone_number:
        'O número indicado não corresponde a um número de telemóvel',
      form_validation_invalid_email: 'O endereço de e-mail indicado não é válido',
    },
  },
  invite_contact_methods: {
    [InviteContactMethod.Email]: 'Endereço de e-mail',
    [InviteContactMethod.MobilePhoneNumber]: 'Número de telemóvel',
  },
  search_select_input: {
    no_options: 'Sem opções',
    loading: 'Carregando...',
    more_button: 'Mais',
    items_showing_information: 'A mostrar {{value}} de {{total}} itens',
  },
  user_card_form: {
    exit_button: 'Sair',
    create_user_card_title: 'Criar cartão',
    edit_user_card_title: 'Editar cartão',
    view_user_card_title: 'Detalhes do cartão',
    name_input: 'Nome',
    state_input: 'Estado',
    state_value_true: 'Ativo',
    state_value_false: 'Inativo',
    tag_input: 'Número de identificação',
    entity_input: 'Entidade',
    user_input: 'Usuário',
    user_info_message:
      'Este campo só pode ser preenchido se uma entidade for previamente selecionada.',
    save_button: 'Guardar',
    saving_button: 'Guardando...',
    deleting_button: 'Apagando...',
    delete_button: 'Apagar cartão',
    delete_dialog_title: 'Tem a certeza que deseja apagar cartão "{{name}}"?',
    delete_dialog_content: 'Ao apagar o cartão, este será apagado permanentemente do sistema.',
    create_dialog_title: 'Guardar criação do cartão "{{name}}"?',
    create_dialog_message: 'Ao guardar, o novo cartão será adicionado ao sistema.',
    edit_dialog_title: 'Guardar alterações no cartão "{{name}}"?',
    edit_dialog_message:
      'Ao guardar, o cartão será atualizado com as informações recentemente inseridas.',
    router_prompt_create_title:
      'Tem a certeza que deseja sair sem guardar criação do cartão "{{name}}"?',
    router_prompt_create_message:
      'Ao sair, o cartão não será guardado e todas as informações nele inseridas serão apagadas.',
    router_prompt_edit_title:
      'Tem a certeza que deseja sair sem guardar as alterações no cartão "{{name}}"?',
    router_prompt_edit_message:
      'Ao sair, perderá todas as alterações recentemente inseridas no cartão.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Sim, sair',
    edit_cancel_toast: 'A edição do cartão "{{name}}" foi cancelada.',
    create_cancel_toast: 'A criação do novo cartão foi cancelada.',
    disabled_tooltip: 'Não possui permissões para editar o cartão',
    success: {
      save_success_button: 'Guardado!',
      delete_success_button: 'Apagado!',
      create_success_message: 'O cartão "{{name}}" foi criado com sucesso!',
      edit_success_message: 'As alterações no cartão foram guardadas.',
      senior_inactive_success: 'O utente {{name}} foi marcado como inativo.',
    },
    errors: {
      error_on_save_button: 'Erro a guardar',
      error_on_delete_button: 'Erro ao apagar',
      edit_form_validation_invalid_field: 'Preencha este campo para completar a edição do cartão',
      create_form_validation_invalid_field:
        'Preencha este campo para completar a criação do cartão',
      error_on_delete_user: 'Erro ao apagar o usuário',
      error_on_marking_senior_as_inactive: 'Erro ao marcar o utente como inativo',
    },
  },
  device_form: {
    exit_button: 'Sair',
    save_button: 'Guardar',
    saving_button: 'Guardando...',
    create_device_title: 'Criar dispositivo',
    type_input: 'Tipo',
    state_input: 'Estado',
    state_value_true: 'Ativo',
    state_value_false: 'Inativo',
    number_input: 'Número',
    entity_input: 'Entidade',
    user_input: 'Usuário',
    language_input: 'Idioma',
    language_placeholder_input: 'Selecione um idioma',
    sign_in_type_input: 'Tipo de início de sessão',
    sign_in_type_placeholder_input: 'Selecione um tipo',
    mac_address_input: 'Endereço MAC',
    serial_number_input: 'Número de série',
    user_info_message:
      'Este campo só pode ser preenchido se uma entidade for previamente selecionada.',
    edit_type_not_allowed_tooltip_title:
      'Não possui permissões para indicar o tipo de dispositivo.',
    edit_serial_number_not_allowed_tooltip_title:
      'Não possui permissões para indicar o número de série.',
    edit_mac_address_not_allowed_tooltip_title:
      'Não possui permissões para indicar o endereço MAC.',
    edit_number_not_allowed_tooltip_title: 'Não possui permissões para indicar o número.',
    edit_sign_in_type_not_allowed_tooltip_title:
      'Não possui permissões para indicar o tipo de início de sessão.',
    input_disabled_tooltip: 'Não possui permissões para editar os detalhes do dispositivo',
    deleting_button: 'Apagando...',
    delete_button: 'Apagar dispositivo',
    delete_dialog_title:
      'Tem a certeza que deseja apagar o dispositivo com identificação "{{name}}"?',
    delete_dialog_content: 'Ao apagar o dispositivo, este será apagado permanentemente do sistema.',
    create_dialog_title: 'Guardar criação do dispositivo com identificação "{{name}}"?',
    create_dialog_message: 'Ao guardar, o novo dispositivo será adicionado ao sistema.',
    edit_dialog_title: 'Guardar as alterações no dispositivo com identificação "{{name}}"?',
    edit_dialog_message:
      'Ao guardar, o dispositivo será atualizado com as informações recentemente inseridas.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Guardar',
    router_prompt_create_title:
      'Tem a certeza que deseja sair sem guardar criação do dispositivo com identificação "{{name}}"?',
    router_prompt_create_message:
      'Ao sair, o dispositivo não será guardado e todas as informações nele inseridas serão apagadas.',
    router_prompt_edit_title:
      'Tem a certeza que deseja sair sem guardar as alterações no dispositivo com identificação "{{name}}"?',
    router_prompt_edit_message:
      'Ao sair, perderá todas as alterações recentemente inseridas no dispositivo.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Sim, sair',
    edit_cancel_toast: 'A edição do dispositivo com identificação "{{name}}" foi cancelada.',
    create_cancel_toast: 'A criação do novo dispositivo foi cancelada.',
    success: {
      create_message: 'O dispositivo com identificação "{{name}}" foi criado com sucesso!',
      edit_message:
        'As alterações no dispositivo com identificação "{{name}}" foram guardadas com sucesso!',
      deleted_message: 'O dispositivo com identificação "{{name}}" foi apagado.',
    },
    errors: {
      create_message: 'Não foi possível criar o dispositivo com identificação "{{name}}".',
      edit_message:
        'Não foi possível guardar as alterações no dispositivo com identificação "{{name}}".',
      edit_form_validation_invalid_field:
        'Preencha este campo para completar a edição do dispositivo',
      create_form_validation_invalid_field:
        'Preencha este campo para completar a criação do dispositivo',
      deleted_message: 'Não foi possível apagar o dispositivo com identificação "{{name}}".',
    },
    isInDebugMode_input: 'Modo de depuração',
    isInDebugMode_placeholder_input: 'Por definir',
  },
  user_sign_in_types: {
    [UserSignInType.Card]: 'Cartão',
    [UserSignInType.Quick]: 'Rápido',
    [UserSignInType.Code]: 'Código',
  },
  entity_special_purposes: {
    [EntitySpecialPurpose.Commercial]: 'Comercial',
    [EntitySpecialPurpose.Demonstration]: 'Demonstrações',
    [EntitySpecialPurpose.Research]: 'Investigação',
    [EntitySpecialPurpose.Testing]: 'Testes',
  },
  entity_statistics_dashboard: {
    title: 'Estatísticas',
    refresh_button: 'Atualizar',
    text_charts: {
      number_of_active_seniors_title: 'Utentes',
      number_of_active_seniors_subtitle: 'Utentes ativos',
      number_of_active_professional_caregivers_title: 'Cuidadores profissionais',
      number_of_active_professional_caregivers_subtitle:
        'Cuidadores ativos afiliados com a entidade',
      number_of_active_informal_caregivers_title: 'Cuidadores informais',
      number_of_active_informal_caregivers_subtitle: 'Cuidadores ativos com relações com utentes',
      number_of_pending_invites_title: 'Convites pendentes',
      number_of_pending_invites_subtitle: 'Convites a aguardar resposta',
      number_of_calls_title: 'Chamadas',
      number_of_calls_subtitle: 'Duração maior ou igual a 5 segundos',
      number_of_outgoing_calls_title: 'Chamadas efetuadas',
      number_of_outgoing_calls_subtitle: 'Duração maior ou igual a 5 segundos',
      number_of_incoming_calls_title: 'Chamadas recebidas',
      number_of_incoming_calls_subtitle: 'Duração maior ou igual a 5 segundos',
    },
    table_charts: {
      number_of_active_devices_title: 'Número de dispositivos ativos',
      number_of_active_digital_inclusion_devices_title: 'Número de dispositivos Inclusão digital',
      number_of_active_quick_access_devices_title: 'Número de dispositivos Acesso Rápido',
      number_of_active_devices_device_type_column: 'Dispositivo',
      number_of_active_devices_total_column: 'Total',
      top_users_title: 'Top usuários',
      top_users_subtitle: 'Horas em atividades (duração maior ou igual a 5 segundos)',
      top_users_user_column: 'Usuário',
      top_users_total_usage_time_column: 'Tempo de utilização',
      top_users_usage_time_column_unit_label: 'horas',
    },
    area_charts: {
      general_system_usage_title: 'Utilização geral do sistema',
      general_system_usage_subtitle: 'Horas em atividades (duração maior ou igual a 5 segundos)',
      general_system_usage_x_label: 'Data',
      general_system_usage_y_label: 'Duração',
      general_system_usage_y_unit_label: 'horas',
    },
    bar_charts: {
      calls_evolution_title: 'Evolução das chamadas',
      calls_evolution_subtitle: 'Duração maior ou igual a 5 segundos',
      calls_evolution_x_label: 'Data',
      calls_evolution_y_label: 'Chamadas',
    },
    pie_charts: {
      most_used_applications_title: 'Aplicativos mais utilizadas',
      most_used_applications_subtitle: 'Horas em atividades (duração maior ou igual a 5 segundos)',
      most_used_applications_x_label: 'Aplicativo',
      most_used_applications_y_label: 'Duração',
      most_used_applications_y_unit_label: 'horas',
    },
    stack_charts: {
      applications_usage_title: 'Utilização por aplicativo',
      applications_usage_subtitle: 'Horas em atividades (duração maior ou igual a 5 segundos)',
      applications_usage_singular_stack_label: 'Aplicativo',
      applications_usage_plural_stack_label: 'Aplicativos',
      applications_usage_x_label: 'Data',
      applications_usage_y_label: 'Duração',
      applications_usage_y_unit_label: 'horas',
    },
  },
  senior_statistics_dashboard: {
    title: 'Estatísticas',
    refresh_button: 'Atualizar',
    statistics_per_application_title: 'Estatísticas por aplicativo',
    statistics_per_application_application_label: 'Aplicativo',
    statistics_per_application_entity_label: 'Entidade',
    statistics_per_application_entity_placeholder: 'Selecione uma entidade',
    statistics_per_application_info_message:
      'Selecione uma entidade de forma a selecionar um aplicativo e consultar as suas estatísticas.',
    text_charts: {
      number_of_informal_caregivers_title: 'Cuidadores informais',
      number_of_informal_caregivers_subtitle: 'Relações com utente',
      number_of_pending_invites_title: 'Convites pendentes',
      number_of_pending_invites_subtitle: 'Convites a aguardar resposta',
      number_of_calls_title: 'Chamadas',
      number_of_calls_subtitle: 'Duração maior ou igual a 5 segundos',
      number_of_outgoing_calls_title: 'Chamadas efetuadas',
      number_of_outgoing_calls_subtitle: 'Duração maior ou igual a 5 segundos',
      number_of_incoming_calls_title: 'Chamadas recebidas',
      number_of_incoming_calls_subtitle: 'Duração maior ou igual a 5 segundos',
      average_daily_usage_last_title: 'Média de utilização diária',
      average_daily_usage_subtitle: 'Média dos últimos 30 dias',
      average_daily_usage_unit_label: 'horas',
      number_of_media_received_title: 'Multimédia recebidos',
      number_of_media_received_subtitle: 'Fotos e vídeos',
      number_of_video_messages_received_title: 'Mensagens de vídeo recebidas',
      number_of_video_messages_sent_title: 'Mensagens de vídeo enviadas',
      number_of_photos_sent_title: 'Fotos enviadas',
      number_of_drawings_sent_title: 'Desenhos enviados',
      number_of_paintings_sent_title: 'Pinturas enviadas',
      last_access_date_time_title: 'Último acesso',
    },
    table_charts: {
      top_preferences_title: 'Top preferências',
      top_preferences_subtitle: 'Horas em atividades (duração maior ou igual a 5 segundos)',
      top_preferences_application_column: 'Aplicativo',
      top_preferences_total_usage_time_column: 'Tempo de utilização',
      top_preferences_total_usage_time_column_unit_label: 'horas',
    },
    area_charts: {
      general_system_usage_title: 'Utilização geral do sistema',
      general_system_usage_subtitle: 'Horas em atividades (duração maior ou igual a 5 segundos)',
      general_system_usage_x_label: 'Data',
      general_system_usage_y_label: 'Duração',
      general_system_usage_y_unit_label: 'horas',
      application_usage_title: 'Utilização "{{name}}"',
      application_usage_subtitle: 'Horas em atividades (duração maior ou igual a 5 segundos)',
      application_usage_x_label: 'Data',
      application_usage_y_label: 'Duração',
      application_usage_y_unit_label: 'horas',
    },
    pie_charts: {
      most_used_applications_title: 'Aplicativos mais utilizadas',
      most_used_applications_subtitle: 'Horas em atividades (duração maior ou igual a 5 segundos)',
      most_used_applications_x_label: 'Aplicativo',
      most_used_applications_y_label: 'Duração',
      most_used_applications_y_unit_label: 'horas',
    },
    stack_charts: {
      applications_usage_title: 'Utilização por aplicativo',
      applications_usage_subtitle: 'Horas em atividades (duração maior ou igual a 5 segundos)',
      applications_usage_singular_stack_label: 'Aplicativo',
      applications_usage_plural_stack_label: 'Aplicativos',
      applications_usage_x_label: 'Data',
      applications_usage_y_label: 'Duração',
      applications_usage_y_unit_label: 'horas',
    },
    line_charts: {
      application_evolution_title: 'Evolução "{{name}}"',
      application_evolution_subtitle: 'Alterações de nível',
      application_evolution_x_label: 'Data',
      application_evolution_y_label: 'Nível',
    },
  },
  charts: {
    no_data: 'Não existem dados.',
  },
  media_gallery: {
    refresh: 'Atualizar',
    no_more_data: 'Não existem mais dados',
    without_data: 'Não existem dados',
  },
  application_category_form: {
    exit_button: 'Sair',
    create_application_category_title: 'Criar categoria',
    save_button: 'Guardar',
    saving_button: 'Guardando...',
    default_name_input: 'Nome',
    default_description_input: 'Descrição',
    enabled_state_color_input: 'Cor do estado ativado',
    pressed_state_color_input: 'Cor do estado pressionado',
    name_translations_input: 'Traduções para o nome',
    description_translations_input: 'Traduções para a descrição',
    deleting_button: 'Apagando...',
    delete_button: 'Apagar categoria',
    delete_dialog_title: 'Apagar categoria "{{name}}"?',
    delete_dialog_content: 'Ao apagar a categoria, esta será apagada permanentemente do sistema.',
    delete_dialog_action_yes_button: 'Sim, apagar',
    delete_dialog_action_no_button: 'Cancelar',
    router_prompt_create_title:
      'Tem a certeza que deseja sair sem guardar criação da categoria "{{name}}"?',
    router_prompt_create_message:
      'Ao sair, a categoria não será guardada e todas as informações nela inseridas serão apagadas.',
    router_prompt_edit_title:
      'Tem a certeza que deseja sair sem guardar as alterações na categoria "{{name}}"?',
    router_prompt_edit_message:
      'Ao sair, perderá todas as alterações recentemente inseridas na categoria.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Sim, sair',
    edit_cancel_toast: 'A edição da categoria "{{name}}" foi cancelada.',
    create_cancel_toast: 'A criação da nova categoria foi cancelada.',
    create_dialog_title: 'Guardar criação da categoria "{{name}}"?',
    create_dialog_message: 'Ao guardar, a nova categoria será adicionada ao sistema.',
    edit_dialog_title: 'Guardar alterações na categoria "{{name}}"?',
    edit_dialog_message:
      'Ao guardar, a categoria será atualizada com as informações recentemente inseridas.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Guardar',
    success: {
      save_success_button: 'Guardada!',
      delete_success_button: 'Apagada!',
      create_success_message: 'A categoria com identificação "{{name}}" foi criada com sucesso!',
      edit_success_message: 'As alterações na categoria foram guardadas.',
    },
    errors: {
      error_on_save_button: 'Erro a guardar',
      error_on_delete_button: 'Erro ao apagar',
      edit_form_validation_invalid_field:
        'Preencha este campo para completar a edição da categoria',
      create_form_validation_invalid_field:
        'Preencha este campo para completar a criação da categoria',
      form_validation_invalid_color: 'A cor indicada não é válida',
    },
  },
  playlist_types: {
    [PlaylistType.Karaoke]: 'Karaoke',
    [PlaylistType.Movies]: 'Vídeos',
    [PlaylistType.Music]: 'Música',
  },
  playlists: {
    system_administrator: {
      root: {
        title: 'Listas de reprodução',
        search_input_placeholder: 'Pesquisar listas',
        type_filter: 'Tipo',
        name_header: 'Nome',
        no_playlists: 'Não existem listas de reprodução.',
        add_button: 'Adicionar lista de reprodução',
        deleted_playlist_dialog_title: 'Lista de reprodução "{{name}}" apagada',
        deleted_playlist_dialog_message: 'A lista de reprodução já não existe no sistema.',
        deleted_playlist_dialog_ok_button: 'Ok',
      },
      edit: {
        tabs: {details: 'Detalhes', items: 'Itens', entities: 'Entidades'},
        items: {
          search_input_placeholder: 'Pesquisar itens',
          title_header: 'Título',
          no_items: 'Não existem itens na lista de reprodução.',
          add_button: 'Adicionar item',
          deleted_playlist_item_dialog_title: 'Item "{{name}}" apagado da lista de reprodução',
          deleted_playlist_item_dialog_message: 'O item já não existe no sistema.',
          deleted_playlist_item_dialog_ok_button: 'Ok',
        },
        entities: {
          search_input_placeholder: 'Pesquisar entidades',
          name_header: 'Nome',
          type_header: 'Tipo',
          state_header: 'Estado',
          state_value_true: 'Ativo',
          state_value_false: 'Inativo',
          no_entities: 'Não existem entidades.',
          enable_playlist_dialog_title: 'Ativar a lista de reprodução "{{name}}"?',
          enable_playlist_dialog_message:
            'Ao ativar a lista de reprodução, ela estará disponível para a entidade {{name}}.',
          enable_playlist_dialog_action_yes_button: 'Sim, ativar',
          enable_playlist_dialog_action_no_button: 'Cancelar',
          disable_playlist_dialog_title: 'Desativar a lista de reprodução "{{name}}"?',
          disable_playlist_dialog_message:
            'Ao desativar a lista de reprodução, ela deixará de estar disponível para a entidade {{name}}.',
          disable_playlist_dialog_action_yes_button: 'Sim, desativar',
          disable_playlist_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_playlist_error_message:
              'Um erro ocorreu. Não foi possível ativar a lista de reprodução.',
            disable_playlist_error_message:
              'Um erro ocorreu. Não foi possível desativar a lista de reprodução.',
            update_state_playlist_error_message:
              'Um erro ocorreu. Não foi possível atualizar o estado da lista de reprodução.',
          },
        },
        errors: {
          playlist_does_not_exist_error:
            'Não foi possível obter a lista de reprodução que tentou aceder.',
        },
      },
      playlist_item: {
        tabs: {details: 'Detalhes', entities: 'Entidades'},
        entities: {
          search_input_placeholder: 'Pesquisar entidades',
          name_header: 'Nome',
          type_header: 'Tipo',
          state_header: 'Estado',
          state_value_true: 'Ativo',
          state_value_false: 'Inativo',
          no_entities: 'Não existem entidades.',
          enable_item_dialog_title: 'Ativar o item "{{name}}" na lista de reprodução?',
          enable_item_dialog_message:
            'Ao ativar o item na lista de reprodução, ele estará disponível para a entidade {{name}}.',
          enable_item_dialog_action_yes_button: 'Sim, ativar',
          enable_item_dialog_action_no_button: 'Cancelar',
          disable_item_dialog_title: 'Desativar o item "{{name}}" na lista de reprodução?',
          disable_item_dialog_message:
            'Ao desativar o item na lista de reprodução, ele deixará de estar disponível para a entidade {{name}}.',
          disable_item_dialog_action_yes_button: 'Sim, desativar',
          disable_item_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_item_error_message:
              'Um erro ocorreu. Não foi possível ativar o item na lista de reprodução.',
            disable_item_error_message:
              'Um erro ocorreu. Não foi possível desativar o item na lista de reprodução.',
            update_state_item_error_message:
              'Um erro ocorreu. Não foi possível atualizar o estado do item na lista de reprodução.',
          },
        },
        errors: {item_does_not_exist_error: 'Não foi possível obter o item que tentou aceder.'},
      },
    },
    professional_caregiver: {
      root: {
        search_input_placeholder: 'Pesquisar listas',
        type_filter: 'Tipo',
        name_header: 'Nome',
        state_header: 'Estado',
        state_value_true: 'Ativo',
        state_value_false: 'Inativo',
        no_playlists: 'Não existem listas de reprodução.',
        enable_playlist_dialog_title: 'Ativar a lista de reprodução "{{name}}"?',
        enable_playlist_dialog_message:
          'Ao ativar a lista de reprodução, ela estará disponível para todos os usuários da entidade {{name}}.',
        enable_playlist_dialog_action_yes_button: 'Sim, ativar',
        enable_playlist_dialog_action_no_button: 'Cancelar',
        disable_playlist_dialog_title: 'Desativar a lista de reprodução "{{name}}"?',
        disable_playlist_dialog_message:
          'Ao desativar a lista de reprodução, ela deixará de estar disponível para todos os usuários da entidade {{name}}.',
        disable_playlist_dialog_action_yes_button: 'Sim, desativar',
        disable_playlist_dialog_action_no_button: 'Cancelar',
        errors: {
          enable_playlist_error_message:
            'Um erro ocorreu. Não foi possível ativar a lista de reprodução.',
          disable_playlist_error_message:
            'Um erro ocorreu. Não foi possível desativar a lista de reprodução.',
          update_state_playlist_error_message:
            'Um erro ocorreu. Não foi possível atualizar o estado da lista de reprodução.',
        },
      },
      edit: {
        tabs: {
          items: 'Itens',
          users: 'Usuários',
          details: 'Detalhes',
        },
        items: {
          search_input_placeholder: 'Pesquisar itens',
          title_header: 'Título',
          state_header: 'Estado',
          state_value_true: 'Ativo',
          state_value_false: 'Inativo',
          no_items: 'Não existem itens na lista de reprodução.',
          enable_item_dialog_title: 'Ativar o item "{{name}}" na lista de reprodução?',
          enable_item_dialog_message:
            'Ao ativar o item na lista de reprodução, ele estará disponível para todos os usuários da entidade {{name}}.',
          enable_item_dialog_action_yes_button: 'Sim, ativar',
          enable_item_dialog_action_no_button: 'Cancelar',
          disable_item_dialog_title: 'Desativar o item "{{name}}" na lista de reprodução?',
          disable_item_dialog_message:
            'Ao desativar o item na lista de reprodução, ele deixará de estar disponível para todos os usuários da entidade {{name}}.',
          disable_item_dialog_action_yes_button: 'Sim, desativar',
          disable_item_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_item_error_message:
              'Um erro ocorreu. Não foi possível ativar o item na lista de reprodução.',
            disable_item_error_message:
              'Um erro ocorreu. Não foi possível desativar o item na lista de reprodução.',
            update_state_item_error_message:
              'Um erro ocorreu. Não foi possível atualizar o estado do item na lista de reprodução.',
          },
        },
        users: {
          search_input_placeholder: 'Pesquisar usuários',
          name_header: 'Nome',
          type_header: 'Tipo',
          state_header: 'Estado',
          state_value_true: 'Ativo',
          state_value_false: 'Inativo',
          no_users: 'Não existem usuários.',
          enable_playlist_dialog_title: 'Ativar a lista de reprodução "{{name}}"?',
          enable_playlist_dialog_message:
            'Ao ativar a lista de reprodução, ela estará disponível para {{name}}.',
          enable_playlist_dialog_action_yes_button: 'Sim, ativar',
          enable_playlist_dialog_action_no_button: 'Cancelar',
          disable_playlist_dialog_title: 'Desativar a lista de reprodução "{{name}}"?',
          disable_playlist_dialog_message:
            'Ao desativar a lista de reprodução, ela deixará de estar disponível para {{name}}.',
          disable_playlist_dialog_action_yes_button: 'Sim, desativar',
          disable_playlist_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_playlist_error_message:
              'Um erro ocorreu. Não foi possível ativar a lista de reprodução.',
            disable_playlist_error_message:
              'Um erro ocorreu. Não foi possível desativar a lista de reprodução.',
            update_state_playlist_error_message:
              'Um erro ocorreu. Não foi possível atualizar o estado da lista de reprodução.',
          },
        },
        errors: {
          playlist_does_not_exist_error:
            'Não foi possível obter a lista de reprodução que tentou aceder.',
        },
      },
      playlist_item: {
        tabs: {
          users: 'Usuários',
          details: 'Detalhes',
        },
        users: {
          search_input_placeholder: 'Pesquisar usuários',
          name_header: 'Nome',
          type_header: 'Tipo',
          state_header: 'Estado',
          state_value_true: 'Ativo',
          state_value_false: 'Inativo',
          no_users: 'Não existem usuários.',
          enable_item_dialog_title: 'Ativar o item "{{name}}" na lista de reprodução?',
          enable_item_dialog_message:
            'Ao ativar o item na lista de reprodução, ele estará disponível para {{name}}.',
          enable_item_dialog_action_yes_button: 'Sim, ativar',
          enable_item_dialog_action_no_button: 'Cancelar',
          disable_item_dialog_title: 'Desativar o item "{{name}}" na lista de reprodução?',
          disable_item_dialog_message:
            'Ao desativar o item na lista de reprodução, ele deixará de estar disponível para {{name}}.',
          disable_item_dialog_action_yes_button: 'Sim, desativar',
          disable_item_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_item_error_message:
              'Um erro ocorreu. Não foi possível ativar o item na lista de reprodução.',
            disable_item_error_message:
              'Um erro ocorreu. Não foi possível desativar o item na lista de reprodução.',
            update_state_item_error_message:
              'Um erro ocorreu. Não foi possível atualizar o estado do item na lista de reprodução.',
          },
        },
        errors: {item_does_not_exist_error: 'Não foi possível obter o item que tentou aceder.'},
      },
    },
  },
  user_playlists: {
    search_input_placeholder: 'Pesquisar listas',
    type_filter: 'Tipo',
    entity_filter: 'Entidade',
    name_header: 'Nome',
    state_header: 'Estado',
    state_value_true: 'Ativo',
    state_value_false: 'Inativo',
    state_disabled_tooltip: 'Não possui permissões para editar o estado da lista de reprodução',
    no_playlists: 'Não existem listas de reprodução.',
    no_affiliations_info_message:
      'O usuário não possui afiliação com nenhuma entidade. Assim, não é possível consultar as listas de reprodução do usuário.',
    enable_playlist_dialog_title:
      'Tem a certeza que deseja ativar a lista de reprodução "{{name}}"?',
    enable_playlist_dialog_message:
      'Ao ativar a lista de reprodução, ela estará disponível para {{name}}.',
    enable_playlist_for_entity_dialog_message:
      'Ao ativar a lista de reprodução, ela estará disponível para {{name}} na entidade {{entity}}.',
    enable_playlist_dialog_action_yes_button: 'Sim, ativar',
    enable_playlist_dialog_action_no_button: 'Cancelar',
    disable_playlist_dialog_title:
      'Tem a certeza que deseja desativar a lista de reprodução "{{name}}"?',
    disable_playlist_dialog_message:
      'Ao desativar a lista de reprodução, ela deixará de estar disponível para {{name}}.',
    disable_playlist_for_entity_dialog_message:
      'Ao desativar a lista de reprodução, ela deixará de estar disponível para {{name}} na entidade {{entity}}.',
    disable_playlist_dialog_action_yes_button: 'Sim, desativar',
    disable_playlist_dialog_action_no_button: 'Cancelar',
    errors: {
      enable_playlist_error_message:
        'Um erro ocorreu. Não foi possível ativar a lista de reprodução.',
      disable_playlist_error_message:
        'Um erro ocorreu. Não foi possível desativar a lista de reprodução.',
      update_state_playlist_error_message:
        'Um erro ocorreu. Não foi possível atualizar o estado da lista de reprodução.',
    },
  },
  time_lapse_types: {
    [TimeLapseType.Custom]: 'Personalizado',
    [TimeLapseType.Last180Days]: 'Últimos 180 dias',
    [TimeLapseType.Last30Days]: 'Últimos 30 dias',
    [TimeLapseType.Last365Days]: 'Últimos 365 dias',
    [TimeLapseType.LastMonth]: 'Mês passado',
    [TimeLapseType.LastWeek]: 'Semana passada',
    [TimeLapseType.ThisMonth]: 'Este mês',
    [TimeLapseType.ThisWeek]: 'Esta semana',
  },
  time_lapse: {
    dialog_title: 'Escolha um intervalo de tempo',
    range_label: 'Tipo de intervalo',
    start_text: 'Início',
    end_text: 'Fim',
    to_text: 'até',
    cancel_button: 'Cancelar',
    confirm_button: 'Confirmar',
  },
  device_health: {
    no_data: 'Não existem dados.',
    last_recording: 'Último registo',
    basic_information_section_title: 'Informação básica',
    wifi_section_title: 'Wi-Fi',
    networks_wifi_subsection_title: 'Redes disponíveis',
    current_wifi_subsection_title: 'Redes conectadas',
    mobile_data_section_title: 'Dados móveis',
    graphics_section_title: 'Gráficos',
    controllers_graphics_subsection_title: 'Controladores',
    displays_graphics_subsection_title: 'Ecrãs',
    system_section_title: 'Sistema',
    battery_section_title: 'Bateria',
    disks_section_title: 'Discos',
    operating_system_section_title: 'Sistem operativo',
    cpu_section_title: 'CPU',
    memory_section_title: 'Memória',
    applications_section_title: 'Aplicativos',
    versions_section_title: 'Versões',
    request_tracking_section_title: 'Acompanhamento',
    cache_images_section_title: 'Cache (Imagens)',
    creation_date_label: 'Data de criação',
    expiration_date_label: 'Data de expiração',
    entity_label: 'Entidade',
    user_label: 'Usuário',
    data_usage_label: 'Dados consumidos',
    ssid_label: 'SSID',
    quality_label: 'Qualidade',
    ip_address_label: 'Endereço IP',
    is_data_usage_enabled_label: 'Consumo de dados móveis ativado?',
    carrier_label: 'Operadora',
    network_type_label: 'Tipo de rede',
    dialer_label: 'Marcador',
    signal_strength_label: 'Força do sinal',
    signal_strength_level_label: 'Nível da força do sinal',
    signal_strength_no: 'Sem sinal',
    signal_strength_weak: 'Fraco',
    signal_strength_fair: 'Razoável',
    signal_strength_good: 'Bom',
    signal_strength_strong: 'Forte',
    brand_label: 'Marca',
    model_label: 'Modelo',
    is_main_display_label: 'É o ecrã principal?',
    width_label: 'Largura',
    height_label: 'Altura',
    current_width_label: 'Largura atual',
    current_height_label: 'Altura atual',
    current_scale_factor_label: 'Fator de escala',
    sku_label: 'SKU',
    serial_number_label: 'Número de série',
    version_label: 'Versão',
    has_battery_label: 'Tem bateria?',
    is_battery_charging_label: 'Está a carregar?',
    is_battery_connected_to_power_label: 'Está ligado à corrente?',
    battery_level_label: 'Estado',
    size_label: 'Total',
    used_disks_label: 'Utilizado',
    available_label: 'Disponível',
    mount_label: 'Ponto de montagem',
    name_label: 'Nome',
    distribution_label: 'Distribuição',
    release_label: 'Lançamento',
    build_label: 'Compilação',
    web_view_version_label: 'Versão WebView',
    cores_label: 'Número de núcleos',
    used_memory_label: 'Utilizada',
    country_label: 'País',
    region_label: 'Região',
    city_label: 'Cidade',
    city_longitude_label: 'Longitude',
    city_latitude_label: 'Latitude',
    language_label: 'Idioma',
    user_ip_address_label: 'Endereço IP do usuário',
    yes_label: 'Sim',
    no_label: 'Não',
    gb_unit_label: 'GB',
    mb_unit_label: 'MB',
    px_unit_label: 'px',
    agent_label: 'Agente',
    chrome_label: 'Chrome',
    electron_label: 'Electron',
    launcher_binary_label: 'Binário do Launcher',
    launcher_frontend_label: 'Frontend do Launcher',
  },
  operative_system_names: {
    [OperativeSystemName.Android]: 'Android',
    [OperativeSystemName.Linux]: 'Linux',
    [OperativeSystemName.MacOS]: 'MacOS',
    [OperativeSystemName.Other]: 'Outro',
    [OperativeSystemName.Windows]: 'Windows',
    [OperativeSystemName.iOS]: 'iOS',
  },
  mobile_network_types: {
    [MobileNetworkType.Unspecified]: 'Outro',
    [MobileNetworkType._1G]: '1G',
    [MobileNetworkType._2G]: '2G',
    [MobileNetworkType._3G]: '3G',
    [MobileNetworkType._4G]: '4G',
    [MobileNetworkType._5G]: '5G',
  },
  home: {
    refreshing: 'Atualizando...',
    release_to_refresh: 'Solte para atualizar',
    pull_to_refresh: 'Puxe para atualizar',
    none: {
      root: {
        no_associations_message_title:
          'A sua conta de acesso à plataforma {{brand}} não tem associação a nenhum utente ou entidade neste momento.',
        no_associations_message_subtitle:
          'Por favor, solicite um convite a outros cuidadores ou entre em contacto com o Apoio ao Cliente.',
      },
    },
  },
  localized_string_input: {
    cancel_button: 'Cancelar',
    confirm_button: 'Confirmar',
    has_zero_translations_tooltip_message:
      'Não existem traduções definidas num total de {{total}} possíveis.',
    has_one_translation_tooltip_message:
      'Existe uma tradução definida num total de {{total}} possíveis.',
    has_many_translations_tooltip_message:
      'Existem {{count}} traduções definidas num total de {{total}} possíveis.',
  },
  user_playlist_items: {
    title: 'Lista de reprodução "{{name}}"',
    search_input_placeholder: 'Pesquisar itens',
    title_header: 'Título',
    state_header: 'Estado',
    state_value_true: 'Ativo',
    state_value_false: 'Inativo',
    state_disabled_tooltip: 'Não possui permissões para editar o estado do item',
    no_items: 'Não existem itens na lista de reprodução.',
    enable_item_dialog_title:
      'Tem a certeza que deseja ativar o item "{{name}}" na lista de reprodução?',
    enable_item_dialog_message:
      'Ao ativar o item na lista de reprodução, ele estará disponível para {{name}}.',
    enable_item_dialog_action_yes_button: 'Sim, ativar',
    enable_item_dialog_action_no_button: 'Cancelar',
    disable_item_dialog_title:
      'Tem a certeza que deseja desativar o item "{{name}}" na lista de reprodução?',
    disable_item_dialog_message:
      'Ao desativar o item na lista de reprodução, ele deixará de estar disponível para {{name}}.',
    disable_item_dialog_action_yes_button: 'Sim, desativar',
    disable_item_dialog_action_no_button: 'Cancelar',
    errors: {
      enable_item_error_message:
        'Um erro ocorreu. Não foi possível ativar o item na lista de reprodução.',
      disable_item_error_message:
        'Um erro ocorreu. Não foi possível desativar o item na lista de reprodução.',
      update_state_item_error_message:
        'Um erro ocorreu. Não foi possível atualizar o estado do item na lista de reprodução.',
      playlist_does_not_exist_error:
        'Não foi possível obter a lista de reprodução que tentou aceder.',
    },
  },
  playlist_form: {
    exit_button: 'Sair',
    create_playlist_title: 'Criar lista de reprodução',
    save_button: 'Guardar',
    saving_button: 'Guardando...',
    default_name_input: 'Nome',
    name_translations_input: 'Traduções para o nome',
    default_description_input: 'Descrição',
    description_translations_input: 'Traduções para a descrição',
    enabled_state_color_input: 'Cor do estado ativado',
    pressed_state_color_input: 'Cor do estado pressionado',
    state_input: 'Estado',
    state_value_true: 'Ativo',
    state_value_false: 'Inativo',
    type_input: 'Tipo',
    active_by_default_for_all_entities_input: 'Ativo por defeito para todas as entidades?',
    active_by_default_for_all_entities_placeholder_input: 'Selecione uma opção',
    yes_value: 'Sim',
    no_value: 'Não',
    image_url_input: 'Imagem',
    image_info_message:
      'A imagem deverá ser do tipo JPG/JPEG. Para melhores resultados deverá utilizar a resolução de 300x300px.',
    regional_lockout_input: 'Bloqueio regional',
    regional_lockout_placeholder: 'Selecione regiões',
    deleting_button: 'Apagando...',
    delete_button: 'Apagar lista de reprodução',
    delete_dialog_title: 'Tem a certeza que deseja apagar lista de reprodução "{{name}}"?',
    delete_dialog_content:
      'Ao apagar a lista de reprodução, esta será apagada permanentemente do sistema.',
    delete_dialog_action_yes_button: 'Sim, apagar',
    delete_dialog_action_no_button: 'Cancelar',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Guardar',
    router_prompt_create_title:
      'Tem a certeza que deseja sair sem guardar criação da lista de reprodução "{{name}}"?',
    router_prompt_create_message:
      'Ao sair, a lista de reprodução não será guardada e todas as informações nela inseridas serão apagadas.',
    router_prompt_edit_title:
      'Tem a certeza que deseja sair sem guardar as alterações na lista de reprodução "{{name}}"?',
    router_prompt_edit_message:
      'Ao sair, perderá todas as alterações recentemente inseridas na lista de reprodução.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Sim, sair',
    edit_cancel_toast: 'A edição da lista de reprodução "{{name}}" foi cancelada.',
    create_cancel_toast: 'A criação da nova lista de reprodução foi cancelada.',
    success: {
      save_success_button: 'Guardado!',
      delete_success_button: 'Apagado!',
      create_success_message: 'A lista de reprodução "{{name}}" foi criada com sucesso!',
      edit_success_message: 'As alterações na lista de reprodução foram guardadas.',
    },
    errors: {
      error_on_save_button: 'Erro a guardar',
      error_on_delete_button: 'Erro ao apagar',
      edit_form_validation_invalid_field:
        'Preencha este campo para completar a edição da lista de reprodução',
      create_form_validation_invalid_field:
        'Preencha este campo para completar a criação da lista de reprodução',
      form_validation_invalid_color: 'A cor indicada não é válida',
    },
  },
  playlist_item_form: {
    exit_button: 'Sair',
    create_playlist_item_title: 'Criar item na lista de reprodução',
    save_button: 'Guardar',
    saving_button: 'Guardando...',
    deleting_button: 'Apagando...',
    delete_button: 'Apagar item da lista de reprodução',
    delete_dialog_title:
      'Tem a certeza que deseja apagar o item "{{name}}" da lista de reprodução?',
    delete_dialog_content:
      'Ao apagar o item da lista de reprodução, este será apagado permanentemente do sistema.',
    delete_dialog_action_yes_button: 'Sim, apagar',
    delete_dialog_action_no_button: 'Cancelar',
    create_dialog_title: 'Guardar criação do item "{{name}}" na lista de reprodução?',
    create_dialog_message:
      'Ao guardar, o novo item para a lista de reprodução será adicionado ao sistema.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Guardar',
    router_prompt_create_title:
      'Tem a certeza que deseja sair sem guardar criação do item "{{name}}" na lista de reprodução?',
    router_prompt_create_message:
      'Ao sair, o item não será guardado na lista de reprodução e todas as informações nele inseridas serão apagadas.',
    router_prompt_edit_title:
      'Tem a certeza que deseja sair sem guardar as alterações no item "{{name}}" da lista de reprodução?',
    router_prompt_edit_message:
      'Ao sair, perderá todas as alterações recentemente inseridas no item da lista de reprodução.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Sim, sair',
    edit_cancel_toast: 'A edição do item "{{name}}" da lista de reprodução foi cancelada.',
    create_cancel_toast: 'A criação do novo item na lista de reprodução foi cancelada.',
    default_title_input: 'Título',
    title_translations_input: 'Traduções para o título',
    default_description_input: 'Descrição',
    description_translations_input: 'Traduções para a descrição',
    regional_lockout_input: 'Bloqueio regional',
    regional_lockout_placeholder: 'Selecione regiões',
    state_input: 'Estado',
    state_value_true: 'Ativo',
    state_value_false: 'Inativo',
    playlist_input: 'Lista de reprodução',
    resource_url_input: 'URL do recurso',
    resource_input: 'Recurso',
    thumbnail_image_url_input: 'Imagem',
    thumbnail_image_info_message:
      'A imagem deverá ser do tipo JPG/JPEG. Para melhores resultados deverá utilizar a resolução de 300x300px.',
    success: {
      save_success_button: 'Guardado!',
      delete_success_button: 'Apagado!',
      create_success_message: 'O item "{{name}}" foi criado com sucesso na lista de reprodução!',
      edit_success_message: 'As alterações no item da lista de reprodução foram guardadas.',
    },
    errors: {
      error_on_save_button: 'Erro a guardar',
      error_on_delete_button: 'Erro ao apagar',
      edit_form_validation_invalid_field: 'Preencha este campo para completar a edição do item',
      create_form_validation_invalid_field: 'Preencha este campo para completar a criação do item',
      form_validation_invalid_resource_url: 'O URL do recurso indicado não é válido',
    },
  },
  licenses: {
    professional_caregiver: {
      root: {
        refresh_button: 'Atualizar',
        number_of_monthly_active_licenses_title: 'Licenças ativas mensais',
        number_of_active_seniors_title: 'Utentes ativos',
        number_of_monthly_active_licenses_not_defined: 'Não definido',
      },
    },
  },
  videocall: {
    you: 'Eu',
    incomingCall: 'Está ligando para você...',
    incomingCallConnecting: 'Conectando..',
    incomingCallConnectingHint: '(isso pode demorar um pouco)',
    answer: 'Atender',
    decline: 'Rejeitar',
    settingsTitle: 'Configurações',
    settingsMicrophone: 'Microfone',
    settingsCamera: 'Câmera',
    settingsSpeaker: 'Altifalante',
    default: 'Padrão',
    callFailed: {
      title: 'A chamada falhou',
      ok: 'Está bem',
      e486: 'O número de destino está ocupado',
      e487: 'Pedido encerrado',
      e603: 'A chamada foi rejeitada',
      e404: 'Número inválido',
      e480: 'O número de destino não está disponível',
      e402: 'Fundos insuficientes',
    },
  },
  entityFrontCardDesignTool: {
    title: 'Design Frontal do Cartão de Entidade',
    step1: 'Entidade',
    step2: 'Usuários',
    step3: 'Resultado',
    entityPicture: 'Logótipo da entidade',
    chooseAnotherEntityPicture: 'Escolher outro logótipo para a entidade',
    entityName: 'Nome da entidade',
    addEntityName: 'Adicionar nome da entidade',
    entityNameToBePrinted: 'Nome da entidade a ser impresso',
    entityNameTextColor: 'Cor do texto do nome da entidade',
    entityNameTextFont: 'Fonte do texto do nome da entidade',
    next: 'Avançar',
    save: 'Guardar',
    restart: 'Reiniciar',
    failedToLoadUsers: 'Falha ao carregar os usuários',
  },
  entityBackCardDesignTool: {
    title: 'Design Traseiro do Cartão de Entidade',
    step1: 'Especificações',
    step2: 'Pré-visualização',
    entityName: 'Nome da entidade',
    entityNumber: 'Número da entidade',
    seniorUsersCount: 'Número de utentes',
    caregiverUsersCount: 'Número de cuidadores',
    cardName: 'Nome do cartão',
    entityPictureHeight: 'Altura da imagem da entidade',
    lineColor: 'Cor da linha',
    includeEntityName: 'Incluir nome da entidade',
    generatePreview: 'Gerar pré-visualização',
    saveCards: 'Guardar cartões',
    saveSpreadsheet: 'Guardar folha de cálculo',
    restart: 'Reiniciar',
  },
  cards: {
    pendingInvitation: 'Convite pendente',
    years: 'anos',
  },
  dashboard_side_menu: {
    dashboard: 'Visão Geral',
    general_activity: 'Actividade geral',
    statistics: 'Estatísticas',
    general_statistics: 'Estatísticas gerais',
    social_statistics: 'Estatísticas social',
  },
  digital_inclusion_side_menu: {
    applications: 'Aplicativos',
    details: 'Detalhes',
    users: 'Usuários',
    photos: 'Fotografias',
    cards: 'Cartões',
    devices: 'Dispositivos',
    services: 'Serviços',
    playlists: 'Listas de reprodução',
  },
  seniorSideMenu: {
    generalStatistics: 'Estatísticas gerais',
    healthStatistics: 'Estatísticas de saúde',
    basicActivitiesDailyLife: 'ABVD',
    careAndActivity: 'Resumo de cuidados',
    individualPlan: 'Plano individual',
    personalData: 'Dados pessoais',
    careServices: 'Escala de Serviço',
  },
  teamSideMenu: {
    professionals: 'Profissionais',
    shifts: {
      title: 'Turnos',
      shiftsManagement: 'Gestão dos turnos',
      shiftsAllocations: 'Atribuição de turnos',
    },
    attendanceTracker: {
      title: 'Registro de Ponto',
      assignments: 'Atribuições',
      registry: 'Registros',
    },
    careServices: 'Escala Geral de Serviço',
    permissions: 'Permissões',
    profileDropdown: {
      profile: 'Perfil',
      careServices: 'Escala de Serviço',
      applications: 'Aplicativos',
      cards: 'Cartões',
      playLists: 'Listas de reprodução',
    },
  },
  profileSideMenu: {
    profile: 'Perfil',
  },
  settingsSideMenu: {
    institution: 'Instituição',
    activities_sectors: {
      load_error: 'Não foi possível carregar os sectores de actividades',
    },
  },
  responsibleUserDropdown: {
    noResponsibleDefined: 'Responsável não definido',
    noCoordinatorDefined: 'Coordenador não definido',
  },
  activities_group_entry: {
    modals: {
      edit: {
        title: 'Editar detalhes',
        observations_max_characters: 'Máximo de 120 caracteres.',
      },
      confirm_delete: {
        title: 'Apagar ABVD - {{ name }}',
        copy: {
          title: 'Tem certeza que deseja apagar a atividade?',
          subtitle: 'Esta ação apagará a atividade da lista.',
        },
      },
    },
    activity_types: 'Tarefas',
    no_activity_types: 'Ainda não há tarefas adicionadas',
    configuration_type_not_defined: 'Não definido',
    configuration_type_label: 'Tipo',
    exceptions: 'Exceções',
    edit_details: 'Editar detalhes',
    add_activities: 'Adicionar',
    configure_first: 'Configure primeiro o tipo de repetição do grupo de actividades.',
    actions: {
      exit: 'Sair',
      edit: 'Editar',
      delete: 'Apagar',
      history: 'Histórico',
    },
    snackbar: {
      create: {
        success: 'A atividade foi adicionada com sucesso!',
        error: 'Não foi possível adicionar a atividade.',
      },
      update: {
        success: 'A atividade foi editada com sucesso!',
        error: 'Não foi possível editar a atividade.',
      },
      delete: {
        success: 'A atividade foi apagada com sucesso!',
        error: 'Não foi possível apagar a atividade.',
      },
      configuration: {
        success: 'A atividade foi carregada com sucesso.',
        error: 'Não foi possível carregar a atividade.',
      },
    },
    error: 'Não foi possível carregar os dados das actividades de grupo.',
  },
  activities_type_entry: {
    modals: {
      edit: {
        title: 'Detalhes tarefa',
      },
      delete: {
        title: 'Detalhes - {{ type }}',
        copy: {
          title: 'Tem a certeza que deseja remover {{ type }} das atividades?',
          subtitle: 'A atividade será removida da lista de atividades do utente.',
        },
      },
      view: {
        leave: 'Sair',
        view_only: 'Apenas para visualização, não pode editar.',
      },
    },
    snackbar: {
      update: {
        success: 'A tarefa foi editada com sucesso!',
        error: 'Não foi possível editar a tarefa.',
      },
      delete: {
        success: 'A tarefa foi eliminada com sucesso!',
        error: 'Não foi possível eliminar a tarefa.',
      },
      configuration: {
        success: 'A tarefa foi carregado com sucesso!',
        error: 'Não foi possível carregar a tarefa.',
      },
    },
    templates: {
      name: 'Nome do modelo',
      description: 'Descrição',
      activities_save: 'Atividades a guardar',
      activities_import: 'Selecione as atividades',
      check_all: 'Marcar todos',
      snackbar: {
        success_message:
          'O modelo "{{name}}" foi guardado com sucesso. Poderá editar ou apagar os modelos nas configurações.',
        error_message: 'O modelo "{{name}}" não pôde ser guardado.',
        import_success_message: 'O modelo "{{name}}" foi aplicado corretamente.',
        import_error_message: 'O modelo "{{name}}" não pôde ser aplicado.',
      },
      template: 'Modelo',
      confirmation_title: 'O modelo "{{name}}" selecionado será utilizado neste utente',
      confirmation_subtitle:
        'Poderá a qualquer momento editar ou apagar as atividades selecionadas',
      confirmation_replace_title:
        'Ao importar o modelo "{{name}}", os dados do plano atual serão substituídos',
      confirmation_replace_subtitle: 'Para avançar escreva a palavra "CONFIRMO”',
      confirm: 'confirmo',
      name_error: 'O nome do modelo deve ter menos do que {{max}} carateres',
      description_error: 'A descrição do modelo deve ter menos do que {{max}} carateres',
      select: 'Selecione um modelo',
    },
  },
  dynamic_form: {
    required_message: 'Campo obrigatório.',
    pattern_message: {
      invalid: 'Campo inválido.',
      max_characters: 'Máximo de {{ max }} caracteres.',
    },
    error_loading: 'Ocorreu um erro ao carregar o formulário.',
    feature_fields: {
      autocomplete: {
        load_more: 'Carregar mais',
        loading: 'Carregando...',
        no_options: 'Sem opções',
        placeholder: 'Selecione um item',
        counter: 'A mostrar {{ current }} de {{ max }} itens',
      },
    },
  },
  modal: {
    ok: 'Ok',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    add: 'Adicionar',
    save: 'Guardar',
    remove: 'Remover',
    import: 'Importar',
    back: 'Voltar',
    next: 'Próximo',
    delete: 'Apagar',
    revert: 'Reverter',
    archive: 'Arquivar',
  },
  daily_care_log: {
    generics: {
      back: 'Voltar',
    },
    senior_actions: {
      open_actions: 'Abrir ações para {{name}}',
      title: 'Ações para {{name}}',
    },
    tabs: {
      home: 'Início',
      occurrences: 'Ocorrências',
      attendance_tracker: 'Ponto',
    },
    date_carousel: {
      yesterday: 'Ontem',
      today: 'Hoje',
      tomorrow: 'Amanhã',
    },
    task_table: {
      tasks: 'Tarefas',
      empty: 'Não existem tarefas registadas.',
      select_all: 'Selecionar todos os utentes',
      sort: 'Ordernar',
      drawer: {
        clear: 'Limpar',
        save: 'Guardar',
        filters: {
          title: 'Filtros',
          social_responses: 'Serviços socioassistenciais',
        },
        sort: {
          title: 'Ordernar por',
          name: 'Nome',
          autonomy: 'Autonomia',
          status: 'Estado',
          ascending: 'Crescente',
          descending: 'Decrescente',
        },
      },
    },
    cards: {
      entity_user: {
        task_conclusion_summary_label: {
          concluded: 'Concluída',
          task: 'Tarefa',
        },
      },
    },
    home: {
      my_shifts: 'Os meus turnos',
      care_services: 'Escala de serviços',
      other_shifts: 'Outros turnos',
      no_allocated_shifts: 'Não há turnos atribuídos para este dia',
      no_other_shifts: 'Não há outros turnos para este dia',
      no_care_services: 'Não há serviços atribuídos para este dia',
      previous_days_shift: 'Turno com início no dia anterior',
    },
    seniors: {
      title: 'Utentes',
      no_seniors: 'Ainda não há utentes registados',
    },
    task_details: {
      task_not_done_reason: 'Motivo da não realização',
      task_done: 'Realizada',
      task_not_done: 'Não realizada',
      add_a_note: 'Escreva uma observação...',
      cancel: 'Cancelar',
      save: 'Guardar',
      register: 'Registar',
      edit: 'Editar',
      edit_entry: 'Editar registo',
      disabled_tooltip: 'Não tem permissões para editar esta tarefa.',
    },
    shift_collapsible: {
      shift: 'Turno',
      other_shift_members: 'Outras pessoas no turno',
      no_other_shift_members: 'Não existem outras pessoas no turno',
    },
    shift_details: {
      all_tasks_handled: 'Todas as tarefas do turno foram concluídas!',
      tabs: {
        simple: {
          title: 'Simples',
        },
        grouped: {title: 'Agrupadas', task: 'Tarefa'},
        user: {
          title: 'Utente',
          grid: {
            empty: 'Sem utentes',
            empty_search_message: {
              title: 'Desculpe, não conseguimos encontrar resultados para "{{ name }}".',
              subtitle: 'Tente uma pesquisa diferente.',
            },
          },
        },
      },
      unplanned_tasks: {
        header_title: 'Nova tarefa não planeada',
        activity_sector: 'Setor de actividade',
        activity: 'Atividade',
        name: 'Nome da tarefa',
        execution_date: 'Data de execução',
        execution_hour: 'Hora de execução',
        add_success_toast: 'A tarefa foi registada com sucesso!',
        add_error_toast: 'Não foi possível registar a tarefa.',
        upload_error_toast: 'Não foi possível carregar os ficheiros.',
        add_multiple_success_toast: 'As tarefas foram registadas com sucesso!',
        add_cancel_modal_title: 'Tem a certeza que deseja sair sem registar a tarefa?',
        add_cancel_modal_subtitle: 'Os dados inseridos serão perdidos.',
        add_user_placeholder: 'Adicione o utente',
        add_files: 'Adicionar ficheiros',
        delete_attachment: 'Tem a certeza que deseja apagar o ficheiro "{{ name }}"?',
        files_size: 'Tamanho máximo por arquivo {{ size }}MB.',
        file_size_error: 'Tamanho ultrapassa o máximo de {{ size }}MB.',
        file_not_supported_error: 'Formato do arquivo não suportado.',
        file_both_errors: 'Tamanho ultrapassa o máximo de {{ size }}MB e formato não é suportado.',
      },
    },
    grouped_tasks_details: {
      select_bottom_bar: {
        count: '{{ count }} utente selecionado',
        count_plural: '{{ count }} utentes selecionados',
        next: 'Próximo',
      },
    },
    grouped_tasks_handling: {
      snackbar: {
        loading_tasks: {
          error: 'Erro ao carregar as tarefas!',
        },
        updating_tasks: {
          success: 'As tarefas foram registadas com sucesso!',
          error: 'Erro ao registar as tarefas.',
        },
      },
    },
    attendance_tracker: {
      title: 'Registro de Ponto',
      empty_title: 'Ainda não há registros de ponto',
      empty_subtitle: 'Toque no botão para registrar o ponto',
      register: 'Registrar ponto',
      filters: {
        sections: {
          attendanceMovements: 'Movimento',
          attendanceStatus: 'Status',
          attendancePeriods: 'Período do dia',
        },
        values: {
          voided: 'Anulado',
          entry: 'Entradas',
          exit: 'Saídas',
          open: 'Em andamento',
          closed: 'Concluído',
          morning: 'Manhã',
          afternoon: 'Tarde',
          night: 'Noite',
        },
      },
      session: {
        entry: 'Entrada',
        exit: 'Saída',
        running: 'Acontecendo agora',
        overdue: 'Por fechar',
        closed: 'Concluído',
        elapsed: '{{duration}} decorridos',
        overdue_alert:
          'Você tem uma entrada em aberto desde {{date}}. A saída precisa ser registrada.',
        total: 'Total: {{duration}}',
      },
      bar: {
        running: 'Em andamento',
        since: 'Desde {{date}}',
        not_started: 'Registro de ponto não iniciado',
      },
      manual: {
        registered_by: 'Registrado por {{name}}',
        entry_registered_by: 'Entrada registrada por {{name}}',
        exit_registered_by: 'Saída registrada por {{name}}',
        notice: 'Registrado manualmente por {{name}} em {{date}}, em nome do profissional.',
        reason: 'Motivo: {{reason}}',
        no_location: 'Sem localização (registro manual)',
        unknown_user: 'outro usuário',
      },
      voided: {
        tag: 'Anulado',
        notice:
          'Esta sessão foi anulada por {{name}} em {{date}} e não conta mais como tempo trabalhado.',
        reason: 'Motivo: {{reason}}',
      },
      register_entry: 'Registrar entrada',
      register_exit: 'Registrar saída',
      load_error: 'Não foi possível obter seus registros de ponto.',
      empty_other_day: 'Não há registros de ponto neste dia.',
      details: {
        no_exit: 'A saída ainda não foi registrada.',
        open_in_maps: 'Abrir no Google Maps',
        time_zone: 'Fuso horário',
      },
      new: {
        title_entry: 'Registrar entrada',
        title_exit: 'Registrar saída',
        save_entry: 'Registrar',
        save_exit: 'Registrar',
        record_type: 'Tipo de registo',
        entry: 'Entrada',
        exit: 'Saída',
        professional: 'Profissional',
        entity: 'Entidade',
        date_time: 'Data e hora',
        location: 'Localização',
        latitude: 'Latitude',
        longitude: 'Longitude',
        accuracy: 'Precisão',
        accuracy_value: '± {{meters}} m',
        waiting: 'Obtendo sua localização exata...',
        accuracy_info:
          'A precisão atual é de ± {{current}} m. É a melhor que o dispositivo consegue obter neste momento e vai sendo atualizada. Em um espaço interno, aproxime-se de uma janela ou vá para o exterior para melhorar a precisão.',
        unavailable:
          'Não foi possível obter sua localização. Verifique se o GPS do dispositivo está ativo.',
        loading_error:
          'Não foi possível obter seu último registro de ponto. Verifique a conexão e tente novamente.',
        retry: 'Tentar novamente',
        success_entry: 'Entrada registrada com sucesso!',
        success_exit: 'Saída registrada com sucesso!',
        type_conflict:
          'O último registro de ponto mudou. Confira o tipo de registro e registre novamente.',
        save_error: 'Não foi possível registrar o ponto. Tente novamente.',
        purpose:
          'Sua localização exata é coletada apenas no momento em que você registra o ponto, para comprovar o local onde o ponto foi registrado. Esses dados são pessoais e ficam associados ao seu registro.',
        permission: {
          prompt_title: 'Precisamos da sua localização',
          prompt_description:
            'Para registrar o ponto é necessário comprovar o local onde você está. A localização só é solicitada nesta tela e é guardada apenas no momento do registro.',
          allow: 'Permitir localização',
          denied_title: 'A localização está bloqueada',
          denied_description:
            'O acesso à localização foi negado neste navegador, por isso não é possível registrar o ponto. Para permitir novamente:',
          unsupported_title: 'Localização indisponível',
          unsupported_description:
            'Este navegador não suporta localização ou a página não está sendo aberta em uma conexão segura (HTTPS).',
          help: {
            chrome:
              'Clique no ícone à esquerda do endereço (cadeado ou ajustes), escolha «Configurações do site» e, em «Localização», selecione «Permitir». Depois, recarregue a página.',
            edge: 'Clique no cadeado à esquerda do endereço, abra «Permissões deste site» e, em «Localização», selecione «Permitir». Depois, recarregue a página.',
            firefox:
              'Clique no ícone de permissões à esquerda do endereço, remova o bloqueio de «Acessar sua localização» e permita. Depois, recarregue a página.',
            safari:
              'Abra «Safari» > «Ajustes» > «Sites» > «Localização», escolha este site e selecione «Permitir». Depois, recarregue a página.',
            ios: 'Abra «Ajustes» > «Privacidade e Segurança» > «Serviços de Localização», ative-os e permita o acesso ao navegador que está usando. Depois, recarregue a página.',
            android_chrome:
              'Toque no ícone à esquerda do endereço, abra «Permissões» > «Localização» e selecione «Permitir». Se o bloqueio for do celular, ative a localização em «Configurações» > «Localização». Depois, recarregue a página.',
            generic:
              'Abra as configurações de permissões do site no seu navegador, permita o acesso à localização e recarregue a página.',
          },
        },
      },
    },
    occurrences: {
      empty_title: 'Ainda não há occorrências',
      empty_filters_title: 'Não existem ocorrências para os filtros selecionados',
      empty_subtitle: 'Prima o botão para adicionar uma nova ocorrência',
      new: 'Nova',
      new_occurrence: 'Nova ocorrência',
      add: 'Adicionar',
      type: 'Tipo de ocorrência',
      date: 'Data da ocorrência',
      time: 'Hora da ocorrência',
      local: 'Local da ocorrência',
      responsible: 'Responsável da ocorrência',
      patient: 'Utente envolvido',
      details: 'Detalhes da ocorrência',
      add_success_toast: 'A ocorrência foi adicionada com sucesso!',
      add_error_toast: 'Não foi possível adicionar a ocorrência.',
      add_cancel_modal_title: 'Tem a certeza que deseja sair sem adicionar a ocorrência?',
      add_cancel_modal_subtitle: 'Os dados inseridos serão perdidos.',
      local_error: 'O local inserido não poderá ultrapassar os {{ characters }} caracteres',
    },
  },
  care_and_activities: {
    date: 'Data',
    activity: 'Atividade',
    task: 'Tarefa',
    evaluation: 'Avaliação',
    responsible: 'Tratada por',
    status: 'Estado',
    empty_state: 'Não existem tarefas registadas.',
    permissions: 'Não possui permissões para visualizar as tarefas.',
    register: 'Registo',
    notes: 'Observações',
    status_types: {
      [CaregiverTaskCompletion.DONE]: 'Realizada',
      [CaregiverTaskCompletion.NOT_DONE]: 'Não realizada',
      [CaregiverTaskCompletion.PLANNED]: 'Planeada',
      [CaregiverTaskCompletion.MISSED]: 'Não tratada',
    },
    sort_icon: {
      sort_in_ascending: 'Ordenar por ordem crescente',
      sort_in_descending: 'Ordenar por ordem decrescente',
    },
    handle_task_modal: {
      task_status: {
        done: 'Realizada',
        not_done: 'Não realizada',
      },
      details: 'Detalhes',
      observations: 'Observações',
      treated: 'Tratada',
      cancel: 'Cancelar',
      write_observation: 'Escreva uma observação.',
      observation_required_message: 'Indique mais informações sobre o motivo',
      card_collapse: {
        title: 'Informações da tarefa',
      },
      time_input: 'Hora da execução',
      date_input: 'Data do registo',
      select_date: 'Selecione a data',
      select_time: 'Selecione a hora',
      task_info_load_error: 'Erro ao carregar as informações da tarefa.',
      feature_input: {
        label: {
          [CareTaskFeature.BloodGlucoseHealthMeasurement]: 'Glicose no sangue',
          [CareTaskFeature.BloodOxygenHealthMeasurement]: 'Oxigénio no sangue',
          [CareTaskFeature.BloodPressureHealthMeasurement]: 'Tensão arterial (SIS - DIA/mmHg)',
          [CareTaskFeature.BodyFatPercentageHealthMeasurement]: 'Percentagem de gordura',
          [CareTaskFeature.BodyHeightHealthMeasurement]: 'Altura',
          [CareTaskFeature.BodyMusclePercentageHealthMeasurement]: 'Percentagem de músculo',
          [CareTaskFeature.BodyTemperatureHealthMeasurement]: 'Temperatura corporal',
          [CareTaskFeature.BodyWaterPercentageHealthMeasurement]: 'Percentagem de água',
          [CareTaskFeature.BodyWeightHealthMeasurement]: 'Peso',
          [CareTaskFeature.HeartRateHealthMeasurement]: 'Frequência cárdiaca',
          [CareTaskFeature.NatriureticPeptideTestHealthMeasurement]:
            'Teste do peptídeo natriurético',
          [CareTaskFeature.RespiratoryRateHealthMeasurement]: 'Frequência respiratória',
          [CareTaskFeature.SleepHealthMeasurement]: 'Medição da saúde do sono',
          [CareTaskFeature.StepsHealthMeasurement]: 'Passos',
        },
        inputs: {
          value: {
            label: 'Valor',
            placeholder: 'Inserir um valor',
          },
          minutes: {
            label: 'Minutos',
            placeholder: 'Inserir minutos',
          },
          percentage: {
            label: 'Percentagem',
            placeholder: 'Inserir uma percentagem',
          },
          mealTime: {
            label: 'Hora da refeição',
            placeholder: 'Selecione um horário',
            options: {
              'before-meal': 'Antes da refeição',
              'after-meal': 'Depois da refeição',
            },
          },
          bnp: {
            label: 'Peptídeo natriurético',
            placeholder: 'Selecione um valor',
          },
          nt_proBnp: {
            label: 'N-terminal (NT)-pro BNP',
            placeholder: 'Selecione um valor',
          },
          type: {
            label: 'Tipo',
            placeholder: 'Selecione um tipo',
            options: {
              'in-bed': 'Na cama',
              awake: 'Acordado',
              rem: 'REM',
              core: 'Core',
              deep: 'Profundo',
            },
          },
          startTime: {
            label: 'Hora início',
          },
          endTime: {
            label: 'Hora fim',
          },
        },
        units: {
          steps: 'passos',
        },
        required_message: 'Obrigatório',
        number_pattern_validation: {
          min: 'Mín. {{ min_suffix }}',
          max: 'Máx. {{ max_suffix }}',
        },
      },
      undo_task: 'Desfazer tarefa',
      undo: 'Desfazer',
      edit_entry: 'Editar registo',
      confirm: 'Confirmar',
      snackbar: {
        update: {
          success: 'A tarefa foi registada com sucesso!',
          error: 'Ocorreu um erro ao atualizar o registo de tarefa.',
        },
        reset: {
          success: 'A tarefa voltou ao estado inicial!',
          error: 'Ocorreu um erro ao dar reset do registo de tarefa.',
        },
      },
      history: {
        title: 'Histórico',
        update: 'editou a tarefa',
        status: 'Estado',
        someone: 'Alguém ',
        remove: 'desfez a tarefa',
        auto: 'A tarefa foi dada como realizada pelo sistema',
        validate: 'validou a tarefa',
        create: 'criou a tarefa',
        read: 'leu a tarefa',
        contract_update: 'atualizou o contrato',
        changes: 'Alterações',
        task_not_done_reason: 'Motivo de não realização',
        notes: 'Notas',
        files_added: 'Ficheiros adicionados',
        files_removed: 'Ficheiros removidos',
      },
      files: 'Ficheiros',
    },
  },
  configurations_fields: {
    feature: {
      name: {
        placeholder: 'Escreva um nome para a tarefa',
      },
    },
  },
  schedule_modal: {
    trigger_button_label: 'Repetição',
    activity: {
      title: 'Escolher Período - Personalizado',
    },
    task: {
      title: 'Definir repetição',
    },
  },
  schedule_field: {
    readable_periodicity: {
      not_defined: '(Não definida)',
      no_repetition: '(Sem repetição)',
      defined_per_activity: 'Repetição definida por atividade',
      at_every: 'A cada',
      hourly: 'hora',
      daily: 'dia',
      weekly: 'semana',
      monthly: 'mês',
      weekly_at: 'à',
      and: 'e',
      at_hour: 'às',
      until_end_day: 'até dia',
    },
    quantities: 'Quantidades',
  },
  activity_type_posology_schedule_form: {
    title: 'Definir Posologia',
    fields: {
      psology: {
        label: 'Posologia',
      },
      posology_schedule_type: {
        label: 'Posologia',
        options: {
          [PosologySchedule.PeriodsOfTheDay]: 'Período do dia',
          [PosologySchedule.TimesList]: 'Lista de horas',
          [PosologySchedule.Repetition]: 'Horária',
          [PosologySchedule.SOS]: 'SOS',
        },
      },
      frequency: {
        options: {
          [Frequency.Hourly]: 'Horas',
          [Frequency.Daily]: 'Dias',
          [Frequency.Weekly]: 'Semanas',
          [Frequency.Monthly]: 'Meses',
        },
      },
      frequency_value: {
        label: 'Repetir a cada',
        validations: {
          min: 'O valor tem de ser igual ou superior a um.',
        },
      },
      time_list: {
        hour: {
          label: 'Hora',
        },
        quantity: {
          label: 'Quantidade',
        },
        add: 'Adicionar hora',
        required: 'Adicione pelo menos um horário.',
        error: 'Hora já registada.',
      },
      periods_of_the_day_item: {
        required: 'Adicione pelo menos um.',
      },
      repetitionHourlyFrequencyValue: 'Toma cada',
      repetitionHourlyFrequencyValueHour: 'hora',
      repetitionHourlyFrequencyValueHours: 'horas',
      repetition_quantity: {
        label: 'Quantidades',
        validations: {
          min: 'O valor tem de ser igual ou superior a um.',
        },
      },
      end_date: {
        label: 'Data Fim',
        validations: {
          min: 'Data inválida',
        },
      },
      end_hour: {
        label: 'Hora Fim',
        validations: {
          min: 'Hora inválida',
        },
      },
    },
  },
  care_plan_sheet_activities_group_entry_configurationvalue: {
    repetition: {
      per_activity: 'Por atividade',
      per_task: 'Por tarefa',
    },
    aivd: {
      at_time: 'às',
      transportation: 'Transporte',
    },
  },
  periodicity_form: {
    empty_state: 'Ainda não há repetição definida.',
    fields: {
      frequency: {
        label: 'A cada',
        hourly: 'Horas',
        daily: 'Dias',
        weekly: 'Semanas',
        monthly: 'Meses',
      },
      frequency_value: {
        label: 'Repetição por:',
        activity: 'Atividade',
        task: 'Tarefa',
        validations: {
          required: 'Campo obrigatório',
          min: 'O valor da frequência tem de ser superior a um.',
          max: 'A frequência tem de ser inferior ou igual a {{ value }}.',
        },
      },
      start_at_date: {
        label: 'Data de início',
        validations: {
          superior_to_end_date: 'A data de inicío tem de ser anterior à data de fim.',
        },
      },
      end_date: {
        label: 'Data fim',
        validations: {
          superior_to_start_date: 'A data final tem de ser posterior à data de início.',
        },
      },
      end_hour: {
        label: 'Hora fim',
      },
      days: {
        label: 'Dias da semana',
        validations: {
          required: 'Campo obrigatório',
        },
      },
    },
  },
  role_permissions_professional_caregiver_office_screen: {
    generic_header: {
      subtitle: 'Criar cargo e gerir permissões',
    },
    fields: {
      name: {label: 'Nome', placeholder: 'Adicione um nome ao cargo'},
      description: {label: 'Descrição', placeholder: 'Adicione uma descrição'},
    },
    view: {
      tooltip: 'Permite ao usuário ver conteúdo',
      label: 'Ver',
    },
    edit: {
      tooltip: 'Permite ao usuário editar conteúdo',
      label: 'Editar',
    },
    select_all_permissions: 'Todas as permissões',
    localized_string_input: {
      name: {
        title: 'Traduções para o nome',
      },
      description: {
        title: 'Traduções para a descrição',
      },
    },
    router_prompt: {
      title: 'Cargo e Permissões',
      content: {
        title: 'Tem a certeza que deseja sair?',
        subtitle: 'As alterações feitas serão perdidas.',
      },
    },
    snackbar: {
      load: {
        error: 'Ocorreu um erro ao carregar a página.',
      },
      update: {
        success: 'Actualizado com sucesso!',
        error: 'Ocorreu um erro ao atualizar as permissões deste cargo.',
      },
      exit: 'As alterações não foram guardadas.',
    },
  },
  shifts_page: {
    shifts: 'Turnos',
    team: 'Equipa',
    create_button: 'Criar',
    schedule: 'Horário:',
    to_hour: 'às',
    responsible: {
      label: 'Responsável',
      not_defined: 'Não definido',
    },
    professionals: {
      singular: 'Profissional',
      plural: 'Profissionais',
    },
    teams: {
      singular: 'equipa',
      plural: 'equipas',
    },
    empty_state: {
      title: 'Ainda não há turnos registados.',
      subtitle: 'Prima criar para registar um turno.',
    },
    shift_modal: {
      create_shift: 'Criar turno',
      edit_shift: 'Editar turno',
      shift_form: {
        name: 'Nome do turno',
        name_placeholder: 'Turno da manhã, tarde, noite',
        required_field_message: 'Campo obrigatório.',
        min_required_field_message: 'Obrigatório.',
        pattern_field_message: 'Campo inválido.',
        max_characters_name_message: 'Máx {{ characters }} caracteres.',
        abbreviation: 'Id',
        abbreviation_placeholder: 'M(manhã), T(tarde), N(noite)',
        color: 'Cor',
        social_response: 'Serviços socioassistenciais do turno',
        social_responses_placeholder_input: 'Inserir serviços socioassistenciais',
        people_per_shift: 'Pessoas no turno',
        people_per_shift_placeholder_input: 'Máximo de pessoas por turno',
        start_hour: 'Hora início',
        end_hour: 'Hora fim',
        shift_responsible: 'Responsável por turno',
        shift_responsible_placeholder: 'Sem responsável atribuído',
        snackbar: {
          create_shift: {
            success: 'Turno {{ name }} criado com sucesso!',
            error: 'Erro ao criar o turno.',
          },
          update_shift: {
            success: 'Turno {{ name }} atualizado com sucesso!',
            error: 'Erro ao atualizar o turno.',
          },
        },
      },
    },
    confirm_delete: {
      title: 'Apagar turno - {{ name }}',
      copy: {
        title: 'Tem a certeza que deseja apagar o turno?',
        subtitle: 'Esta ação não pode ser revertida.',
      },
    },
    cancel: 'Cancelar',
    create: 'Criar',
    save: 'Guardar',
    delete: 'Apagar',
  },
  planner: {
    header: {
      title: 'Calendário',
      filter_all_seniors_label: 'Todos os utentes',
      filter_input_placeholder: 'Filtros',
      range: {
        day: 'Dia',
        week: 'Semana',
        month: 'Mês',
      },
      add: 'Adicionar',
      add_disabled: 'Não tem permissões para adicionar esta tarefa.',
      toggler: {
        calendar: 'calendário',
        list: 'lista',
      },
    },
    body: {
      untreaten: '',
      untreaten_one: '{{count}} Não tratada',
      untreaten_other: '{{count}} Não tratadas',
      empty: 'Não existem tarefas planeadas para mostrar.',
    },
    side: {
      see_all: 'Ver todos',
      birthdays: 'Aniversários',
    },
    modal: {
      create: {
        activity: {
          ativities_group: 'Atividade',
          ativities_group_placeholder: 'Selecione uma atividade',
          activities_type: 'Tarefa',
          activities_type_placeholder: 'Selecione uma tarefa',
          add_users_label: 'Adicionar utentes',
          add_users_placeholder: 'Adicione os utentes',
          added_users: 'Utentes adicionados',
          add_users_type: {
            social_response: 'Serviços socioassistenciais',
            user: 'Utentes',
          },
          add_users_mark: '',
          add_users_mark_one: 'Marcar todos {{sr}} ({{count}} utente)',
          add_users_mark_other: 'Marcar todos {{sr}} ({{count}} utentes)',
          add_users_mark_all: 'Selecionar todos os utentes',
          success: '',
          success_one: 'A tarefa foi adicionada com sucesso!',
          success_other: 'As tarefas foram adicionadas com sucesso!',
        },
      },
      delete: {
        title: {
          single: 'Tem certeza que deseja apagar a tarefa para o utente "{{name}}"?',
          multiple: 'Tem certeza que deseja apagar as tarefas "{{name}}"?',
        },
        desc: {
          single: 'A tarefa será apagada no plano de cuidados do utente.',
          multiple: 'As tarefas serão apagadas no plano de cuidados de cada utente.',
        },
      },
    },
    item_menu: {
      activity: {
        users: 'Utentes',
        action: {
          delete: 'Apagar',
          delete_disabled: 'Não tem permissões para apagar esta tarefa.',
          edit: 'Editar Plano',
          plan: 'Plano',
          register: 'Registo',
        },
      },
      anniversary: {
        birthday: 'Aniversário',
        born: 'Nasceu em',
        years: 'anos',
      },
    },
    errors: {
      content: 'Ocorreu um erro ao carregar o calendário.',
      save_activity: 'Ocorreu um erro ao guardar a tarefa. Por favor, tente de novo.',
      delete_activity: 'Ocorreu um erro ao apagar a(s) tarefa(s). Por favor, tente de novo.',
      unknown_item_type: "Tipo de item do planejador desconhecido: '{{ type }}'",
    },
  },
  care_services: {
    header: {
      add_button_disabled_tooltip: 'Você não tem permissões para adicionar serviços',
      seniors_label: 'Utentes',
      filter_caregivers_label: 'Todos os profissionais',
    },
    form: {
      required_field_message: 'Este campo é obrigatório',
      title_label: 'Título',
      title_placeholder: 'Título do serviço',
      color_label: 'Cor',
      start_date_label: 'Data Inicio',
      end_date_label: 'Data fim',
      start_time_label: 'Hora início',
      end_time_label: 'Hora fim',
      repetition_label: 'Repetição',
      responsible_users_label: 'Profissionais associados',
      responsible_users_placeholder: 'Selecione um responsável',
      senior_label: 'Utente',
      senior_placeholder: 'Selecionar utente',
      notes_label: 'Observações',
      notes_placeholder: 'Escreva uma observação',
      permissions_disabled_tooltip: 'Você não tem permissão para editar este campo',
      twenty_four_hours_label: 'Serviço 24 horas',
    },
    modals: {
      edit: {
        title: 'Editar serviço',
      },
      create: {
        title: 'Criar serviço',
      },
      delete: {
        title: 'Apagar serviço',
        description: 'Tem a certeza que pretende apagar este serviço?',
        cannot_be_undone: 'Esta ação não pode ser desfeita.',
      },
      repetition_options: {
        only_this_event: 'Apenas este evento',
        this_and_following: 'Este e os seguintes',
      },
      edit_recurrent: {
        title: 'Editar serviço recorrente',
      },
      delete_recurrent: {
        title: 'Apagar serviço recorrente',
      },
      edit_confirmation: {
        title: 'Salvar alterações no serviço',
        subtitle: 'Tem a certeza que deseja editar o serviço?',
      },
    },
    toasts: {
      edit: {
        success: {
          title: 'Serviço atualizado',
          message: 'O serviço foi atualizado com sucesso.',
        },
        error: {
          unknown: {
            title: 'Erro ao atualizar serviço',
            message: 'Ocorreu um erro ao atualizar o serviço. Tente novamente mais tarde.',
          },
          not_found: {
            title: 'Serviço não encontrado',
            message: 'O turno de serviço que você está tentando editar não foi encontrado.',
          },
          access_denied: {
            title: 'Acesso negado',
            message: 'Você não tem permissão para editar este turno de serviço.',
          },
          invalid_data: {
            title: 'Dados inválidos',
            message:
              'Os dados fornecidos são inválidos. Verifique as informações e tente novamente.',
          },
          end_date_invalid: {
            title: 'Data de término inválida',
            message: 'A data de término deve ser posterior à data de início.',
          },
          repetition_required: {
            title: 'Erro de edição',
            message: 'A repetição é requirida quando o serviço é recorrente.',
          },
          repetition_removal_scope: {
            title: 'Erro de edição',
            message: 'Para remover a repetição necessita editar todos os eventos da série.',
          },
          repetition_change_scope: {
            title: 'Erro de edição',
            message: 'Para alterar a repetição necessita editar todos os eventos da série.',
          },
        },
      },
      create: {
        success: {
          title: 'Serviço criado',
          message: 'O serviço foi criado com sucesso.',
        },
        error: {
          title: 'Erro ao criar novo serviço',
          message: 'Ocorreu um erro ao criar o novo serviço. Tente novamente mais tarde.',
        },
      },
      delete: {
        error: {
          title: 'Erro ao excluir serviço',
          message: 'Ocorreu um erro ao excluir o serviço. Tente novamente mais tarde.',
        },
      },
    },
    export: {
      repetition: {
        ends_on: 'Termina em',
        ends_after: 'Termina após',
        occurrences: 'ocorrências',
      },
    },
    permissions: {
      view: 'Você não tem permissões para ver os serviços.',
    },
  },
  shared: {
    handle_care_task_modal: {
      confirm_edit: {
        title: 'Tem certeza que deseja editar a tarefa?',
      },
      confirm_edit_cancel: {
        title: 'Ainda não guardou as informações. Tem certeza que deseja sair?',
        subtitle: 'Os dados não guardados serão perdidos.',
      },
      confirm_reset: {
        title: 'Tem certeza que deseja desfazer a tarefa?',
        subtitle: 'A tarefa voltará ao estado inicial',
      },
    },
    input_disabled_tooltip: 'Não possui permissões para editar este campo',
  },
  export: {
    plan: 'Plano',
    address: 'Endereço',
    contributor_number: 'Nº CPF',
    sns_number: 'Nº CNS',
    contact: 'Contato',
    others: 'Outros',
    details: 'Detalhes',
    tasks: 'Tarefas',
    task: 'Tarefa',
    register: 'Registro',
    date: 'Data',
    start_date: 'Início',
    end_date: 'Fim',
    periodicity: 'Periodicidade',
    observations: 'Observações',
    no_records: 'Nenhum registro',
    no_repetition: '(Sem repetição)',
    senior_profile: {
      save_pdf: 'Ficha de acompanhamento (.pdf)',
      report_title: 'Ficha de acompanhamento',
      user_section_title: 'Dados do paciente',
      profile_section_subtitle: 'Perfil',
      full_name: 'Nome completo',
      preferred_name: 'Como prefere ser chamado',
      birth_date: 'Data de nascimento',
      sns_number: 'Número do CNS',
      social_security_number: 'Número da Previdência Social - NIS',
      taxpayer_number: 'CPF',
      additional_information: 'Outras informações',
      health_conditions_subtitle: 'Patologias/Limitações',
      food_intolerances_title: 'Alergias / Intolerâncias',
      food_intolerances_subtitle: 'Intolerâncias (Alimentação)',
      medication_allergies_subtitle: 'Alergias (Medicação)',
      medication_title: 'Medicação',
      latest_vital_signs_title: 'Histórico de sinais vitais (últimos 7 dias)',
    },
    error: {
      pdf_generation: 'Ocorreu um erro ao gerar o PDF. Por favor, tente novamente mais tarde.',
      xlsx_generation: 'Ocorreu um erro ao gerar o Excel. Por favor, tente novamente mais tarde.',
      pdf_monthly_not_supported: 'A exportação mensal não é suportada.',
    },
  },
};
